<template>
  <div class="contract-v2" :class="{ 'contract-v2--public': isPublic, 'contract-v2--admin': !isPublic }">
    <header v-if="isPublic" class="public-header">
      <v-btn icon dark aria-label="Zur Standortauswahl" @click="$router.push('/contract')">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <div class="public-header__brand">
        <img src="/img/roody_logo.png" alt="roody" />
        <div>
          <span>Online Abo</span>
          <small v-if="creatorData">{{ creatorStudioName }}</small>
        </div>
      </div>
      <div class="public-header__spacer"></div>
    </header>

    <v-container fluid class="contract-v2__container">
      <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4"></v-progress-linear>
      <v-alert v-if="error" :dark="!isPublic" type="error" outlined dismissible @input="error = ''">{{ error }}</v-alert>

      <template v-if="!isPublic">
        <div v-if="adminTab !== 2" class="page-heading">
          <div>
            <div class="eyebrow">Vertragsplattform V2</div>
            <h1>Abos, Abschlüsse und TAC</h1>
            <p>Parallel zur bisherigen Vertragslösung. Jeder Standort und jeder Kanal kann einzeln umgestellt werden.</p>
          </div>
          <v-chip color="primary" outlined>
            <v-icon left small>mdi-shield-check-outline</v-icon>
            V1 bleibt aktiv
          </v-chip>
        </div>

        <v-tabs v-model="adminTab" dark background-color="transparent" show-arrows class="v2-tabs mb-6">
          <v-tab><v-icon left>mdi-database-cog-outline</v-icon>Katalog</v-tab>
          <v-tab><v-icon left>mdi-tune-variant</v-icon>Rollout</v-tab>
          <v-tab><v-icon left>mdi-message-processing-outline</v-icon>Vertrag erstellen</v-tab>
          <v-tab><v-icon left>mdi-file-document-multiple-outline</v-icon>V2-Verträge</v-tab>
          <v-tab><v-icon left>mdi-swap-horizontal</v-icon>TAC-Mapping</v-tab>
        </v-tabs>
      </template>

      <section v-if="!isPublic && adminTab === 0" class="section-stack">
        <v-card dark class="surface-card catalog-command-card" outlined>
          <div class="catalog-command-card__top">
            <div class="catalog-command-card__identity">
              <span><v-icon>mdi-bookshelf</v-icon></span>
              <div>
                <div class="eyebrow">Abo-Katalog</div>
                <h2>{{ catalog ? catalog.name : "Dein zentraler Produktkatalog" }}</h2>
                <p>Einfach pflegen, sicher versionieren und gezielt ausrollen.</p>
              </div>
            </div>
            <div v-if="catalog" class="catalog-command-card__state">
              <span class="catalog-status" :class="`catalog-status--${catalog.status}`"
                ><i></i>{{ catalog.status === "published" ? "Publiziert" : catalog.status === "archived" ? "Archiviert" : "Entwurf" }}</span
              >
              <small>Version {{ catalog.version }}</small>
            </div>
          </div>

          <div class="catalog-command-bar">
            <v-select
              v-model="selectedCatalogId"
              :items="catalogOptions"
              item-text="label"
              item-value="value"
              label="Arbeitsversion"
              outlined
              hide-details
              class="catalog-version-select"
              @change="loadCatalog"></v-select>
            <div class="catalog-command-actions">
              <v-btn v-if="catalog" outlined @click="cloneCatalog"><v-icon left>mdi-content-copy</v-icon>Neue Version</v-btn>
              <v-btn v-if="catalog && catalog.status === 'draft'" color="primary" :loading="saving" @click="saveCatalog"
                ><v-icon left>mdi-content-save-outline</v-icon>Speichern</v-btn
              >
              <v-btn v-if="catalog && catalog.status === 'draft'" color="success" :loading="publishing" @click="publishCatalog"
                ><v-icon left>mdi-rocket-launch-outline</v-icon>Publizieren</v-btn
              >
            </div>
          </div>

          <div v-if="catalog" class="catalog-kpis">
            <div>
              <span><v-icon small>mdi-shape-outline</v-icon></span
              ><strong>{{ catalog.contractTypes.length }}</strong
              ><small>Vertragstypen</small>
            </div>
            <div>
              <span><v-icon small>mdi-card-account-details-star-outline</v-icon></span
              ><strong>{{ catalog.plans.length }}</strong
              ><small>Pläne</small>
            </div>
            <div>
              <span><v-icon small>mdi-puzzle-plus-outline</v-icon></span
              ><strong>{{ catalog.addons.length }}</strong
              ><small>Zusatzpakete</small>
            </div>
            <div>
              <span><v-icon small>mdi-map-marker-alert-outline</v-icon></span
              ><strong>{{ catalog.overrides.length }}</strong
              ><small>Ausnahmen</small>
            </div>
            <button type="button" class="catalog-details-toggle" @click="catalogDetailsOpen = !catalogDetailsOpen">
              <v-icon small>{{ catalogDetailsOpen ? "mdi-chevron-up" : "mdi-tune-variant" }}</v-icon>
              {{ catalogDetailsOpen ? "Details schliessen" : "Katalogdetails" }}
            </button>
          </div>

          <v-expand-transition>
            <div v-if="catalog && catalogDetailsOpen" class="catalog-details-panel">
              <v-text-field
                v-model="catalog.name"
                label="Name dieser Katalogversion"
                outlined
                hide-details
                :disabled="!catalogEditable"></v-text-field>
              <div v-if="catalog.source === 'legacy-import'" class="import-scope">
                <div>
                  <v-icon color="success">mdi-check-circle-outline</v-icon
                  ><span><strong>Übernommen</strong>Pläne, Laufzeiten, Zusatzpakete, Zahlungsarten, Kategorien, Firmen und Bedingungen</span>
                </div>
                <div>
                  <v-icon color="warning">mdi-filter-remove-outline</v-icon
                  ><span
                    ><strong>Bewusst weggelassen</strong>Legacy-Aktionen, Regeln, Sale-Werte, Standort-Preisabweichungen und Formular-Shells</span
                  >
                </div>
              </div>
            </div>
          </v-expand-transition>

          <div class="catalog-import-bar">
            <div>
              <span><v-icon>mdi-database-arrow-right-outline</v-icon></span>
              <div><strong>Legacy-Daten</strong><small>Als neuen, bearbeitbaren Entwurf übernehmen.</small></div>
            </div>
            <div class="button-row button-row--end">
              <v-btn text :loading="importing" :disabled="resettingImport" @click="importLegacy(false)">Importieren</v-btn>
              <v-btn text :loading="importing" :disabled="resettingImport" @click="importLegacy(true)">Frisch importieren</v-btn>
              <v-btn
                v-if="catalog && catalog.source === 'legacy-import' && catalog.status === 'draft'"
                text
                color="error"
                :loading="resettingImport"
                :disabled="importing"
                @click="openResetImportDialog"
                >Zurücksetzen</v-btn
              >
            </div>
          </div>
        </v-card>

        <template v-if="catalog">
          <div v-if="validationIssues.length || importReviewIssues.length" class="catalog-health">
            <div v-if="validationIssues.length" class="catalog-health-item catalog-health-item--warning">
              <button type="button" @click="validationIssuesOpen = !validationIssuesOpen">
                <span><v-icon color="warning">mdi-alert-circle-outline</v-icon></span>
                <div>
                  <strong>{{ validationIssues.length }} Punkte vor dem Publizieren</strong
                  ><small>Diese Angaben brauchen noch deine Aufmerksamkeit.</small>
                </div>
                <v-icon>{{ validationIssuesOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </button>
              <v-expand-transition
                ><ul v-if="validationIssuesOpen">
                  <li v-for="issue in validationIssues.slice(0, 8)" :key="issue.path + issue.message">{{ issue.path }}: {{ issue.message }}</li>
                  <li v-if="validationIssues.length > 8">Und {{ validationIssues.length - 8 }} weitere Punkte.</li>
                </ul></v-expand-transition
              >
            </div>
            <div v-if="importReviewIssues.length" class="catalog-health-item catalog-health-item--info">
              <button type="button" @click="importIssuesOpen = !importIssuesOpen">
                <span><v-icon color="info">mdi-information-outline</v-icon></span>
                <div>
                  <strong>{{ importReviewIssues.length }} Importhinweise</strong><small>Der Katalog bleibt aufgeräumt; Details nur bei Bedarf.</small>
                </div>
                <v-icon>{{ importIssuesOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
              </button>
              <v-expand-transition
                ><ul v-if="importIssuesOpen">
                  <li v-for="issue in importReviewIssues.slice(0, 8)" :key="issue.code + issue.path">{{ issue.message }}</li>
                  <li v-if="importReviewIssues.length > 8">Und {{ importReviewIssues.length - 8 }} weitere Hinweise.</li>
                </ul></v-expand-transition
              >
            </div>
          </div>

          <div class="catalog-workspace-nav">
            <div class="catalog-section-tabs" role="tablist" aria-label="Katalogbereiche">
              <button
                v-for="section in catalogSectionOptions"
                :key="section.value"
                type="button"
                :class="{ active: catalogSection === section.value }"
                @click="catalogSection = section.value">
                <v-icon small>{{ section.icon }}</v-icon>
                <span>{{ section.label }}</span>
                <b>{{ catalogSectionCount(section.value) }}</b>
              </button>
            </div>
            <v-text-field
              v-model="catalogSearch"
              dark
              outlined
              dense
              hide-details
              clearable
              prepend-inner-icon="mdi-magnify"
              label="In diesem Bereich suchen"></v-text-field>
          </div>

          <v-card v-if="catalogSection === 'types'" dark class="surface-card" outlined>
            <v-card-title class="card-title-row">
              <div>
                <div class="eyebrow">Das neue Fundament</div>
                <h2>Vertragstypen und Bedingungen</h2>
                <p>
                  Normal, Flexi und MTT steuern Angebot, Sichtbarkeit, Bedingungen und erforderliche Angaben. Ein Plan darf zu mehreren Typen gehören.
                </p>
              </div>
              <v-btn v-if="catalogEditable" color="primary" outlined @click="addContractType"><v-icon left>mdi-plus</v-icon>Vertragstyp</v-btn>
            </v-card-title>
            <v-card-text>
              <v-expansion-panels dark accordion flat class="type-panels">
                <v-expansion-panel
                  v-for="(contractType, typeIndex) in catalog.contractTypes"
                  v-show="catalogMatches(contractType)"
                  :key="contractType.id">
                  <v-expansion-panel-header>
                    <div class="type-panel-header">
                      <span class="type-icon"
                        ><v-icon>{{
                          contractType.kind === "medical"
                            ? "mdi-medical-bag"
                            : contractType.kind === "rolling"
                            ? "mdi-autorenew"
                            : "mdi-calendar-lock"
                        }}</v-icon></span
                      >
                      <div>
                        <strong>{{ contractType.title || "Neuer Vertragstyp" }}</strong
                        ><small>{{ contractType.description || contractTypeKindLabel(contractType.kind) }}</small>
                      </div>
                      <div class="chip-wrap">
                        <v-chip v-for="channel in contractType.channels" :key="channel" small outlined>{{
                          channel === "internal" ? "Intern" : "Online"
                        }}</v-chip>
                        <v-chip v-if="contractType.requiresPhysiotherapist" small color="info" dark>Physiotherapie</v-chip>
                        <v-chip small :color="contractType.active ? 'success' : 'grey'" dark>{{ contractType.active ? "Aktiv" : "Inaktiv" }}</v-chip>
                      </div>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="5"
                        ><v-text-field v-model="contractType.title" label="Titel" outlined :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6" md="3"
                        ><v-select
                          v-model="contractType.kind"
                          :items="contractTypeKinds"
                          label="Vertragsart"
                          outlined
                          :disabled="!catalogEditable"></v-select
                      ></v-col>
                      <v-col cols="12" sm="6" md="4"
                        ><v-select
                          v-model="contractType.channels"
                          :items="channelOptions"
                          multiple
                          chips
                          deletable-chips
                          label="Sichtbar in"
                          outlined
                          :disabled="!catalogEditable"></v-select
                      ></v-col>
                      <v-col cols="12" md="8"
                        ><v-textarea
                          v-model="contractType.description"
                          label="Kurze Erklärung für den Abschluss"
                          rows="2"
                          outlined
                          :disabled="!catalogEditable"></v-textarea
                      ></v-col>
                      <v-col cols="6" md="2" class="toggle-col"
                        ><v-switch
                          v-model="contractType.requiresPhysiotherapist"
                          label="Physiotherapie wählen"
                          :disabled="!catalogEditable"></v-switch
                      ></v-col>
                      <v-col cols="6" md="2" class="toggle-col"
                        ><v-switch v-model="contractType.active" label="Aktiv" :disabled="!catalogEditable"></v-switch
                      ></v-col>
                    </v-row>

                    <div class="legal-heading">
                      <div>
                        <h3>Vertragsbedingungen</h3>
                        <p>
                          Das Standardprofil gilt für alle Standorte und Pläne. Zusätzliche Profile kannst du bewusst auf einzelne Standorte oder
                          Pläne begrenzen.
                        </p>
                      </div>
                      <v-btn v-if="catalogEditable" text color="primary" @click="addLegalProfile(contractType)"
                        ><v-icon left>mdi-plus</v-icon>Bedingungsprofil</v-btn
                      >
                    </div>
                    <div class="legal-profile-list">
                      <div v-for="(profile, profileIndex) in contractType.legalProfiles" :key="profile.id" class="legal-profile-card">
                        <div class="legal-profile-card__header">
                          <div>
                            <v-text-field
                              v-model="profile.title"
                              label="Profilname"
                              outlined
                              dense
                              hide-details
                              :disabled="!catalogEditable"></v-text-field>
                          </div>
                          <v-btn
                            :outlined="!profile.isDefault"
                            :color="profile.isDefault ? 'primary' : ''"
                            small
                            :disabled="!catalogEditable"
                            @click="setDefaultLegalProfile(contractType, profile.id)">
                            <v-icon left small>mdi-star{{ profile.isDefault ? "" : "-outline" }}</v-icon
                            >{{ profile.isDefault ? "Standard" : "Als Standard" }}
                          </v-btn>
                          <v-switch v-model="profile.active" dense hide-details label="Aktiv" :disabled="!catalogEditable"></v-switch>
                          <v-btn
                            v-if="catalogEditable && contractType.legalProfiles.length > 1"
                            icon
                            color="error"
                            @click="removeLegalProfile(contractType, profileIndex)"
                            ><v-icon>mdi-delete-outline</v-icon></v-btn
                          >
                        </div>
                        <v-row class="mt-2">
                          <v-col cols="12" md="6"
                            ><v-select
                              v-model="profile.studioIds"
                              :items="studioOptions"
                              item-text="label"
                              item-value="value"
                              multiple
                              chips
                              deletable-chips
                              label="Nur diese Standorte (leer = alle)"
                              outlined
                              :disabled="!catalogEditable"></v-select
                          ></v-col>
                          <v-col cols="12" md="6"
                            ><v-select
                              v-model="profile.planIds"
                              :items="planOptions"
                              item-text="label"
                              item-value="value"
                              multiple
                              chips
                              deletable-chips
                              label="Nur diese Pläne (leer = alle)"
                              outlined
                              :disabled="!catalogEditable"></v-select
                          ></v-col>
                          <v-col cols="12"
                            ><v-textarea
                              v-model="profile.preamble"
                              label="Einleitung"
                              rows="2"
                              outlined
                              :hint="'Platzhalter: {{company.name}} und {{studio.name}}'"
                              persistent-hint
                              :disabled="!catalogEditable"></v-textarea
                          ></v-col>
                        </v-row>
                        <div class="condition-list">
                          <div v-for="(condition, conditionIndex) in profile.conditions" :key="condition.id" class="condition-row">
                            <v-textarea
                              v-model="condition.text"
                              :label="`Bedingung ${conditionIndex + 1}`"
                              rows="2"
                              auto-grow
                              outlined
                              hide-details
                              :disabled="!catalogEditable"></v-textarea>
                            <v-switch v-model="condition.highlight" label="Hervorheben" dense hide-details :disabled="!catalogEditable"></v-switch>
                            <v-btn v-if="catalogEditable" icon color="error" @click="profile.conditions.splice(conditionIndex, 1)"
                              ><v-icon>mdi-close</v-icon></v-btn
                            >
                          </div>
                        </div>
                        <v-btn v-if="catalogEditable" text color="primary" @click="addLegalCondition(profile)"
                          ><v-icon left>mdi-plus</v-icon>Bedingung</v-btn
                        >
                        <v-textarea
                          v-model="profile.confirmation"
                          label="Bestätigung vor der Unterschrift"
                          rows="2"
                          outlined
                          class="mt-3"
                          :disabled="!catalogEditable"></v-textarea>
                      </div>
                    </div>
                    <div class="button-row button-row--end mt-4">
                      <v-btn v-if="catalogEditable && catalog.contractTypes.length > 1" text color="error" @click="removeContractType(typeIndex)"
                        ><v-icon left>mdi-delete-outline</v-icon>Vertragstyp entfernen</v-btn
                      >
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>

          <v-card v-if="catalogSection === 'plans'" dark class="surface-card" outlined>
            <v-card-title class="card-title-row">
              <div>
                <h2>Pläne und Laufzeiten</h2>
                <p>Ratenzahlung ist eine Variante, nicht mehr ein dupliziertes Abo.</p>
              </div>
              <v-btn v-if="catalogEditable" color="primary" outlined @click="addPlan"><v-icon left>mdi-plus</v-icon>Plan</v-btn>
            </v-card-title>
            <v-card-text>
              <v-expansion-panels dark accordion flat>
                <v-expansion-panel v-for="(plan, planIndex) in catalog.plans" v-show="catalogMatches(plan)" :key="plan.id">
                  <v-expansion-panel-header>
                    <div class="panel-header">
                      <strong>{{ plan.title || "Neuer Plan" }}</strong>
                      <span>{{ plan.variants.length }} Varianten · {{ contractTypeNames(plan.contractTypeIds) }}</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="5">
                        <v-text-field v-model="plan.title" label="Titel" outlined :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="plan.contractTypeIds"
                          :items="contractTypeOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Vertragstypen"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="3" class="toggle-col">
                        <v-switch v-model="plan.active" label="Aktiv" :disabled="!catalogEditable"></v-switch>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea v-model="plan.description" label="Beschreibung" outlined rows="2" :disabled="!catalogEditable"></v-textarea>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="plan.studioIds"
                          :items="studioOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Standorte (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="plan.eligibility.minAge"
                          type="number"
                          label="Mindestalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-combobox
                          v-model="plan.tags"
                          multiple
                          chips
                          deletable-chips
                          label="Empfehlungs-Tags"
                          outlined
                          :disabled="!catalogEditable"></v-combobox>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="plan.includedAddonIds"
                          :items="addonOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Im Plan enthaltene Zusatzpakete"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="plan.eligibility.maxAge"
                          type="number"
                          label="Maximalalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                    </v-row>

                    <div v-for="(variant, variantIndex) in plan.variants" :key="variant.id" class="nested-card mb-4">
                      <div class="nested-card__heading">
                        <strong>{{ variant.title || "Neue Variante" }}</strong>
                        <v-btn v-if="catalogEditable" icon color="error" @click="removeVariant(planIndex, variantIndex)">
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                      <v-row>
                        <v-col cols="12" md="5">
                          <v-text-field v-model="variant.title" label="Variantenname" outlined dense :disabled="!catalogEditable"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-select
                            v-model="variant.paymentMode"
                            :items="paymentModes"
                            label="Zahlungsmodus"
                            outlined
                            dense
                            :disabled="!catalogEditable"></v-select>
                        </v-col>
                        <v-col cols="12" md="3">
                          <v-text-field
                            v-model.number="variant.installments"
                            type="number"
                            min="1"
                            label="Raten"
                            outlined
                            dense
                            :disabled="!catalogEditable"></v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-select
                            v-model="variant.studioIds"
                            :items="studioOptions"
                            item-text="label"
                            item-value="value"
                            multiple
                            chips
                            deletable-chips
                            label="Varianten-Standorte (leer = Plan-Standorte)"
                            outlined
                            dense
                            :disabled="!catalogEditable"></v-select>
                        </v-col>
                      </v-row>
                      <div class="term-grid">
                        <div v-for="(term, termIndex) in variant.terms" :key="term.id" class="term-row">
                          <v-text-field v-model="term.label" label="Laufzeit" outlined dense hide-details :disabled="!catalogEditable"></v-text-field>
                          <v-select
                            v-model="term.unit"
                            :items="termUnits"
                            label="Einheit"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"
                            @change="syncAddonPriceUnits(term.id, term.unit)"></v-select>
                          <v-text-field
                            v-if="term.unit === 'fixed'"
                            v-model.number="term.years"
                            type="number"
                            min="0"
                            label="Jahre"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-if="term.unit === 'fixed'"
                            v-model.number="term.months"
                            type="number"
                            min="0"
                            label="Monate"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-if="term.unit === 'fixed'"
                            v-model.number="term.days"
                            type="number"
                            min="0"
                            label="Tage"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-if="term.unit !== 'fixed'"
                            v-model.number="term.minUnits"
                            type="number"
                            min="1"
                            label="Min. Einheiten"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-if="term.unit !== 'fixed'"
                            v-model.number="term.maxUnits"
                            type="number"
                            min="1"
                            label="Max. Einheiten"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-model.number="term.price"
                            type="number"
                            min="0"
                            step="0.05"
                            prefix="CHF"
                            label="Preis"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-text-field
                            v-model.number="term.signUpFee"
                            type="number"
                            min="0"
                            step="0.05"
                            prefix="CHF"
                            label="Aktivierung"
                            outlined
                            dense
                            hide-details
                            :disabled="!catalogEditable"></v-text-field>
                          <v-btn v-if="catalogEditable" icon color="error" @click="removeTerm(planIndex, variantIndex, termIndex)">
                            <v-icon>mdi-close</v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <v-btn v-if="catalogEditable" text color="primary" @click="addTerm(planIndex, variantIndex)">
                        <v-icon left>mdi-plus</v-icon>Laufzeit
                      </v-btn>
                    </div>
                    <div class="button-row">
                      <v-btn v-if="catalogEditable" text color="primary" @click="addVariant(planIndex)">
                        <v-icon left>mdi-plus</v-icon>Variante
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn v-if="catalogEditable" text color="error" @click="removePlan(planIndex)">
                        <v-icon left>mdi-delete-outline</v-icon>Plan entfernen
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>

          <v-card v-if="catalogSection === 'addons'" dark class="surface-card" outlined>
            <v-card-title class="card-title-row">
              <div>
                <h2>Zusatzpakete</h2>
                <p>Ein Preis pro Laufzeit. Standort- oder Abo-Sonderfälle kommen darunter als Ausnahme.</p>
              </div>
              <v-btn v-if="catalogEditable" color="primary" outlined @click="addAddon"><v-icon left>mdi-plus</v-icon>Zusatzpaket</v-btn>
            </v-card-title>
            <v-card-text>
              <v-expansion-panels dark accordion flat>
                <v-expansion-panel v-for="(addon, addonIndex) in catalog.addons" v-show="catalogMatches(addon)" :key="addon.id">
                  <v-expansion-panel-header>
                    <div class="panel-header">
                      <strong>{{ addon.title }}</strong
                      ><span>{{ addon.prices.length }} Preise · {{ contractTypeNames(addon.contractTypeIds) }}</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="addon.title" label="Titel" outlined :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="3" class="toggle-col">
                        <v-switch v-model="addon.active" label="Aktiv" :disabled="!catalogEditable"></v-switch>
                      </v-col>
                      <v-col cols="12" md="3" class="toggle-col">
                        <v-switch v-model="addon.amountSelectable" label="Menge wählbar" :disabled="!catalogEditable"></v-switch>
                      </v-col>
                      <v-col cols="12">
                        <v-textarea v-model="addon.description" label="Beschreibung" outlined rows="2" :disabled="!catalogEditable"></v-textarea>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="addon.contractTypeIds"
                          :items="contractTypeOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Vertragstypen"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="addon.eligibility.minAge"
                          type="number"
                          label="Mindestalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="addon.eligibility.maxAge"
                          type="number"
                          label="Maximalalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-combobox
                          v-model="addon.tags"
                          multiple
                          chips
                          deletable-chips
                          label="Empfehlungs-Tags"
                          outlined
                          :disabled="!catalogEditable"></v-combobox>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="addon.studioIds"
                          :items="studioOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Standorte (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col v-if="addon.amountSelectable" cols="6" md="3"
                        ><v-text-field
                          v-model.number="addon.minQuantity"
                          type="number"
                          min="1"
                          label="Mindestmenge"
                          outlined
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col v-if="addon.amountSelectable" cols="6" md="3"
                        ><v-text-field
                          v-model.number="addon.maxQuantity"
                          type="number"
                          min="1"
                          label="Maximalmenge"
                          outlined
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="addon.incompatibleAddonIds"
                          :items="addonOptions.filter((option) => option.value !== addon.id)"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Nicht kombinierbar mit"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                    </v-row>
                    <div class="addon-price-grid">
                      <div
                        v-for="(price, priceIndex) in addon.prices"
                        :key="price.termId + '-' + priceIndex"
                        class="addon-price-row"
                        :class="{ 'addon-price-row--monthly': addonPriceUnit(price) === 'month' }">
                        <v-select
                          v-model="price.termId"
                          :items="termOptions"
                          item-text="label"
                          item-value="value"
                          label="Laufzeit"
                          outlined
                          dense
                          hide-details
                          :disabled="!catalogEditable"
                          @change="addonPriceTermChanged(price)"></v-select>
                        <v-text-field
                          v-model.number="price.price"
                          type="number"
                          min="0"
                          step="0.05"
                          prefix="CHF"
                          label="Preis"
                          outlined
                          dense
                          hide-details
                          :disabled="!catalogEditable"></v-text-field>
                        <v-text-field
                          v-if="addonPriceUnit(price) === 'month'"
                          v-model.number="price.maxPrice"
                          type="number"
                          min="0.05"
                          step="0.05"
                          prefix="CHF"
                          label="Maximalpreis gesamt"
                          outlined
                          dense
                          clearable
                          hide-details
                          :disabled="!catalogEditable"></v-text-field>
                        <v-btn v-if="catalogEditable" icon color="error" @click="addon.prices.splice(priceIndex, 1)"
                          ><v-icon>mdi-close</v-icon></v-btn
                        >
                      </div>
                    </div>
                    <div class="button-row">
                      <v-btn v-if="catalogEditable" text color="primary" @click="addAddonPrice(addon)"><v-icon left>mdi-plus</v-icon>Preis</v-btn>
                      <v-spacer></v-spacer>
                      <v-btn v-if="catalogEditable" text color="error" @click="catalog.addons.splice(addonIndex, 1)">
                        <v-icon left>mdi-delete-outline</v-icon>Entfernen
                      </v-btn>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>

          <v-card v-if="catalogSection === 'payments'" dark class="surface-card" outlined>
            <v-card-title class="card-title-row">
              <div>
                <h2>Zahlungsarten</h2>
                <p>Gemeinsame Optionen für den internen und öffentlichen Abschluss.</p>
              </div>
              <v-btn v-if="catalogEditable" color="primary" outlined @click="addPaymentMethod"><v-icon left>mdi-plus</v-icon>Zahlungsart</v-btn>
            </v-card-title>
            <v-card-text>
              <div class="payment-method-grid">
                <div v-for="(method, methodIndex) in catalog.paymentMethods" v-show="catalogMatches(method)" :key="method.id" class="nested-card">
                  <v-text-field v-model="method.title" label="Titel" outlined dense :disabled="!catalogEditable"></v-text-field>
                  <v-text-field v-model="method.description" label="Hinweis" outlined dense :disabled="!catalogEditable"></v-text-field>
                  <v-select
                    v-model="method.channels"
                    :items="channelOptions"
                    multiple
                    chips
                    deletable-chips
                    label="Sichtbar in"
                    outlined
                    dense
                    :disabled="!catalogEditable"></v-select>
                  <v-select
                    v-model="method.contractTypeIds"
                    :items="contractTypeOptions"
                    item-text="label"
                    item-value="value"
                    multiple
                    chips
                    deletable-chips
                    label="Vertragstypen"
                    outlined
                    dense
                    :disabled="!catalogEditable"></v-select>
                  <v-select
                    v-model="method.studioIds"
                    :items="studioOptions"
                    item-text="label"
                    item-value="value"
                    multiple
                    chips
                    deletable-chips
                    label="Standorte (leer = alle)"
                    outlined
                    dense
                    :disabled="!catalogEditable"></v-select>
                  <div class="button-row">
                    <v-switch v-model="method.selectDate" label="Datum wählbar" :disabled="!catalogEditable"></v-switch>
                    <v-spacer></v-spacer>
                    <v-btn v-if="catalogEditable" icon color="error" @click="catalog.paymentMethods.splice(methodIndex, 1)"
                      ><v-icon>mdi-delete-outline</v-icon></v-btn
                    >
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card v-if="catalogSection === 'masterdata'" dark class="surface-card masterdata-card" outlined>
            <v-card-title class="card-title-row masterdata-heading">
              <div>
                <div class="eyebrow">Ein Ort für alles Rechtliche</div>
                <h2>Stammdaten</h2>
                <p>Wähle links einen Eintrag und bearbeite rechts nur das, was gerade zählt.</p>
              </div>
              <div class="masterdata-switch" role="tablist" aria-label="Stammdatenbereiche">
                <button type="button" :class="{ active: masterDataView === 'companies' }" @click="masterDataView = 'companies'">
                  <v-icon small>mdi-domain</v-icon>
                  <span>Vertragsfirmen</span>
                  <b>{{ catalog.companies.length }}</b>
                </button>
                <button type="button" :class="{ active: masterDataView === 'categories' }" @click="masterDataView = 'categories'">
                  <v-icon small>mdi-account-tag-outline</v-icon>
                  <span>Kategorien</span>
                  <b>{{ catalog.categories.length }}</b>
                </button>
              </div>
            </v-card-title>

            <v-card-text class="masterdata-body">
              <div v-if="masterDataView === 'companies'" class="entity-workspace">
                <aside class="entity-list" aria-label="Vertragsfirmen">
                  <div class="entity-list__header">
                    <div>
                      <strong>Vertragsfirmen</strong><span>{{ filteredCatalogCompanies.length }} von {{ catalog.companies.length }}</span>
                    </div>
                    <v-btn v-if="catalogEditable" icon color="primary" aria-label="Vertragsfirma hinzufügen" @click="addCompany"
                      ><v-icon>mdi-plus</v-icon></v-btn
                    >
                  </div>
                  <div class="entity-list__items">
                    <button
                      v-for="company in filteredCatalogCompanies"
                      :key="company.id"
                      type="button"
                      class="entity-list-item"
                      :class="{ active: selectedCatalogCompany && selectedCatalogCompany.id === company.id }"
                      :aria-pressed="selectedCatalogCompany && selectedCatalogCompany.id === company.id"
                      @click="selectedCatalogCompanyId = company.id">
                      <span class="entity-list-item__icon"><v-icon>mdi-domain</v-icon></span>
                      <span class="entity-list-item__copy">
                        <strong>{{ company.name || "Neue Vertragsfirma" }}</strong>
                        <small>{{ companySubtitle(company) }}</small>
                      </span>
                      <span class="entity-list-item__meta">{{ studioScopeLabel(company.studioIds) }}</span>
                      <v-icon small>mdi-chevron-right</v-icon>
                    </button>
                    <div v-if="filteredCatalogCompanies.length === 0" class="entity-empty">
                      <v-icon>mdi-domain-off</v-icon><strong>Keine Firma gefunden</strong
                      ><span>Ändere die Suche oder erstelle eine neue Vertragsfirma.</span>
                    </div>
                  </div>
                </aside>

                <section v-if="selectedCatalogCompany" class="entity-editor" aria-label="Vertragsfirma bearbeiten">
                  <div class="entity-editor__hero">
                    <span class="entity-editor__hero-icon"><v-icon>mdi-domain</v-icon></span>
                    <div>
                      <span class="eyebrow">Vertragsfirma</span>
                      <h3>{{ selectedCatalogCompany.name || "Neue Vertragsfirma" }}</h3>
                      <p>{{ companyAddress(selectedCatalogCompany) }}</p>
                    </div>
                    <v-chip small outlined color="primary">{{ studioScopeLabel(selectedCatalogCompany.studioIds) }}</v-chip>
                  </div>

                  <div class="editor-section">
                    <div class="editor-section__heading">
                      <span><v-icon small>mdi-card-account-details-outline</v-icon></span>
                      <div><strong>Firmendaten</strong><small>Diese Angaben erscheinen im Vertrag.</small></div>
                    </div>
                    <div class="editor-field-grid">
                      <v-text-field v-model="selectedCatalogCompany.name" label="Firmenname" outlined :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.phone"
                        label="Telefon"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.street"
                        label="Strasse und Nummer"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.city"
                        label="PLZ und Ort"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                    </div>
                  </div>

                  <div class="editor-section">
                    <div class="editor-section__heading">
                      <span><v-icon small>mdi-map-marker-multiple-outline</v-icon></span>
                      <div><strong>Gültigkeit</strong><small>Leer bedeutet automatisch alle Standorte.</small></div>
                    </div>
                    <v-select
                      v-model="selectedCatalogCompany.studioIds"
                      :items="studioOptions"
                      item-text="label"
                      item-value="value"
                      multiple
                      chips
                      deletable-chips
                      label="Standorte"
                      outlined
                      :disabled="!catalogEditable"></v-select>
                    <div class="editor-toggle-row">
                      <div>
                        <strong>Stammcenter verwenden</strong><span>Zeigt im Vertrag zusätzlich die Angaben des zuständigen Stammcenters.</span>
                      </div>
                      <v-switch v-model="selectedCatalogCompany.config.useMainCompany" inset hide-details :disabled="!catalogEditable"></v-switch>
                    </div>
                  </div>

                  <div v-if="selectedCatalogCompany.config.useMainCompany" class="editor-section editor-section--accent">
                    <div class="editor-section__heading">
                      <span><v-icon small>mdi-home-city-outline</v-icon></span>
                      <div><strong>Stammcenter</strong><small>Diese Kontaktdaten werden zusätzlich zur Vertragsfirma ausgewiesen.</small></div>
                    </div>
                    <div class="editor-field-grid">
                      <v-text-field
                        v-model="selectedCatalogCompany.config.mainCompany.name"
                        label="Name des Stammcenters"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.mainCompany.phone"
                        label="Telefon Stammcenter"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.mainCompany.street"
                        label="Strasse und Nummer"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCompany.config.mainCompany.city"
                        label="PLZ und Ort"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                    </div>
                  </div>

                  <div v-if="catalogEditable" class="entity-editor__footer">
                    <span>Änderungen werden erst mit „Speichern“ übernommen.</span>
                    <v-btn text color="error" @click="removeCatalogCompany(selectedCatalogCompany.id)"
                      ><v-icon left>mdi-delete-outline</v-icon>Firma entfernen</v-btn
                    >
                  </div>
                </section>
                <section v-else class="entity-editor entity-editor--empty" aria-label="Keine Vertragsfirma ausgewählt">
                  <span><v-icon>mdi-domain-plus</v-icon></span>
                  <h3>Noch keine Vertragsfirma</h3>
                  <p>Erstelle eine Firma und ordne sie danach allen oder einzelnen Standorten zu.</p>
                  <v-btn v-if="catalogEditable" color="primary" outlined @click="addCompany"
                    ><v-icon left>mdi-plus</v-icon>Vertragsfirma erstellen</v-btn
                  >
                </section>
              </div>

              <div v-else class="entity-workspace">
                <aside class="entity-list" aria-label="Mitgliedschaftskategorien">
                  <div class="entity-list__header">
                    <div>
                      <strong>Kategorien</strong><span>{{ filteredCatalogCategories.length }} von {{ catalog.categories.length }}</span>
                    </div>
                    <v-btn v-if="catalogEditable" icon color="primary" aria-label="Kategorie hinzufügen" @click="addCategory"
                      ><v-icon>mdi-plus</v-icon></v-btn
                    >
                  </div>
                  <div class="entity-list__items">
                    <button
                      v-for="category in filteredCatalogCategories"
                      :key="category.id"
                      type="button"
                      class="entity-list-item"
                      :class="{ active: selectedCatalogCategory && selectedCatalogCategory.id === category.id }"
                      :aria-pressed="selectedCatalogCategory && selectedCatalogCategory.id === category.id"
                      @click="selectedCatalogCategoryId = category.id">
                      <span class="entity-list-item__icon"><v-icon>mdi-account-tag-outline</v-icon></span>
                      <span class="entity-list-item__copy"
                        ><strong>{{ category.title || "Neue Kategorie" }}</strong
                        ><small>{{ channelSummary(category.channels) }}</small></span
                      >
                      <span class="entity-state" :class="{ inactive: category.active === false }">{{
                        category.active === false ? "Inaktiv" : "Aktiv"
                      }}</span>
                      <v-icon small>mdi-chevron-right</v-icon>
                    </button>
                    <div v-if="filteredCatalogCategories.length === 0" class="entity-empty">
                      <v-icon>mdi-account-search-outline</v-icon><strong>Keine Kategorie gefunden</strong
                      ><span>Ändere die Suche oder erstelle eine neue Kategorie.</span>
                    </div>
                  </div>
                </aside>

                <section v-if="selectedCatalogCategory" class="entity-editor" aria-label="Kategorie bearbeiten">
                  <div class="entity-editor__hero">
                    <span class="entity-editor__hero-icon"><v-icon>mdi-account-tag-outline</v-icon></span>
                    <div>
                      <span class="eyebrow">Mitgliedschaftskategorie</span>
                      <h3>{{ selectedCatalogCategory.title || "Neue Kategorie" }}</h3>
                      <p>{{ selectedCatalogCategory.config.description || "Steuert Abschlussart und optionale Zusatzangaben." }}</p>
                    </div>
                    <v-switch v-model="selectedCatalogCategory.active" inset hide-details label="Aktiv" :disabled="!catalogEditable"></v-switch>
                  </div>

                  <div class="editor-section">
                    <div class="editor-section__heading">
                      <span><v-icon small>mdi-tune-variant</v-icon></span>
                      <div><strong>Grundlagen</strong><small>Name, Kanäle und passende Vertragstypen.</small></div>
                    </div>
                    <div class="editor-field-grid">
                      <v-text-field v-model="selectedCatalogCategory.title" label="Titel" outlined :disabled="!catalogEditable"></v-text-field>
                      <v-text-field
                        v-model="selectedCatalogCategory.config.description"
                        label="Kurze Beschreibung"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-select
                        v-model="selectedCatalogCategory.channels"
                        :items="categoryChannelOptions"
                        multiple
                        chips
                        deletable-chips
                        label="Kanäle"
                        outlined
                        :disabled="!catalogEditable"></v-select>
                      <v-select
                        v-model="selectedCatalogCategory.contractTypeIds"
                        :items="contractTypeOptions"
                        item-text="label"
                        item-value="value"
                        multiple
                        chips
                        deletable-chips
                        label="Vertragstypen"
                        outlined
                        :disabled="!catalogEditable"></v-select>
                    </div>
                    <v-select
                      v-model="selectedCatalogCategory.studioIds"
                      :items="studioOptions"
                      item-text="label"
                      item-value="value"
                      multiple
                      chips
                      deletable-chips
                      label="Standorte (leer = alle)"
                      outlined
                      :disabled="!catalogEditable"></v-select>
                  </div>

                  <div class="editor-section">
                    <div class="editor-section__heading">
                      <span><v-icon small>mdi-form-textbox</v-icon></span>
                      <div><strong>Abschlussregeln</strong><small>Nur aktivieren, was im Creator wirklich benötigt wird.</small></div>
                    </div>
                    <div class="editor-toggle-grid">
                      <label
                        ><span><strong>Keine Aktivierungsgebühr</strong><small>Überspringt die Gebühr für diese Kategorie.</small></span
                        ><v-switch v-model="selectedCatalogCategory.config.noSignUpFee" inset hide-details :disabled="!catalogEditable"></v-switch
                      ></label>
                      <label
                        ><span><strong>Zusatzangabe verlangen</strong><small>Fragt beim Abschluss eine weitere Information ab.</small></span
                        ><v-switch v-model="selectedCatalogCategory.config.notes" inset hide-details :disabled="!catalogEditable"></v-switch
                      ></label>
                      <label :class="{ disabled: !selectedCatalogCategory.config.notes }"
                        ><span><strong>Als Auswahlliste</strong><small>Vorgegebene Werte statt Freitext.</small></span
                        ><v-switch
                          v-model="selectedCatalogCategory.config.notesSelection"
                          inset
                          hide-details
                          :disabled="!catalogEditable || !selectedCatalogCategory.config.notes"></v-switch
                      ></label>
                    </div>
                    <div v-if="selectedCatalogCategory.config.notes" class="editor-field-grid mt-5">
                      <v-text-field
                        v-model="selectedCatalogCategory.config.additionalInfo"
                        label="Frage oder Feldbezeichnung"
                        outlined
                        :disabled="!catalogEditable"></v-text-field>
                      <v-combobox
                        v-if="selectedCatalogCategory.config.notesSelection"
                        v-model="selectedCatalogCategory.config.notesSelectionOptions"
                        multiple
                        chips
                        deletable-chips
                        label="Auswahlwerte"
                        outlined
                        :disabled="!catalogEditable"></v-combobox>
                      <v-textarea
                        v-model="selectedCatalogCategory.config.notesDescription"
                        class="editor-field--wide"
                        label="Hilfetext zur Zusatzangabe"
                        rows="2"
                        outlined
                        :disabled="!catalogEditable"></v-textarea>
                    </div>
                  </div>

                  <div v-if="catalogEditable" class="entity-editor__footer">
                    <span>Änderungen werden erst mit „Speichern“ übernommen.</span>
                    <v-btn text color="error" @click="removeCatalogCategory(selectedCatalogCategory.id)"
                      ><v-icon left>mdi-delete-outline</v-icon>Kategorie entfernen</v-btn
                    >
                  </div>
                </section>
                <section v-else class="entity-editor entity-editor--empty" aria-label="Keine Kategorie ausgewählt">
                  <span><v-icon>mdi-account-tag-outline</v-icon></span>
                  <h3>Noch keine Kategorie</h3>
                  <p>Erstelle nur Kategorien, die im internen oder öffentlichen Abschluss wirklich gebraucht werden.</p>
                  <v-btn v-if="catalogEditable" color="primary" outlined @click="addCategory"
                    ><v-icon left>mdi-plus</v-icon>Kategorie erstellen</v-btn
                  >
                </section>
              </div>
            </v-card-text>
          </v-card>

          <v-card v-if="catalogSection === 'overrides'" dark class="surface-card pa-5" outlined>
            <div class="section-heading">
              <div>
                <h2>Standort-Ausnahmen</h2>
                <p>Nur hier entstehen zusätzliche Kombinationen. Der Normalfall bleibt global.</p>
              </div>
              <v-chip outlined>{{ catalog.overrides.length }} Overrides</v-chip>
            </div>
            <div v-if="catalogEditable" class="override-builder mt-4">
              <v-select v-model="newOverride.target" :items="overrideTargets" label="Art" outlined dense hide-details></v-select>
              <v-select
                v-model="newOverride.studioId"
                :items="studioOptions"
                item-text="label"
                item-value="value"
                label="Standort"
                outlined
                dense
                hide-details></v-select>
              <v-select
                v-if="newOverride.target === 'planTerm'"
                v-model="newOverride.planId"
                :items="planOptions"
                item-text="label"
                item-value="value"
                label="Plan"
                outlined
                dense
                hide-details
                @change="newOverride.variantId = null"></v-select>
              <v-select
                v-if="newOverride.target === 'planTerm'"
                v-model="newOverride.variantId"
                :items="overrideVariantOptions"
                item-text="label"
                item-value="value"
                label="Variante"
                outlined
                dense
                hide-details></v-select>
              <v-select
                v-else
                v-model="newOverride.addonId"
                :items="addonOptions"
                item-text="label"
                item-value="value"
                label="Zusatzpaket"
                outlined
                dense
                hide-details></v-select>
              <v-select
                v-model="newOverride.termId"
                :items="overrideTermOptions"
                item-text="label"
                item-value="value"
                label="Laufzeit"
                outlined
                dense
                hide-details></v-select>
              <v-text-field v-model.number="newOverride.price" type="number" prefix="CHF" label="Preis" outlined dense hide-details></v-text-field>
              <v-text-field
                v-if="newOverride.target === 'planTerm'"
                v-model.number="newOverride.signUpFee"
                type="number"
                prefix="CHF"
                label="Aktivierung"
                outlined
                dense
                hide-details></v-text-field>
              <v-btn color="primary" @click="addOverride"><v-icon left>mdi-plus</v-icon>Ausnahme</v-btn>
            </div>
            <v-simple-table v-if="catalog.overrides.length" dark class="mt-5 override-table">
              <thead>
                <tr>
                  <th>Art</th>
                  <th>Standort</th>
                  <th>Auswahl</th>
                  <th>Laufzeit</th>
                  <th>Werte</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(override, index) in catalog.overrides" :key="override.id">
                  <td>{{ override.target === "planTerm" ? "Plan" : "Zusatzpaket" }}</td>
                  <td>{{ studioName(override.studioId) }}</td>
                  <td>{{ override.target === "planTerm" ? planName(override.planId) : addonName(override.addonId) }}</td>
                  <td>{{ termName(override.termId) }}</td>
                  <td>
                    CHF {{ money(override.values.price)
                    }}<span v-if="override.values.signUpFee !== undefined"> / Aktivierung CHF {{ money(override.values.signUpFee) }}</span>
                  </td>
                  <td>
                    <v-btn v-if="catalogEditable" icon color="error" @click="catalog.overrides.splice(index, 1)"
                      ><v-icon>mdi-delete-outline</v-icon></v-btn
                    >
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>

          <v-card v-if="catalogSection === 'campaigns'" dark class="surface-card" outlined>
            <v-card-title class="card-title-row">
              <div>
                <h2>Aktionen und Regeln</h2>
                <p>Black Friday, Tag der offenen Tür oder die CHF-100-Regel sind Kampagnen statt gehackte Abos.</p>
              </div>
              <div class="button-row">
                <v-btn v-if="catalogEditable" outlined color="primary" @click="addActivationWaiverCampaign"
                  ><v-icon left>mdi-lightning-bolt-outline</v-icon>CHF-100-Regel</v-btn
                >
                <v-btn v-if="catalogEditable" color="primary" outlined @click="addCampaign"><v-icon left>mdi-plus</v-icon>Aktion</v-btn>
              </div>
            </v-card-title>
            <v-card-text>
              <v-expansion-panels dark accordion flat>
                <v-expansion-panel v-for="(campaign, campaignIndex) in catalog.campaigns" v-show="catalogMatches(campaign)" :key="campaign.id">
                  <v-expansion-panel-header>
                    <div class="panel-header">
                      <strong>{{ campaign.title }}</strong
                      ><span>{{ campaign.active ? "Aktiv" : "Inaktiv" }}</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="5"
                        ><v-text-field v-model="campaign.title" label="Titel" outlined :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="12" md="3"
                        ><v-text-field
                          v-model="campaign.code"
                          label="Aktionscode (optional)"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="2"
                        ><v-text-field
                          v-model.number="campaign.priority"
                          type="number"
                          label="Priorität"
                          outlined
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="2" class="toggle-col"
                        ><v-switch v-model="campaign.active" label="Aktiv" :disabled="!catalogEditable"></v-switch
                      ></v-col>
                      <v-col cols="12"
                        ><v-textarea v-model="campaign.description" label="Beschreibung" rows="2" outlined :disabled="!catalogEditable"></v-textarea
                      ></v-col>
                      <v-col cols="12" md="4"
                        ><v-text-field
                          v-model="campaign.startsAt"
                          type="date"
                          label="Start"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="12" md="4"
                        ><v-text-field
                          v-model="campaign.endsAt"
                          type="date"
                          label="Ende"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="12" md="4"
                        ><v-select
                          v-model="campaign.conditions.isRenewal"
                          :items="renewalOptions"
                          label="Abschlussart"
                          outlined
                          :disabled="!catalogEditable"></v-select
                      ></v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.studioIds"
                          :items="studioOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Standorte (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.channels"
                          :items="channelOptions"
                          multiple
                          chips
                          deletable-chips
                          label="Kanäle"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.contractTypeIds"
                          :items="contractTypeOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Vertragstypen"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.conditions.planIds"
                          :items="planOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Pläne (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.conditions.variantIds"
                          :items="variantOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Varianten (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="campaign.conditions.termIds"
                          :items="termOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Laufzeiten (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="campaign.conditions.categoryIds"
                          :items="categoryOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Kategorien (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="campaign.conditions.paymentMethodIds"
                          :items="paymentMethodOptions"
                          item-text="label"
                          item-value="value"
                          multiple
                          chips
                          deletable-chips
                          label="Zahlungsarten (leer = alle)"
                          outlined
                          :disabled="!catalogEditable"></v-select>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="campaign.conditions.minAddonTotal"
                          type="number"
                          prefix="CHF"
                          label="Paket-Schwelle"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="campaign.conditions.minAddonCount"
                          type="number"
                          label="Mindestanzahl Pakete"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3"
                        ><v-text-field
                          v-model.number="campaign.conditions.minAge"
                          type="number"
                          label="Mindestalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="3"
                        ><v-text-field
                          v-model.number="campaign.conditions.maxAge"
                          type="number"
                          label="Maximalalter"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="campaign.effects.planDiscountAmount"
                          type="number"
                          prefix="CHF"
                          label="Planrabatt"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3">
                        <v-text-field
                          v-model.number="campaign.effects.planDiscountPercent"
                          type="number"
                          suffix="%"
                          label="Planrabatt"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field>
                      </v-col>
                      <v-col cols="6" md="3"
                        ><v-text-field
                          v-model.number="campaign.effects.addonDiscountAmount"
                          type="number"
                          prefix="CHF"
                          label="Paketrabatt"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="3"
                        ><v-text-field
                          v-model.number="campaign.effects.addonDiscountPercent"
                          type="number"
                          suffix="%"
                          label="Paketrabatt"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="3"
                        ><v-text-field
                          v-model.number="campaign.effects.bonusMonths"
                          type="number"
                          label="Bonusmonate"
                          outlined
                          clearable
                          :disabled="!catalogEditable"></v-text-field
                      ></v-col>
                      <v-col cols="6" md="3" class="toggle-col"
                        ><v-switch v-model="campaign.stackable" label="Kombinierbar" :disabled="!catalogEditable"></v-switch
                      ></v-col>
                      <v-col cols="12" md="4" class="toggle-col"
                        ><v-switch v-model="campaign.autoApply" label="Automatisch anwenden" :disabled="!catalogEditable"></v-switch
                      ></v-col>
                      <v-col cols="12" md="4" class="toggle-col"
                        ><v-switch v-model="campaign.effects.waiveSignUpFee" label="Aktivierung schenken" :disabled="!catalogEditable"></v-switch
                      ></v-col>
                      <v-col cols="12" md="4" class="toggle-col"
                        ><v-switch
                          v-model="campaign.effects.giftCheapestAddon"
                          label="Günstigstes Paket schenken"
                          :disabled="!catalogEditable"></v-switch
                      ></v-col>
                    </v-row>
                    <div class="button-row button-row--end">
                      <v-btn v-if="catalogEditable" text color="error" @click="catalog.campaigns.splice(campaignIndex, 1)"
                        ><v-icon left>mdi-delete-outline</v-icon>Aktion entfernen</v-btn
                      >
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>
          </v-card>
        </template>
      </section>

      <section v-if="!isPublic && adminTab === 1" class="section-stack">
        <v-card dark class="surface-card pa-5" outlined>
          <div class="section-heading">
            <div>
              <h2>Kontrollierter Rollout</h2>
              <p>Interner Abschluss, Online-Abschluss, Website-Abos und TAC werden pro Standort separat geschaltet.</p>
            </div>
          </div>
        </v-card>
        <v-expansion-panels dark accordion>
          <v-expansion-panel v-for="studio in rolloutStudios" :key="studio._id">
            <v-expansion-panel-header>
              <div class="rollout-header">
                <div>
                  <strong>{{ studio.name }}</strong
                  ><span>{{ studio.code }}</span>
                </div>
                <v-chip small :color="rolloutActive(studio) ? 'success' : 'grey'" dark>{{
                  rolloutActive(studio) ? "V2 teilweise aktiv" : "Legacy"
                }}</v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="studio.rollout.catalog"
                    :items="publishedCatalogOptions"
                    item-text="label"
                    item-value="value"
                    label="Publizierter Katalog"
                    outlined
                    clearable></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="studio.rollout.internalFlow" :items="flowOptions" label="Interne Erstellung" outlined></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="studio.rollout.publicFlow" :items="flowOptions" label="Online-Vertrag" outlined></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="studio.rollout.websiteCatalog" :items="flowOptions" label="Website-Abos" outlined></v-select>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="studio.rollout.tacFlow" :items="tacFlowOptions" label="TAC-Prozess" outlined></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="rollout-types">
                    <span>Verfügbare Vertragstypen</span>
                    <div class="chip-wrap">
                      <v-chip v-for="contractType in rolloutContractTypes(studio)" :key="contractType.id" small outlined>
                        <v-icon left small>{{ contractType.kind === "medical" ? "mdi-medical-bag" : "mdi-shape-outline" }}</v-icon
                        >{{ contractType.title }}
                      </v-chip>
                      <small v-if="rolloutContractTypes(studio).length === 0">Katalog wählen, um Vertragstypen zu sehen.</small>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <div class="rollout-note">
                <v-icon color="info" class="mr-2">mdi-information-outline</v-icon>
                <span>Eine Umschaltung ändert keine alten Verträge. Neue Abschlüsse laufen ab dann über die gewählte Version.</span>
              </div>
              <div class="button-row button-row--end mt-4">
                <v-btn color="primary" :loading="savingRolloutId === studio._id" @click="saveRollout(studio)">
                  <v-icon left>mdi-content-save-outline</v-icon>Standort speichern
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </section>

      <section v-if="isPublic || (!isPublic && adminTab === 2)" class="creator-shell">
        <v-card v-if="!isPublic && !creatorData" dark class="surface-card creator-launcher mb-5" outlined>
          <div class="creator-launcher__intro">
            <span><v-icon>mdi-auto-fix</v-icon></span>
            <div>
              <div class="eyebrow">Geführter Abschluss</div>
              <h2>Neuen Vertrag starten</h2>
              <p>Standort und Katalog wählen. Der Assistent zeigt danach nur passende Optionen.</p>
            </div>
          </div>
          <div class="creator-launcher__form">
            <div>
              <v-select
                v-model="creatorStudioId"
                :items="studioOptions"
                item-text="label"
                item-value="value"
                label="Standort"
                outlined
                hide-details
                @change="resetCreatorData"></v-select>
            </div>
            <div>
              <v-select
                v-model="creatorCatalogId"
                :items="creatorCatalogOptions"
                item-text="label"
                item-value="value"
                label="Katalogversion"
                outlined
                hide-details
                @change="resetCreatorData"></v-select>
            </div>
            <v-btn large color="primary" :disabled="!creatorStudioId || !creatorCatalogId" @click="loadCreatorPreview"
              >Setup starten<v-icon right>mdi-arrow-right</v-icon></v-btn
            >
          </div>
          <v-alert v-if="!hasPublishedCreatorCatalog" type="warning" outlined class="creator-launcher__notice">
            Es gibt noch keinen publizierten Katalog. Entwürfe können als Vorschau getestet, aber noch nicht als Vertrag gespeichert werden.
          </v-alert>
        </v-card>

        <v-card v-if="creatorData && !createdContract" :dark="!isPublic" class="creator-card" outlined>
          <div class="creator-app-layout">
            <aside class="creator-progress" aria-label="Fortschritt">
              <div class="creator-progress__brand">
                <span><v-icon>mdi-auto-fix</v-icon></span>
                <div><small>Vertragsassistent</small><strong>Einfach zum Abschluss</strong></div>
              </div>
              <div v-if="!isPublic" class="creator-progress__context">
                <small>Aktiver Abschluss</small>
                <strong>{{ creatorStudioName }}</strong>
                <span>{{ creatorCatalogVersionLabel }}</span>
                <button v-if="!editingTemporaryContractId" type="button" class="creator-context-change" @click="resetCreatorData">
                  <v-icon small>mdi-swap-horizontal</v-icon>Auswahl ändern
                </button>
              </div>
              <div class="creator-progress__meter">
                <span><i :style="{ width: creatorProgressPercent + '%' }"></i></span><small>{{ creatorProgressPercent }}%</small>
              </div>
              <button
                v-for="step in creatorStepLabels.length"
                :key="step"
                type="button"
                class="creator-progress__step"
                :class="{ active: creatorStep === step, done: creatorStep > step }"
                @click="goToCreatorStep(step)">
                <span
                  ><v-icon small>{{ creatorStep > step ? "mdi-check" : creatorStepIcons[step - 1] }}</v-icon></span
                >
                <div>
                  <small>Schritt {{ step }}</small
                  ><strong>{{ creatorStepLabels[step - 1] }}</strong>
                </div>
              </button>
              <div class="creator-progress__trust">
                <v-icon small>mdi-shield-check-outline</v-icon><span>Preise und Regeln kommen direkt aus dem freigegebenen Katalog.</span>
              </div>
            </aside>

            <main class="creator-content">
              <div ref="creatorScroll" class="creator-content__scroll">
                <div class="creator-stage-kicker">
                  <span>Schritt {{ creatorStep }} von {{ creatorStepLabels.length }}</span
                  ><b>{{ creatorStepLabels[creatorStep - 1] }}</b>
                </div>
                <v-alert v-if="editingTemporaryContractId" type="warning" outlined class="creator-draft-alert">
                  <div>
                    <strong>Temporären Vertrag abschliessen</strong
                    ><span>Abo und Preise bleiben unverändert. Ergänze die Abschlussdaten und Unterschriften.</span>
                  </div>
                </v-alert>
                <v-alert v-if="!isPublic && creatorData.catalog && creatorData.catalog.status !== 'published'" type="info" outlined>
                  Vorschau: Preise und Regeln können getestet werden, ein verbindlicher Abschluss ist erst mit einer publizierten Version möglich.
                </v-alert>
                <v-alert v-if="submitError" type="error" outlined dismissible @input="submitError = ''">{{ submitError }}</v-alert>

                <div v-if="creatorStep === 1" class="onboarding-stage onboarding-stage--welcome">
                  <div class="onboarding-stage__hero">
                    <span><v-icon>mdi-rocket-launch-outline</v-icon></span>
                    <div>
                      <small>Willkommen</small>
                      <h2>Dein Abo. In wenigen klaren Schritten.</h2>
                      <p>Wir merken uns jede Auswahl und zeigen immer nur das, was als Nächstes wichtig ist.</p>
                    </div>
                  </div>

                  <div class="onboarding-promise-row">
                    <span><v-icon small>mdi-check-decagram-outline</v-icon>Transparente Preise</span>
                    <span><v-icon small>mdi-lightning-bolt-outline</v-icon>Passende Empfehlungen</span>
                    <span><v-icon small>mdi-shield-check-outline</v-icon>Sicherer Abschluss</span>
                  </div>

                  <div class="onboarding-profile-grid">
                    <section class="onboarding-profile-card">
                      <div class="onboarding-block-heading">
                        <span><v-icon>mdi-account-outline</v-icon></span>
                        <div>
                          <small>{{ isPublic ? "Dein Roody-Account" : "Vertragspartner" }}</small
                          ><strong>{{ isPublic ? "Wer bist du?" : "Für wen ist das Abo?" }}</strong>
                        </div>
                      </div>
                      <template v-if="!isPublic">
                        <user-search
                          v-if="!creatorUser"
                          :value="creatorUser"
                          dark
                          label="Kunde suchen"
                          return-user
                          ignore-rights
                          @update="creatorUserChanged"></user-search>
                        <div v-else class="onboarding-selected-user">
                          <span>{{ (creatorUser.vorname || "?").slice(0, 1) }}{{ (creatorUser.nachname || "?").slice(0, 1) }}</span>
                          <div>
                            <strong>{{ creatorUser.vorname }} {{ creatorUser.nachname }}</strong
                            ><small>{{ creatorUser.email }}</small>
                          </div>
                          <button type="button" aria-label="Anderen Kunden wählen" @click="clearCreatorUser">
                            <v-icon small>mdi-swap-horizontal</v-icon>
                          </button>
                        </div>
                      </template>
                      <template v-else>
                        <div v-if="publicAuthenticated && publicBirthdayVerified" class="onboarding-account-ready">
                          <v-icon color="success">mdi-check-circle-outline</v-icon>
                          <div><strong>Account verbunden</strong><small>Dein Geburtsdatum wurde sicher übernommen.</small></div>
                        </div>
                        <div v-else class="onboarding-account-choice">
                          <p>Mit deinem Account übernehmen wir später die Vertragsdaten automatisch.</p>
                          <div>
                            <v-btn color="primary" @click="showRegisterDialog = true">Account erstellen</v-btn
                            ><v-btn outlined color="primary" @click="showLoginDialog = true">Einloggen</v-btn>
                          </div>
                          <small>Du kannst das Angebot vorher trotzdem in Ruhe zusammenstellen.</small>
                        </div>
                      </template>
                    </section>

                    <section class="onboarding-profile-card">
                      <div class="onboarding-block-heading">
                        <span><v-icon>mdi-cake-variant-outline</v-icon></span>
                        <div><small>Persönliche Empfehlung</small><strong>Wie alt bist du?</strong></div>
                      </div>
                      <v-text-field
                        v-model.number="creatorAge"
                        type="number"
                        min="0"
                        max="110"
                        label="Alter"
                        suffix="Jahre"
                        outlined
                        hide-details
                        :disabled="creatorAgeLocked"
                        @change="creatorAgeChanged"></v-text-field>
                      <p>
                        {{
                          creatorAgeLocked
                            ? "Das Alter wurde sicher aus dem Kundenkonto übernommen."
                            : "Das Alter steuert nur, welche Abos und Pakete wirklich passen."
                        }}
                      </p>
                    </section>
                  </div>

                  <section class="onboarding-goals">
                    <div class="onboarding-block-heading">
                      <span><v-icon>mdi-heart-pulse</v-icon></span>
                      <div><small>Optional</small><strong>Was ist dir beim Training wichtig?</strong></div>
                    </div>
                    <div class="onboarding-goal-grid">
                      <button
                        v-for="goal in goalOptions"
                        :key="goal.value"
                        type="button"
                        :class="{ selected: creatorGoals.includes(goal.value) }"
                        @click="toggleGoal(goal.value)">
                        <v-icon>{{ goal.icon }}</v-icon
                        ><span>{{ goal.label }}</span
                        ><v-icon small>{{ creatorGoals.includes(goal.value) ? "mdi-check-circle" : "mdi-plus-circle-outline" }}</v-icon>
                      </button>
                    </div>
                  </section>
                </div>

                <div v-if="creatorStep === 2" class="onboarding-stage">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-arm-flex-outline</v-icon></v-avatar>
                    <div>
                      <h2>Was passt zu dir?</h2>
                      <p>Wähle zuerst den Vertragstyp. Danach erscheinen nur die passenden Kategorien.</p>
                    </div>
                  </div>
                  <h3 class="choice-title choice-title--first">Welche Art von Mitgliedschaft suchst du?</h3>
                  <div class="type-choice-grid">
                    <button
                      v-for="contractType in creatorData.contractTypes"
                      :key="contractType.id"
                      class="type-choice-card"
                      :class="{ selected: creatorContractTypeId === contractType.id }"
                      @click="selectCreatorContractType(contractType)">
                      <span
                        ><v-icon>{{
                          contractType.kind === "medical"
                            ? "mdi-medical-bag"
                            : contractType.kind === "rolling"
                            ? "mdi-autorenew"
                            : "mdi-calendar-lock"
                        }}</v-icon></span
                      >
                      <div>
                        <strong>{{ contractType.title }}</strong>
                        <p>{{ contractType.description }}</p>
                      </div>
                      <v-icon class="type-choice-card__check">{{
                        creatorContractTypeId === contractType.id ? "mdi-check-circle" : "mdi-chevron-right-circle-outline"
                      }}</v-icon>
                    </button>
                  </div>
                  <v-alert v-if="!creatorContractTypeId" type="info" outlined class="mt-4"
                    >Wähle zuerst einen Vertragstyp. Danach zeigen wir nur passende Pläne und Optionen.</v-alert
                  >
                  <template v-if="creatorContractTypeId">
                    <template v-if="creatorCategories.length">
                      <h3 class="choice-title">Was trifft auf dich zu?</h3>
                      <div class="choice-grid choice-grid--compact">
                        <button
                          v-for="category in creatorCategories"
                          :key="category.id"
                          class="choice-card choice-card--compact"
                          :class="{ selected: creatorCategoryId === category.id }"
                          @click="selectCategory(category)">
                          <strong>{{ category.title }}</strong>
                          <p>{{ category.config.description || category.config.additionalInfo || "Mitgliedschaftskategorie" }}</p>
                          <v-chip v-if="category.config.noSignUpFee" small color="success" dark>Keine Aktivierungsgebühr</v-chip>
                        </button>
                      </div>
                      <v-select
                        v-if="selectedCategory && selectedCategory.config.notes && selectedCategory.config.notesSelection"
                        v-model="creatorCategoryNotes"
                        :items="selectedCategory.config.notesSelectionOptions"
                        :label="selectedCategory.config.additionalInfo || 'Zusatzangabe'"
                        outlined
                        class="mt-4"></v-select>
                      <v-text-field
                        v-else-if="selectedCategory && selectedCategory.config.notes"
                        v-model="creatorCategoryNotes"
                        :label="selectedCategory.config.additionalInfo || 'Zusatzangabe'"
                        outlined
                        class="mt-4"></v-text-field>
                      <v-alert v-if="selectedCategory && selectedCategory.config.notesDescription" type="info" outlined>{{
                        selectedCategory.config.notesDescription
                      }}</v-alert>
                      <v-switch
                        v-if="!isPublic"
                        v-model="creatorIsRenewal"
                        label="Als Wiederabschluss behandeln"
                        color="primary"
                        @change="resetQuote"></v-switch>
                    </template>
                  </template>
                </div>

                <div v-if="creatorStep === 3" class="onboarding-stage">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-card-account-details-star-outline</v-icon></v-avatar>
                    <div>
                      <h2>Wähle dein Abo</h2>
                      <p>Alle angezeigten Pläne passen zu Alter, Vertragstyp und Standort.</p>
                    </div>
                  </div>
                  <div class="onboarding-plan-grid">
                    <button
                      v-for="plan in eligiblePlans"
                      :key="plan.id"
                      type="button"
                      class="onboarding-plan-card"
                      :class="{ selected: creatorPlanId === plan.id }"
                      @click="selectPlan(plan)">
                      <div class="onboarding-plan-card__top">
                        <span><v-icon>mdi-dumbbell</v-icon></span
                        ><v-icon>{{ creatorPlanId === plan.id ? "mdi-check-circle" : "mdi-circle-outline" }}</v-icon>
                      </div>
                      <div>
                        <strong>{{ plan.title }}</strong>
                        <p>{{ plan.description || "Flexibel passend zu deinen Zielen." }}</p>
                      </div>
                      <div class="chip-wrap">
                        <v-chip v-for="tag in (plan.tags || []).slice(0, 3)" :key="tag" small outlined>{{ tag }}</v-chip>
                      </div>
                      <div v-if="planStartingPrice(plan) !== null" class="onboarding-plan-card__price">
                        <small>ab</small><strong>CHF {{ money(planStartingPrice(plan)) }}</strong>
                      </div>
                    </button>
                  </div>
                  <div v-if="creatorAge !== null && eligiblePlans.length === 0" class="onboarding-empty">
                    <span><v-icon>mdi-filter-remove-outline</v-icon></span>
                    <h3>Noch kein passendes Abo</h3>
                    <p>Gehe einen Schritt zurück und prüfe Alter oder Vertragstyp.</p>
                  </div>
                </div>

                <div v-if="creatorStep === 4" class="onboarding-stage">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-calendar-range</v-icon></v-avatar>
                    <div>
                      <h2>Wie möchtest du starten?</h2>
                      <p>Wähle Zahlungsvariante und Laufzeit. Der Preis kommt direkt aus dem Katalog.</p>
                    </div>
                  </div>
                  <h3 class="choice-title">Zahlungsvariante</h3>
                  <div class="choice-grid choice-grid--compact">
                    <button
                      v-for="variant in creatorVariants"
                      :key="variant.id"
                      class="choice-card choice-card--compact"
                      :class="{ selected: creatorVariantId === variant.id }"
                      @click="selectVariant(variant)">
                      <strong>{{ variant.title }}</strong>
                      <p>{{ variant.paymentMode === "installments" ? variant.installments + " Raten" : "Einmalzahlung" }}</p>
                    </button>
                  </div>
                  <h3 class="choice-title">Laufzeit</h3>
                  <div class="choice-grid choice-grid--compact">
                    <button
                      v-for="term in creatorTerms"
                      :key="term.id"
                      class="choice-card choice-card--compact"
                      :class="{ selected: creatorTermId === term.id }"
                      @click="selectTerm(term)">
                      <v-chip v-if="term.saleActive" small color="success" dark>Aktion</v-chip>
                      <strong>{{ term.label }}</strong>
                      <p>
                        <span v-if="term.saleActive" class="old-price">CHF {{ money(term.regularPrice) }}</span> CHF {{ money(term.price) }}
                      </p>
                      <small v-if="term.signUpFee">plus CHF {{ money(term.signUpFee) }} Aktivierung</small>
                    </button>
                  </div>
                  <v-text-field
                    v-if="selectedTerm && selectedTerm.unit !== 'fixed'"
                    v-model.number="creatorUnits"
                    type="number"
                    :min="selectedTerm.minUnits"
                    :max="selectedTerm.maxUnits"
                    label="Anzahl Einheiten"
                    outlined
                    @change="refreshQuote"></v-text-field>
                </div>

                <div v-if="creatorStep === 5" class="onboarding-stage">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-puzzle-plus-outline</v-icon></v-avatar>
                    <div>
                      <h2>Möchtest du dein Abo ergänzen?</h2>
                      <p>Wir zeigen nur Pakete, die zu Alter, Abo und Standort passen.</p>
                    </div>
                  </div>
                  <div class="addon-grid">
                    <div v-for="addon in availableAddons" :key="addon.id" class="addon-card" :class="{ selected: addonSelected(addon.id) }">
                      <button class="addon-card__main" @click="toggleAddon(addon)">
                        <span class="addon-card__check"
                          ><v-icon>{{ addonSelected(addon.id) ? "mdi-check" : "mdi-plus" }}</v-icon></span
                        >
                        <div>
                          <strong>{{ addon.title }}</strong>
                          <p>{{ addon.description || addon.tags.join(", ") }}</p>
                        </div>
                        <b>CHF {{ money(addonPrice(addon)) }}</b>
                      </button>
                      <v-text-field
                        v-if="addon.amountSelectable && addonSelected(addon.id)"
                        :value="creatorAddonQuantities[addon.id]"
                        type="number"
                        :min="addon.minQuantity"
                        :max="addon.maxQuantity"
                        label="Menge"
                        outlined
                        dense
                        hide-details
                        @input="setAddonQuantity(addon, $event)"></v-text-field>
                    </div>
                  </div>
                  <div v-if="availableAddons.length === 0" class="onboarding-empty onboarding-empty--compact">
                    <span><v-icon>mdi-check-decagram-outline</v-icon></span>
                    <div>
                      <h3>Alles Wichtige ist schon drin</h3>
                      <p>Für diese Auswahl gibt es keine zusätzlichen Pakete. Du kannst direkt weitergehen.</p>
                    </div>
                  </div>

                  <v-text-field
                    v-model="creatorCampaignCode"
                    label="Aktionscode (optional)"
                    prepend-inner-icon="mdi-ticket-percent-outline"
                    outlined
                    clearable
                    class="mt-5"
                    @change="refreshQuote"></v-text-field>

                  <v-progress-linear v-if="quoting" indeterminate color="primary" class="my-5"></v-progress-linear>
                  <template v-if="quote">
                    <v-alert
                      v-for="opportunity in quote.result.opportunities"
                      :key="opportunity.campaignId + opportunity.type"
                      color="amber lighten-4"
                      icon="mdi-lightbulb-on-outline"
                      outlined>
                      {{ opportunity.message }}
                    </v-alert>
                    <div v-if="quote.result.recommendations.length" class="recommendation-box">
                      <div>
                        <strong>Smarte Empfehlungen</strong>
                        <p>Nach Zielen und Preisregeln berechnet, ohne Blackbox.</p>
                      </div>
                      <div class="recommendation-list">
                        <button
                          v-for="recommendation in quote.result.recommendations.slice(0, 3)"
                          :key="recommendation.title"
                          @click="applyRecommendation(recommendation)">
                          <span>{{ recommendation.title }}</span
                          ><small>{{ recommendation.reason }}</small
                          ><b>+ CHF {{ money(recommendation.price) }}</b>
                        </button>
                      </div>
                    </div>
                    <div class="price-summary">
                      <div
                        v-for="line in quote.result.lines"
                        :key="line.type + line.refId + line.title"
                        :class="{ discount: line.type === 'discount' }">
                        <span
                          >{{ line.title }}<small v-if="line.quantity > 1"> × {{ line.quantity }}</small></span
                        >
                        <b>{{ line.total < 0 ? "-" : "" }}CHF {{ money(Math.abs(line.total)) }}</b>
                      </div>
                      <div class="price-summary__total">
                        <span>Total</span><strong>CHF {{ money(quote.result.total) }}</strong>
                      </div>
                    </div>
                  </template>
                </div>

                <div v-if="creatorStep === 6" class="onboarding-stage">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-calendar-check-outline</v-icon></v-avatar>
                    <div>
                      <h2>Wann geht es los?</h2>
                      <p>Nur noch Start, Zahlungsart und – falls nötig – die betreuende Fachperson.</p>
                    </div>
                  </div>
                  <div v-if="!isPublic && creatorUser" class="creator-fixed-customer creator-fixed-customer--compact">
                    <span><v-icon>mdi-account-check-outline</v-icon></span>
                    <div>
                      <small>Vertragspartner</small><strong>{{ creatorUser.vorname }} {{ creatorUser.nachname }}</strong>
                      <p>{{ creatorUser.email }}</p>
                    </div>
                    <v-icon color="success">mdi-check-circle</v-icon>
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="creatorStartDate"
                        type="date"
                        label="Vertragsbeginn"
                        prepend-inner-icon="mdi-calendar-start-outline"
                        outlined
                        @change="creatorStartDateChanged"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="creatorUseStartDate"
                        type="date"
                        :min="creatorStartDate"
                        label="Nutzungsbeginn"
                        prepend-inner-icon="mdi-run-fast"
                        outlined></v-text-field>
                    </v-col>
                    <v-col v-if="creatorData.companies && creatorData.companies.length > 1" cols="12" md="6">
                      <v-select
                        v-model="creatorCompanyId"
                        :items="creatorData.companies"
                        item-text="name"
                        item-value="id"
                        label="Vertragsfirma"
                        prepend-inner-icon="mdi-office-building-outline"
                        outlined
                        :disabled="editingTemporaryContractId !== null"
                        @change="refreshQuote"></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="creatorPaymentMethodId"
                        :items="creatorPaymentMethods"
                        item-text="title"
                        item-value="id"
                        label="Zahlungsart"
                        prepend-inner-icon="mdi-credit-card-outline"
                        outlined
                        :disabled="editingTemporaryContractId !== null"
                        @change="paymentMethodChanged"></v-select>
                    </v-col>
                    <v-col v-if="selectedPaymentMethod && selectedPaymentMethod.selectDate" cols="12" md="6">
                      <v-text-field
                        v-model="creatorDebitDate"
                        type="date"
                        label="Gewünschtes Belastungsdatum"
                        prepend-inner-icon="mdi-calendar-clock-outline"
                        outlined></v-text-field>
                    </v-col>
                    <v-col v-if="selectedContractType && selectedContractType.requiresPhysiotherapist" cols="12">
                      <div class="physio-selection">
                        <div class="physio-selection__intro">
                          <span><v-icon color="info">mdi-medical-bag</v-icon></span>
                          <div>
                            <strong>Betreuende Physiotherapie</strong>
                            <p>Für MTT ist die betreuende Fachperson Bestandteil des Vertrags.</p>
                          </div>
                        </div>
                        <v-select
                          v-model="creatorPhysiotherapistId"
                          :items="creatorPhysiotherapists"
                          :item-text="physiotherapistLabel"
                          item-value="_id"
                          label="Physiotherapeutin oder Physiotherapeut"
                          outlined
                          :disabled="editingTemporaryContractId !== null"
                          hide-details></v-select>
                        <v-alert v-if="creatorPhysiotherapists.length === 0" type="warning" outlined class="mt-3 mb-0">
                          Für diesen Standort ist noch keine aktive Physiotherapie hinterlegt. Ein MTT-Vertrag kann deshalb noch nicht abgeschlossen
                          werden.
                        </v-alert>
                      </div>
                    </v-col>
                  </v-row>

                  <div class="creator-readiness" :class="{ ready: creatorTemporaryIssues.length === 0 }">
                    <span
                      ><v-icon>{{ creatorTemporaryIssues.length === 0 ? "mdi-check-decagram" : "mdi-progress-check" }}</v-icon></span
                    >
                    <div>
                      <strong>{{
                        creatorTemporaryIssues.length === 0
                          ? "Alle Basisdaten sind bereit"
                          : "Noch " + creatorTemporaryIssues.length + " Angabe" + (creatorTemporaryIssues.length === 1 ? "" : "n")
                      }}</strong>
                      <p>
                        {{
                          creatorTemporaryIssues.length === 0
                            ? "Du kannst den Vertrag jetzt temporär sichern oder zum Abschluss weitergehen."
                            : creatorTemporaryIssues[0]
                        }}
                      </p>
                    </div>
                  </div>
                </div>

                <div v-if="creatorStep === 7" class="onboarding-stage onboarding-stage--finish">
                  <div class="conversation-title">
                    <v-avatar color="primary" size="44"><v-icon dark>mdi-file-sign</v-icon></v-avatar>
                    <div>
                      <h2>Alles klar für den Abschluss</h2>
                      <p>Bedingungen bestätigen, unterschreiben und den Vertrag sicher einreichen.</p>
                    </div>
                  </div>
                  <details v-if="legalContent" class="legal-disclosure">
                    <summary>
                      <span><v-icon color="primary">mdi-shield-check-outline</v-icon></span>
                      <div>
                        <small>Vertragsbedingungen</small><strong>{{ legalContent.title }}</strong>
                        <p>{{ selectedContractType.title }} · zum Lesen aufklappen</p>
                      </div>
                      <v-icon class="legal-disclosure__chevron">mdi-chevron-down</v-icon>
                    </summary>
                    <div class="contract-copy legal-copy">
                      <p v-if="legalContent.preamble" class="legal-copy__preamble">{{ legalContent.preamble }}</p>
                      <ol v-if="legalContent.conditions.length">
                        <li v-for="condition in legalContent.conditions" :key="condition.id" :class="{ highlight: condition.highlight }">
                          {{ condition.text }}
                        </li>
                      </ol>
                      <p v-if="legalContent.confirmation" class="legal-copy__confirmation">{{ legalContent.confirmation }}</p>
                    </div>
                  </details>
                  <v-alert v-if="isPublic && !publicAuthenticated" type="info" outlined>
                    <div class="account-gate">
                      <div>
                        <strong>Ein letzter Schritt für deinen Vertrag</strong>
                        <p>
                          Logge dich ein oder erstelle deinen Roody-Account. Danach wird dein hinterlegtes Geburtsdatum nochmals gegen die
                          Angebotsregeln geprüft.
                        </p>
                      </div>
                      <div class="button-row">
                        <v-btn color="primary" @click="showRegisterDialog = true">Account erstellen</v-btn>
                        <v-btn outlined color="primary" @click="showLoginDialog = true">Einloggen</v-btn>
                      </div>
                    </div>
                  </v-alert>
                  <v-alert v-else-if="isPublic && !publicBirthdayVerified" type="warning" outlined>
                    Im Roody-Account fehlt ein gültiges Geburtsdatum. Bitte ergänze es, bevor der Vertrag abgeschlossen wird.
                  </v-alert>
                  <v-checkbox v-model="creatorTermsAccepted" color="primary">
                    <template v-slot:label
                      ><span class="checkbox-label">Ich akzeptiere die Vertragsbedingungen und bestätige die Preisübersicht.</span></template
                    >
                  </v-checkbox>
                  <v-checkbox v-model="creatorMarketingAccepted" color="primary">
                    <template v-slot:label
                      ><span class="checkbox-label">Ich möchte Informationen zu passenden Angeboten erhalten (optional).</span></template
                    >
                  </v-checkbox>

                  <div class="signature-box">
                    <label class="field-label">Unterschrift</label>
                    <signature-pad
                      ref="signaturePad"
                      max-width="100%"
                      white-bg
                      auto-save-signature
                      @saveSignature="creatorSignature = $event"
                      @resetDrawing="creatorSignature = null"></signature-pad>
                  </div>

                  <div v-if="isMinor" class="signature-box minor-box">
                    <div class="mb-4">
                      <label class="field-label">Gesetzlicher Vertreter</label>
                      <p class="secondary-text">Bei Minderjährigen sind Kontaktdaten und eine zweite Unterschrift erforderlich.</p>
                    </div>
                    <v-row>
                      <v-col cols="12" sm="6"
                        ><v-text-field v-model="creatorLegalRepresentative.firstName" label="Vorname" outlined></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6"
                        ><v-text-field v-model="creatorLegalRepresentative.lastName" label="Nachname" outlined></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6"
                        ><v-text-field v-model="creatorLegalRepresentative.email" type="email" label="E-Mail" outlined></v-text-field
                      ></v-col>
                      <v-col cols="12" sm="6"
                        ><v-text-field v-model="creatorLegalRepresentative.relationship" label="Beziehung" outlined></v-text-field
                      ></v-col>
                    </v-row>
                    <label class="field-label">Unterschrift gesetzlicher Vertreter</label>
                    <signature-pad
                      ref="legalSignaturePad"
                      max-width="100%"
                      white-bg
                      auto-save-signature
                      @saveSignature="creatorLegalSignature = $event"
                      @resetDrawing="creatorLegalSignature = null"></signature-pad>
                  </div>

                  <div v-if="quote" class="final-total">
                    <span>Vertragstotal</span><strong>CHF {{ money(quote.result.total) }}</strong>
                  </div>
                  <div class="creator-readiness creator-readiness--finish" :class="{ ready: creatorCompletionIssues.length === 0 }">
                    <span
                      ><v-icon>{{ creatorCompletionIssues.length === 0 ? "mdi-check-decagram" : "mdi-format-list-checks" }}</v-icon></span
                    >
                    <div>
                      <strong>{{
                        creatorCompletionIssues.length === 0
                          ? "Bereit zum Abschluss"
                          : "Noch " + creatorCompletionIssues.length + " Punkt" + (creatorCompletionIssues.length === 1 ? "" : "e")
                      }}</strong>
                      <p>
                        {{
                          creatorCompletionIssues.length === 0 ? "Alle Pflichtangaben und Unterschriften sind vorhanden." : creatorCompletionIssues[0]
                        }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="creator-actions">
                <v-btn
                  v-if="creatorStep > 1 && !editingTemporaryContractId"
                  text
                  class="creator-actions__back"
                  @click="goToCreatorStep(creatorStep - 1)"
                  ><v-icon left>mdi-chevron-left</v-icon>Zurück</v-btn
                >
                <div class="creator-action-hint">
                  <v-icon small>{{ creatorStepIssues.length ? "mdi-alert-circle-outline" : "mdi-lightbulb-outline" }}</v-icon
                  ><span>{{ creatorNextHint }}</span>
                </div>
                <div class="creator-actions__buttons">
                  <v-btn
                    v-if="!isPublic && creatorStep >= 6 && !editingTemporaryContractId"
                    outlined
                    color="warning"
                    large
                    :loading="savingTemporary"
                    @click="saveTemporaryContract">
                    <v-icon left>mdi-content-save-clock-outline</v-icon><span class="creator-action-label--full">Temporär speichern</span
                    ><span class="creator-action-label--short">Entwurf</span>
                  </v-btn>
                  <v-btn v-if="creatorStep < creatorStepLabels.length" color="primary" large :loading="quoting" @click="nextCreatorStep">
                    Weiter<v-icon right>mdi-chevron-right</v-icon>
                  </v-btn>
                  <v-btn v-else color="primary" large :loading="submitting" @click="submitContract">
                    <v-icon left>mdi-file-sign</v-icon><span class="creator-action-label--full">Verbindlich abschliessen</span
                    ><span class="creator-action-label--short">Abschliessen</span>
                  </v-btn>
                </div>
              </div>
            </main>

            <aside class="creator-live-summary" aria-label="Aktuelle Auswahl">
              <div class="creator-live-summary__heading">
                <span><v-icon>mdi-receipt-text-check-outline</v-icon></span>
                <div><small>Live-Zusammenfassung</small><strong>Deine Auswahl</strong></div>
              </div>
              <div class="creator-summary-location">
                <v-icon small>mdi-map-marker-outline</v-icon><span>{{ creatorStudioName }}</span>
              </div>
              <div class="creator-summary-list">
                <div>
                  <span>Vertragstyp</span><strong>{{ selectedContractType ? selectedContractType.title : "Noch offen" }}</strong>
                </div>
                <div>
                  <span>Abo</span><strong>{{ selectedPlan ? selectedPlan.title : "Noch offen" }}</strong>
                </div>
                <div>
                  <span>Laufzeit</span><strong>{{ selectedTerm ? selectedTerm.label : "Noch offen" }}</strong>
                </div>
                <div>
                  <span>Extras</span><strong>{{ selectedAddonArray.length ? selectedAddonArray.length + " gewählt" : "Keine" }}</strong>
                </div>
              </div>
              <div v-if="quote" class="creator-summary-total">
                <span>Vertragstotal</span><strong>CHF {{ money(quote.result.total) }}</strong
                ><small>Gemäss aktueller Auswahl</small>
              </div>
              <div v-else class="creator-summary-placeholder">
                <v-icon>mdi-lightbulb-outline</v-icon><span>Dein Preis erscheint automatisch, sobald Abo und Laufzeit gewählt sind.</span>
              </div>
            </aside>
          </div>
        </v-card>

        <v-card v-if="createdContract" :dark="!isPublic" class="success-card pa-8 text-center" outlined>
          <v-avatar color="success" size="72" class="mb-5"><v-icon dark size="40">mdi-check</v-icon></v-avatar>
          <h2>Vertrag erfolgreich erstellt</h2>
          <p>Vertragsnummer {{ createdContract.number }}. Der Vertrag liegt sauber in der V2-Verwaltung und wurde noch nicht live an TAC gesendet.</p>
          <v-btn color="primary" @click="startAnotherContract">Weiteren Vertrag erstellen</v-btn>
        </v-card>

        <v-card v-if="!creatorData && isPublic && !loading" class="surface-card pa-8 text-center" outlined>
          <v-icon size="64" color="grey">mdi-clock-outline</v-icon>
          <h2 class="mt-4">Dieses Angebot ist noch nicht freigeschaltet</h2>
          <p>Bitte gehe zur Standortauswahl zurück oder versuche es später erneut.</p>
        </v-card>
      </section>

      <section v-if="!isPublic && adminTab === 3" class="section-stack contract-management">
        <v-card dark class="surface-card contract-management__command" outlined>
          <div class="contract-management__hero">
            <div class="contract-management__identity">
              <span><v-icon>mdi-file-document-multiple-outline</v-icon></span>
              <div>
                <div class="eyebrow">Vertragszentrale</div>
                <h2>Alles an einem Ort</h2>
                <p>Prüfen, kommentieren, weiterführen und kontrolliert an TAC übergeben.</p>
              </div>
            </div>
            <div class="contract-management__actions">
              <v-btn outlined large @click="loadContracts"><v-icon left>mdi-refresh</v-icon>Aktualisieren</v-btn>
              <v-btn color="primary" large @click="adminTab = 2"><v-icon left>mdi-plus</v-icon>Neuer Vertrag</v-btn>
            </div>
          </div>

          <div class="contract-status-rail" aria-label="Vertragsstatus">
            <button
              v-for="view in contractStatusViews"
              :key="view.value"
              type="button"
              :class="{ active: contractFilters.status === view.value }"
              @click="selectContractStatus(view.value)">
              <span class="contract-status-rail__icon" :class="`contract-status-rail__icon--${view.color}`"
                ><v-icon>{{ view.icon }}</v-icon></span
              >
              <span
                ><small>{{ view.label }}</small
                ><strong>{{ contractStats.counts[view.value] || 0 }}</strong></span
              >
              <v-icon class="contract-status-rail__arrow">mdi-chevron-right</v-icon>
            </button>
          </div>

          <div class="contract-filter-bar">
            <v-text-field
              v-model="contractFilters.search"
              label="Vertrag oder Kunde suchen"
              prepend-inner-icon="mdi-magnify"
              outlined
              hide-details
              clearable
              @click:clear="applyContractFilters"
              @keyup.enter="applyContractFilters"></v-text-field>
            <v-select
              v-model="contractFilters.studioId"
              :items="studioOptions"
              item-text="label"
              item-value="value"
              label="Alle Standorte"
              prepend-inner-icon="mdi-map-marker-outline"
              outlined
              hide-details
              clearable
              @change="applyContractFilters"></v-select>
            <v-select
              v-model="contractSortValue"
              :items="contractSortOptions"
              label="Sortierung"
              prepend-inner-icon="mdi-sort"
              outlined
              hide-details
              @change="applyContractFilters"></v-select>
            <v-btn class="contract-filter-bar__submit" color="primary" large @click="applyContractFilters"
              ><v-icon left>mdi-tune-variant</v-icon>Anwenden</v-btn
            >
          </div>
        </v-card>

        <v-card dark class="surface-card contract-list-card" outlined>
          <div class="contract-list-card__header">
            <div>
              <span class="eyebrow">{{ contractViewLabel(contractFilters.status) }}</span>
              <h3>{{ contractTotal }} {{ contractTotal === 1 ? "Vertrag" : "Verträge" }}</h3>
            </div>
            <div class="contract-list-card__meta">
              <span><v-icon small>mdi-earth</v-icon>{{ contractStats.online || 0 }} online erstellt</span>
              <span><v-icon small>mdi-database-check-outline</v-icon>V2-Dokumente</span>
              <button type="button" :class="{ active: contractFilters.status === 'deleted' }" @click="selectContractStatus('deleted')">
                <v-icon small>mdi-delete-clock-outline</v-icon>Papierkorb {{ contractStats.counts.deleted || 0 }}
              </button>
            </div>
          </div>

          <div class="contract-table-shell">
            <v-data-table
              dark
              :headers="contractHeaders"
              :items="contracts"
              :loading="loadingContracts"
              :items-per-page="25"
              hide-default-footer
              class="contracts-table"
              @click:row="openContract">
              <template v-slot:item.number="{ item }">
                <div class="contract-number-cell">
                  <strong>{{ item.number }}</strong
                  ><span>{{ sourceLabel(item.source) }} · {{ formatDate(item.createdAt) }}</span>
                </div>
              </template>
              <template v-slot:item.customer="{ item }">
                <div class="contract-customer-cell">
                  <span>{{ (item.customerSnapshot.firstName || "?").slice(0, 1) }}{{ (item.customerSnapshot.lastName || "?").slice(0, 1) }}</span>
                  <div>
                    <strong>{{ item.customerSnapshot.firstName }} {{ item.customerSnapshot.lastName }}</strong
                    ><small>{{ item.customerSnapshot.email }}</small>
                  </div>
                </div>
              </template>
              <template v-slot:item.studio="{ item }"
                ><span class="contract-studio-cell"
                  ><v-icon small>mdi-map-marker-outline</v-icon>{{ item.studio && item.studio.name ? item.studio.name : "-" }}</span
                ></template
              >
              <template v-slot:item.contractType="{ item }"
                ><strong>{{ item.contractTypeSnapshot ? item.contractTypeSnapshot.title : item.contractTypeId }}</strong></template
              >
              <template v-slot:item.startsOn="{ item }"
                ><span>{{ formatDateOnly(item.startsOn) }}</span
                ><small class="table-subline">Nutzung {{ formatDateOnly(item.useStartsOn) }}</small></template
              >
              <template v-slot:item.total="{ item }"
                ><strong class="contract-table-total">CHF {{ money(item.offerSnapshot.total) }}</strong></template
              >
              <template v-slot:item.status="{ item }">
                <div class="contract-status-cell">
                  <v-chip small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip
                  ><v-icon v-if="contractHasActiveLock(item)" small color="warning">mdi-lock-clock-outline</v-icon
                  ><span v-if="item.comments && item.comments.length"><v-icon small>mdi-message-text-outline</v-icon>{{ item.comments.length }}</span>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <div class="table-actions" @click.stop>
                  <v-btn
                    v-if="item.status === 'temporary' && !contractHasActiveLock(item)"
                    small
                    text
                    color="warning"
                    :loading="contractActionLoading === `${item._id}:resume`"
                    @click="resumeTemporaryContract(item)"
                    ><v-icon left small>mdi-play-outline</v-icon>Weiter</v-btn
                  >
                  <v-btn
                    v-if="item.status === 'created' && !contractHasActiveLock(item)"
                    small
                    text
                    color="success"
                    :loading="contractActionLoading === `${item._id}:accepted`"
                    @click="changeContractStatus(item, 'accepted')"
                    ><v-icon left small>mdi-check</v-icon>Akzeptieren</v-btn
                  >
                  <v-btn
                    v-if="item.status === 'rejected' && !contractHasActiveLock(item)"
                    small
                    text
                    color="success"
                    :loading="contractActionLoading === `${item._id}:created`"
                    @click="changeContractStatus(item, 'created')"
                    ><v-icon left small>mdi-refresh</v-icon>Einreichen</v-btn
                  >
                  <v-btn
                    v-if="(item.status === 'accepted' || item.status === 'tac_pending') && !contractHasActiveLock(item)"
                    small
                    text
                    color="primary"
                    :loading="contractActionLoading === `${item._id}:tac`"
                    @click="queueTac(item)"
                    ><v-icon left small>mdi-cloud-upload-outline</v-icon>TAC</v-btn
                  >
                  <v-btn icon :loading="contractActionLoading === `${item._id}:open`" aria-label="Vertrag öffnen" @click="openContract(item)"
                    ><v-icon>mdi-chevron-right</v-icon></v-btn
                  >
                </div>
              </template>
              <template v-slot:no-data>
                <div class="contract-empty-state">
                  <span><v-icon>mdi-inbox-check-outline</v-icon></span>
                  <h3>Hier ist alles erledigt</h3>
                  <p>Für diese Auswahl sind aktuell keine Verträge vorhanden.</p>
                </div>
              </template>
            </v-data-table>
          </div>

          <div class="contract-mobile-list">
            <button v-for="item in contracts" :key="item._id" type="button" class="contract-mobile-card" @click="openContract(item)">
              <div class="contract-mobile-card__top">
                <span>{{ item.number }}</span
                ><v-chip small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
              </div>
              <div class="contract-mobile-card__customer">
                <strong>{{ item.customerSnapshot.firstName }} {{ item.customerSnapshot.lastName }}</strong
                ><small>{{ item.customerSnapshot.email }}</small>
              </div>
              <div class="contract-mobile-card__facts">
                <span><v-icon small>mdi-map-marker-outline</v-icon>{{ item.studio && item.studio.name ? item.studio.name : "-" }}</span
                ><span><v-icon small>mdi-calendar-outline</v-icon>{{ formatDateOnly(item.startsOn) }}</span>
              </div>
              <div class="contract-mobile-card__bottom">
                <span>{{ item.contractTypeSnapshot ? item.contractTypeSnapshot.title : item.contractTypeId }}</span
                ><strong>CHF {{ money(item.offerSnapshot.total) }}</strong
                ><v-icon>mdi-chevron-right</v-icon>
              </div>
            </button>
            <div v-if="!loadingContracts && contracts.length === 0" class="contract-empty-state">
              <span><v-icon>mdi-inbox-check-outline</v-icon></span>
              <h3>Hier ist alles erledigt</h3>
              <p>Für diese Auswahl sind aktuell keine Verträge vorhanden.</p>
            </div>
          </div>

          <div v-if="contractTotal" class="contract-pagination">
            <span>Seite {{ contractPage }} von {{ contractPages }}</span>
            <div>
              <v-btn icon :disabled="contractPage <= 1 || loadingContracts" aria-label="Vorherige Seite" @click="changeContractPage(-1)"
                ><v-icon>mdi-chevron-left</v-icon></v-btn
              ><v-btn icon :disabled="contractPage >= contractPages || loadingContracts" aria-label="Nächste Seite" @click="changeContractPage(1)"
                ><v-icon>mdi-chevron-right</v-icon></v-btn
              >
            </div>
          </div>
        </v-card>
      </section>

      <section v-if="!isPublic && adminTab === 4" class="section-stack">
        <v-card dark class="surface-card pa-5" outlined>
          <div class="section-heading">
            <div>
              <h2>TAC-Mapping</h2>
              <p>Lokale V2-Angebote werden explizit TAC-Kategorien zugeordnet. Es findet hier kein Live-Schreibzugriff statt.</p>
            </div>
            <v-chip color="warning" outlined>Vorbereitungsmodus</v-chip>
          </div>
          <v-row class="mt-2">
            <v-col cols="12" md="5">
              <v-select
                v-model="tacStudioId"
                :items="studioOptions"
                item-text="label"
                item-value="value"
                label="Standort"
                outlined
                @change="loadTacMapping"></v-select>
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model="tacSearch" label="Mapping suchen" prepend-inner-icon="mdi-magnify" outlined clearable></v-text-field>
            </v-col>
            <v-col cols="12" md="2"
              ><v-btn block large color="primary" :disabled="!tacMappingRows.length" :loading="savingTac" @click="saveTacMappings"
                >Speichern</v-btn
              ></v-col
            >
          </v-row>
        </v-card>
        <v-card v-if="tacStudioId" dark class="surface-card" outlined>
          <v-data-table dark :headers="tacHeaders" :items="filteredTacRows" :items-per-page="50" class="tac-table">
            <template v-slot:item.local="{ item }"
              ><strong>{{ item.title }}</strong
              ><small class="table-subline">{{ item.subtitle }}</small></template
            >
            <template v-slot:item.tacMemberCategoryId="{ item }">
              <v-text-field
                v-model="item.tacMemberCategoryId"
                label="TAC MemberCategory ID"
                outlined
                dense
                hide-details
                :disabled="item.type === 'campaign'"></v-text-field>
            </template>
            <template v-slot:item.tacPromotionId="{ item }">
              <v-text-field v-model="item.tacPromotionId" label="TAC Promotion ID" outlined dense hide-details clearable></v-text-field>
            </template>
            <template v-slot:item.status="{ item }">
              <v-select v-model="item.status" :items="mappingStatusOptions" outlined dense hide-details></v-select>
            </template>
          </v-data-table>
        </v-card>
      </section>
    </v-container>

    <v-dialog v-model="resetImportDialog" dark persistent max-width="540">
      <v-card v-if="catalog" dark class="reset-import-card">
        <div class="reset-import-card__header">
          <div class="reset-import-card__icon"><v-icon color="error">mdi-backup-restore</v-icon></div>
          <div>
            <span class="eyebrow">Legacy-Import</span>
            <h2>Import zurücksetzen?</h2>
          </div>
        </div>
        <v-card-text class="reset-import-card__content">
          <p>
            Der Entwurf <strong>V{{ catalog.version }} - {{ catalog.name }}</strong> wird gelöscht. Danach kannst du die bestehenden Einstellungen
            komplett neu importieren.
          </p>
          <div class="reset-import-card__safe">
            <v-icon color="success">mdi-shield-check-outline</v-icon>
            <span>Legacy-Einstellungen, publizierte Kataloge, Rollouts, TAC-Mappings und V2-Verträge bleiben unverändert.</span>
          </div>
          <v-alert v-if="resetImportError" type="error" outlined class="mt-4 mb-0">{{ resetImportError }}</v-alert>
        </v-card-text>
        <v-card-actions class="reset-import-card__actions">
          <v-btn text :disabled="resettingImport" @click="resetImportDialog = false">Abbrechen</v-btn>
          <v-btn color="error" :loading="resettingImport" @click="resetLegacyImport">
            <v-icon left>mdi-delete-outline</v-icon>
            Entwurf löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="contractDialog" dark persistent max-width="1120" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="activeContract" dark class="contract-dialog-card contract-workspace">
        <header class="contract-workspace__header">
          <div class="contract-workspace__title">
            <span><v-icon>mdi-file-document-check-outline</v-icon></span>
            <div>
              <small>V2-Vertrag</small><strong>{{ activeContract.number }}</strong>
              <p>{{ activeContract.customerSnapshot.firstName }} {{ activeContract.customerSnapshot.lastName }}</p>
            </div>
          </div>
          <div class="contract-workspace__header-meta">
            <v-chip small outlined>{{ sourceLabel(activeContract.source) }}</v-chip>
            <v-chip small :color="statusColor(activeContract.status)" dark>{{ statusLabel(activeContract.status) }}</v-chip>
            <v-btn icon aria-label="Vertrag drucken" @click="printV2Contract(activeContract)"><v-icon>mdi-printer-outline</v-icon></v-btn>
            <v-btn icon aria-label="Dialog schliessen" @click="closeContractDialog"><v-icon>mdi-close</v-icon></v-btn>
          </div>
        </header>

        <div v-if="activeContractLockedByOther" class="contract-lock-banner contract-lock-banner--warning">
          <v-icon color="warning">mdi-lock-clock-outline</v-icon>
          <div>
            <strong>Wird gerade bearbeitet</strong
            ><span>{{ personName(activeContract.editingBy) }} hat die Bearbeitung übernommen. Die Sperre läuft nach 30 Minuten ab.</span>
          </div>
        </div>
        <div v-else-if="activeContractEditingByCurrent" class="contract-lock-banner contract-lock-banner--active">
          <v-icon color="success">mdi-pencil-lock-outline</v-icon>
          <div>
            <strong>Du bearbeitest diesen Vertrag</strong
            ><span>Andere Mitarbeitende können die Stammdaten bis zum Beenden der Bearbeitung nicht ändern.</span>
          </div>
          <v-btn text color="success" @click="toggleContractEditLock">Bearbeitung beenden</v-btn>
        </div>

        <nav class="contract-workspace__nav" aria-label="Vertragsdetails">
          <button type="button" :class="{ active: contractDialogSection === 'overview' }" @click="contractDialogSection = 'overview'">
            <v-icon small>mdi-view-dashboard-outline</v-icon>Übersicht
          </button>
          <button type="button" :class="{ active: contractDialogSection === 'history' }" @click="contractDialogSection = 'history'">
            <v-icon small>mdi-message-text-clock-outline</v-icon>Kommentare & Verlauf<span>{{ (activeContract.comments || []).length }}</span>
          </button>
          <button
            v-if="['temporary', 'created', 'accepted', 'rejected'].includes(activeContract.status)"
            type="button"
            :class="{ active: contractDialogSection === 'edit' }"
            @click="contractDialogSection = 'edit'">
            <v-icon small>mdi-pencil-outline</v-icon>Bearbeiten
          </button>
        </nav>

        <v-card-text class="contract-workspace__body">
          <v-alert v-if="error" type="error" outlined dismissible @input="error = ''">{{ error }}</v-alert>
          <section v-if="contractDialogSection === 'overview'" class="contract-overview">
            <div class="contract-overview__lead">
              <div class="contract-customer-hero">
                <span
                  >{{ (activeContract.customerSnapshot.firstName || "?").slice(0, 1)
                  }}{{ (activeContract.customerSnapshot.lastName || "?").slice(0, 1) }}</span
                >
                <div>
                  <small>Vertragspartner</small>
                  <h3>{{ activeContract.customerSnapshot.firstName }} {{ activeContract.customerSnapshot.lastName }}</h3>
                  <p>
                    {{ activeContract.customerSnapshot.email
                    }}<template v-if="activeContract.customerSnapshot.phone"> · {{ activeContract.customerSnapshot.phone }}</template>
                  </p>
                  <p>
                    {{ activeContract.customerSnapshot.street }} {{ activeContract.customerSnapshot.streetExtra }},
                    {{ activeContract.customerSnapshot.zip }} {{ activeContract.customerSnapshot.city }}
                  </p>
                </div>
              </div>
              <div class="contract-total-hero">
                <small>Vertragstotal</small><strong>CHF {{ money(activeContract.offerSnapshot.total) }}</strong
                ><span>{{ activeContract.contractTypeSnapshot ? activeContract.contractTypeSnapshot.title : activeContract.contractTypeId }}</span>
              </div>
            </div>

            <div class="contract-fact-grid">
              <div>
                <span><v-icon small>mdi-map-marker-outline</v-icon>Standort</span
                ><strong>{{ activeContract.studio && activeContract.studio.name ? activeContract.studio.name : "-" }}</strong>
              </div>
              <div>
                <span><v-icon small>mdi-calendar-start-outline</v-icon>Vertragsbeginn</span
                ><strong>{{ formatDateOnly(activeContract.startsOn) }}</strong>
              </div>
              <div>
                <span><v-icon small>mdi-run-fast</v-icon>Nutzungsbeginn</span><strong>{{ formatDateOnly(activeContract.useStartsOn) }}</strong>
              </div>
              <div>
                <span><v-icon small>mdi-credit-card-outline</v-icon>Zahlungsart</span><strong>{{ contractPaymentLabel(activeContract) }}</strong
                ><small v-if="activeContract.payment && activeContract.payment.debitDate"
                  >Belastung {{ formatDateOnly(activeContract.payment.debitDate) }}</small
                >
              </div>
              <div>
                <span><v-icon small>mdi-account-edit-outline</v-icon>Erstellt durch</span
                ><strong>{{ personName(activeContract.createdBy, sourceLabel(activeContract.source)) }}</strong
                ><small>{{ formatDate(activeContract.createdAt) }}</small>
              </div>
              <div>
                <span><v-icon small>mdi-shape-outline</v-icon>Kategorie</span
                ><strong>{{
                  activeContract.responses && activeContract.responses.category ? activeContract.responses.category.title : "Keine Kategorie"
                }}</strong
                ><small v-if="activeContract.responses && activeContract.responses.category && activeContract.responses.category.notes">{{
                  activeContract.responses.category.notes
                }}</small>
              </div>
              <div v-if="activeContract.responses && activeContract.responses.company">
                <span><v-icon small>mdi-office-building-outline</v-icon>Vertragsfirma</span
                ><strong>{{ activeContract.responses.company.name }}</strong>
              </div>
              <div v-if="activeContract.physiotherapistSnapshot">
                <span><v-icon small>mdi-medical-bag</v-icon>Physiotherapie</span
                ><strong>{{ activeContract.physiotherapistSnapshot.firstName }} {{ activeContract.physiotherapistSnapshot.lastName }}</strong
                ><small>{{ activeContract.physiotherapistSnapshot.company }}</small>
              </div>
            </div>

            <div class="contract-detail-section">
              <div class="contract-detail-section__heading">
                <div>
                  <span class="eyebrow">Preisübersicht</span>
                  <h3>Gewählte Leistungen</h3>
                </div>
                <v-chip small outlined>{{ activeContract.offerSnapshot.currency || "CHF" }}</v-chip>
              </div>
              <div class="contract-offer-list">
                <div
                  v-for="line in activeContract.offerSnapshot.lines"
                  :key="line.type + line.refId + line.title"
                  :class="{ discount: line.type === 'discount' }">
                  <span
                    ><strong>{{ line.title }}</strong
                    ><small v-if="line.quantity > 1">Menge {{ line.quantity }}</small></span
                  ><b>{{ line.total < 0 ? "-" : "" }}CHF {{ money(Math.abs(line.total)) }}</b>
                </div>
                <div class="contract-offer-list__total">
                  <span>Total</span><strong>CHF {{ money(activeContract.offerSnapshot.total) }}</strong>
                </div>
              </div>
            </div>

            <details v-if="activeContract.legalSnapshot" class="contract-legal-details">
              <summary>
                <span><v-icon>mdi-shield-check-outline</v-icon></span>
                <div>
                  <strong>{{ activeContract.legalSnapshot.title }}</strong
                  ><small>Beim Abschluss gespeicherte Vertragsbedingungen</small>
                </div>
                <v-icon>mdi-chevron-down</v-icon>
              </summary>
              <div>
                <p v-if="activeContract.legalSnapshot.preamble">{{ activeContract.legalSnapshot.preamble }}</p>
                <ol>
                  <li v-for="condition in activeContract.legalSnapshot.conditions" :key="condition.id">{{ condition.text }}</li>
                </ol>
                <p v-if="activeContract.legalSnapshot.confirmation">
                  <strong>{{ activeContract.legalSnapshot.confirmation }}</strong>
                </p>
              </div>
            </details>

            <v-alert v-if="activeContract.tacSync && activeContract.tacSync.lastError" type="warning" outlined>{{
              activeContract.tacSync.lastError
            }}</v-alert>
          </section>

          <section v-else-if="contractDialogSection === 'history'" class="contract-history">
            <div class="contract-comment-composer">
              <div class="contract-detail-section__heading">
                <div>
                  <span class="eyebrow">Interne Notiz</span>
                  <h3>Kommentar erfassen</h3>
                  <p>Kommentare und Ablehnungsgründe bleiben dauerhaft beim V2-Vertrag.</p>
                </div>
                <span><v-icon>mdi-message-plus-outline</v-icon></span>
              </div>
              <v-select
                v-model="contractCommentReason"
                :items="contractCommentReasons"
                label="Grund (optional, für Ablehnung erforderlich)"
                outlined
                clearable></v-select>
              <v-textarea v-model="contractComment" label="Kommentar" outlined rows="3" counter="2000"></v-textarea>
              <div class="contract-comment-composer__actions">
                <v-btn outlined :disabled="!contractComment.trim() && !contractCommentReason" @click="addContractComment()"
                  ><v-icon left>mdi-content-save-outline</v-icon>Kommentar speichern</v-btn
                >
                <v-btn
                  v-if="['created', 'accepted', 'tac_pending'].includes(activeContract.status)"
                  color="error"
                  :disabled="!contractCommentReason || activeContractLockedByOther"
                  :loading="contractActionLoading === `${activeContract._id}:rejected`"
                  @click="rejectActiveContract"
                  ><v-icon left>mdi-close-circle-outline</v-icon>Ablehnen und speichern</v-btn
                >
              </div>
            </div>

            <div class="contract-history-grid">
              <div class="contract-history-column">
                <div class="contract-history-column__heading">
                  <span><v-icon>mdi-message-text-outline</v-icon></span>
                  <div>
                    <strong>Kommentare</strong><small>{{ (activeContract.comments || []).length }} Einträge</small>
                  </div>
                </div>
                <div v-if="activeContract.comments && activeContract.comments.length" class="contract-timeline">
                  <article v-for="(comment, index) in activeContract.comments.slice().reverse()" :key="comment.createdAt + index">
                    <span></span>
                    <div>
                      <div>
                        <strong>{{ personName(comment.createdBy) }}</strong
                        ><time>{{ formatDate(comment.createdAt) }}</time>
                      </div>
                      <v-chip v-if="comment.reason" small outlined color="warning">{{ comment.reason }}</v-chip>
                      <p>{{ comment.text }}</p>
                      <small>Status: {{ statusLabel(comment.status) }}</small>
                    </div>
                  </article>
                </div>
                <div v-else class="contract-history-empty">
                  <v-icon>mdi-message-outline</v-icon>
                  <p>Noch keine Kommentare vorhanden.</p>
                </div>
              </div>
              <div class="contract-history-column">
                <div class="contract-history-column__heading">
                  <span><v-icon>mdi-history</v-icon></span>
                  <div><strong>Aktivitätsverlauf</strong><small>Unveränderbare Aktionen</small></div>
                </div>
                <div class="contract-timeline contract-timeline--audit">
                  <article v-for="(entry, index) in (activeContract.auditLog || []).slice().reverse()" :key="entry.createdAt + index">
                    <span></span>
                    <div>
                      <div>
                        <strong>{{ auditActionLabel(entry.action) }}</strong
                        ><time>{{ formatDate(entry.createdAt) }}</time>
                      </div>
                      <small>{{ personName(entry.createdBy) }}</small>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section v-else class="contract-edit-panel">
            <div class="contract-detail-section__heading">
              <div>
                <span class="eyebrow">Kontrollierte Bearbeitung</span>
                <h3>Vertragsdaten anpassen</h3>
                <p>Abo, Preis und Bedingungen bleiben als unveränderbarer Abschluss-Snapshot erhalten.</p>
              </div>
              <span><v-icon>mdi-pencil-ruler-outline</v-icon></span>
            </div>
            <v-alert v-if="activeContractLockedByOther" type="warning" outlined
              >Die Bearbeitung ist momentan durch {{ personName(activeContract.editingBy) }} gesperrt.</v-alert
            >
            <div v-else-if="!activeContractEditingByCurrent" class="contract-edit-gate">
              <span><v-icon>mdi-lock-open-variant-outline</v-icon></span>
              <div>
                <strong>Bearbeitung übernehmen</strong>
                <p>Damit nicht zwei Personen gleichzeitig Daten ändern, wird der Vertrag für 30 Minuten für dich reserviert.</p>
              </div>
              <v-btn color="primary" :loading="contractActionLoading === `${activeContract._id}:lock`" @click="toggleContractEditLock"
                >Übernehmen</v-btn
              >
            </div>
            <template v-else>
              <v-row class="mt-1">
                <v-col cols="12" sm="6"
                  ><v-text-field v-model="contractEdit.startsOn" type="date" label="Vertragsbeginn" outlined></v-text-field
                ></v-col>
                <v-col cols="12" sm="6"
                  ><v-text-field v-model="contractEdit.useStartsOn" type="date" label="Nutzungsbeginn" outlined></v-text-field
                ></v-col>
                <v-col cols="12" sm="6"
                  ><v-select
                    v-model="contractEdit.paymentMethodId"
                    :items="activeContract.managementOptions.paymentMethods"
                    item-text="title"
                    item-value="id"
                    label="Zahlungsart"
                    outlined
                    @change="managementPaymentMethodChanged"></v-select
                ></v-col>
                <v-col v-if="activeContractPaymentMethod && activeContractPaymentMethod.selectDate" cols="12" sm="6"
                  ><v-text-field v-model="contractEdit.debitDate" type="date" label="Belastungsdatum" outlined></v-text-field
                ></v-col>
                <v-col cols="12"
                  ><v-text-field v-model="contractEdit.reference" label="Zahlungsreferenz / interne Information" outlined counter="500"></v-text-field
                ></v-col>
                <v-col v-if="activeContract.contractTypeSnapshot && activeContract.contractTypeSnapshot.requiresPhysiotherapist" cols="12"
                  ><v-select
                    v-model="contractEdit.physiotherapistId"
                    :items="activeContract.managementOptions.physiotherapists"
                    :item-text="physiotherapistLabel"
                    item-value="_id"
                    label="Betreuende Physiotherapie"
                    outlined></v-select
                ></v-col>
              </v-row>
              <div class="contract-edit-panel__actions">
                <v-btn text @click="toggleContractEditLock">Bearbeitung beenden</v-btn
                ><v-btn color="primary" :loading="contractDetailsSaving" @click="saveContractDetails"
                  ><v-icon left>mdi-content-save-check-outline</v-icon>Änderungen speichern</v-btn
                >
              </div>
            </template>
          </section>
        </v-card-text>

        <footer class="contract-workspace__footer">
          <div>
            <v-btn
              v-if="activeContract.status !== 'tac_synced' && activeContract.status !== 'deleted'"
              text
              color="error"
              :disabled="activeContractLockedByOther"
              @click="requestDeleteContract"
              ><v-icon left>mdi-delete-outline</v-icon>Löschen</v-btn
            ><v-btn text @click="printV2Contract(activeContract)"><v-icon left>mdi-printer-outline</v-icon>PDF / Drucken</v-btn>
          </div>
          <div>
            <v-btn
              v-if="activeContract.status === 'temporary'"
              color="warning"
              :disabled="activeContractLockedByOther"
              :loading="contractActionLoading === `${activeContract._id}:resume`"
              @click="resumeTemporaryContract(activeContract)"
              ><v-icon left>mdi-play-outline</v-icon>Vertrag weiterführen</v-btn
            >
            <v-btn
              v-if="activeContract.status === 'created'"
              outlined
              color="error"
              :disabled="activeContractLockedByOther"
              @click="contractDialogSection = 'history'"
              ><v-icon left>mdi-close-circle-outline</v-icon>Ablehnen</v-btn
            >
            <v-btn
              v-if="activeContract.status === 'created'"
              color="success"
              :disabled="activeContractLockedByOther"
              :loading="contractActionLoading === `${activeContract._id}:accepted`"
              @click="changeContractStatus(activeContract, 'accepted')"
              ><v-icon left>mdi-check-circle-outline</v-icon>Akzeptieren</v-btn
            >
            <v-btn
              v-if="activeContract.status === 'rejected'"
              color="success"
              :disabled="activeContractLockedByOther"
              :loading="contractActionLoading === `${activeContract._id}:created`"
              @click="changeContractStatus(activeContract, 'created')"
              ><v-icon left>mdi-refresh</v-icon>Erneut einreichen</v-btn
            >
            <v-btn
              v-if="activeContract.status === 'accepted' || activeContract.status === 'tac_pending'"
              color="primary"
              :disabled="activeContractLockedByOther"
              :loading="contractActionLoading === `${activeContract._id}:tac`"
              @click="queueTac(activeContract)"
              ><v-icon left>mdi-cloud-upload-outline</v-icon>TAC vorbereiten</v-btn
            >
          </div>
        </footer>
      </v-card>
    </v-dialog>

    <v-dialog v-model="contractDeleteDialog" dark persistent max-width="480">
      <v-card v-if="activeContract" dark class="contract-delete-card">
        <div class="contract-delete-card__icon"><v-icon color="error">mdi-delete-alert-outline</v-icon></div>
        <h2>Vertrag löschen?</h2>
        <p>{{ activeContract.number }} wird aus der aktiven Verwaltung entfernt. Der Audit-Verlauf bleibt für die Nachvollziehbarkeit erhalten.</p>
        <div>
          <v-btn text :disabled="contractActionLoading === `${activeContract._id}:deleted`" @click="contractDeleteDialog = false">Abbrechen</v-btn
          ><v-btn color="error" :loading="contractActionLoading === `${activeContract._id}:deleted`" @click="deleteActiveContract"
            >Vertrag löschen</v-btn
          >
        </div>
      </v-card>
    </v-dialog>
    <register
      v-if="isPublic"
      :active="showRegisterDialog"
      :auto-login="false"
      :requirePassword="true"
      @register="newAccountRegistered"
      @close="showRegisterDialog = false"></register>
    <login v-if="isPublic" :active="showLoginDialog" :auto-login="false" @login="accountLogin" @close="showLoginDialog = false"></login>
  </div>
</template>

<script>
import api from "@/api"
import UserSearch from "@/components/userSearch.vue"
import SignaturePad from "@/components/signaturePad/signaturePad.vue"
import Register from "@/views/launchScreen/register.vue"
import Login from "@/views/launchScreen/login.vue"

export default {
  name: "ContractV2",
  components: { UserSearch, SignaturePad, Register, Login },
  data() {
    return {
      loading: false,
      error: "",
      adminTab: 0,
      catalogs: [],
      catalog: null,
      selectedCatalogId: null,
      catalogSection: "types",
      catalogSearch: "",
      catalogDetailsOpen: false,
      validationIssuesOpen: false,
      importIssuesOpen: false,
      masterDataView: "companies",
      selectedCatalogCompanyId: null,
      selectedCatalogCategoryId: null,
      validationIssues: [],
      importing: false,
      resettingImport: false,
      resetImportDialog: false,
      resetImportError: "",
      saving: false,
      publishing: false,
      rolloutStudios: [],
      rolloutCatalogs: [],
      savingRolloutId: null,
      newOverride: this.emptyOverride(),
      creatorData: null,
      creatorStudioId: null,
      creatorCatalogId: null,
      creatorStep: 1,
      creatorContractTypeId: null,
      creatorAge: null,
      creatorGoals: [],
      creatorCategoryId: null,
      creatorCategoryNotes: "",
      creatorCompanyId: null,
      creatorIsRenewal: false,
      creatorCampaignCode: "",
      creatorStartDate: new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(
        new Date(),
      ),
      creatorUseStartDate: new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(
        new Date(),
      ),
      creatorPlanId: null,
      creatorVariantId: null,
      creatorTermId: null,
      creatorUnits: 1,
      creatorAddonQuantities: {},
      creatorPaymentMethodId: null,
      creatorDebitDate: null,
      creatorPhysiotherapistId: null,
      creatorUser: null,
      creatorTermsAccepted: false,
      creatorMarketingAccepted: false,
      creatorSignature: null,
      creatorLegalSignature: null,
      creatorLegalRepresentative: { firstName: "", lastName: "", email: "", phone: "", relationship: "" },
      quote: null,
      quoting: false,
      quoteTimer: null,
      submitting: false,
      savingTemporary: false,
      editingTemporaryContractId: null,
      creatorValidationAttempted: false,
      submitError: "",
      createdContract: null,
      publicAuthenticated: Boolean(localStorage.getItem("token")),
      publicBirthdayVerified: false,
      showRegisterDialog: false,
      showLoginDialog: false,
      contracts: [],
      loadingContracts: false,
      contractFilters: { search: "", status: "created", studioId: "" },
      contractPage: 1,
      contractPages: 1,
      contractTotal: 0,
      contractSort: "createdAt",
      contractDirection: "desc",
      contractStats: { counts: {}, online: 0, active: 0 },
      contractActionLoading: null,
      contractDetailsSaving: false,
      contractDeleteDialog: false,
      contractDialogSection: "overview",
      contractEdit: { startsOn: "", useStartsOn: "", paymentMethodId: null, debitDate: null, reference: "", physiotherapistId: null },
      contractCommentReason: null,
      currentUserId: null,
      activeContract: null,
      contractDialog: false,
      contractComment: "",
      tacStudioId: null,
      tacCatalog: null,
      tacMappingRows: [],
      tacSearch: "",
      savingTac: false,
      contractTypeKinds: [
        { text: "Feste Laufzeit", value: "fixed" },
        { text: "Monatlich / flexibel", value: "rolling" },
        { text: "Medizinisch betreut", value: "medical" },
      ],
      paymentModes: [
        { text: "Einmalzahlung", value: "upfront" },
        { text: "Ratenzahlung", value: "installments" },
      ],
      termUnits: [
        { text: "Feste Laufzeit", value: "fixed" },
        { text: "Pro Tag", value: "day" },
        { text: "Pro Monat", value: "month" },
        { text: "Pro Jahr", value: "year" },
      ],
      channelOptions: [
        { text: "Intern", value: "internal" },
        { text: "Online-Vertrag", value: "public" },
      ],
      catalogSectionOptions: [
        { value: "types", label: "Vertragstypen", icon: "mdi-shape-outline" },
        { value: "plans", label: "Pläne", icon: "mdi-card-account-details-star-outline" },
        { value: "addons", label: "Zusatzpakete", icon: "mdi-puzzle-plus-outline" },
        { value: "payments", label: "Zahlungsarten", icon: "mdi-credit-card-outline" },
        { value: "masterdata", label: "Stammdaten", icon: "mdi-office-building-cog-outline" },
        { value: "overrides", label: "Ausnahmen", icon: "mdi-map-marker-alert-outline" },
        { value: "campaigns", label: "Aktionen", icon: "mdi-ticket-percent-outline" },
      ],
      categoryChannelOptions: [
        { text: "Intern", value: "internal" },
        { text: "Öffentlich", value: "public" },
      ],
      renewalOptions: [
        { text: "Alle Abschlüsse", value: null },
        { text: "Nur Wiederabschlüsse", value: true },
        { text: "Keine Wiederabschlüsse", value: false },
      ],
      flowOptions: [
        { text: "Bisherige Version", value: "legacy" },
        { text: "V2", value: "v2" },
      ],
      tacFlowOptions: [
        { text: "Bisheriger Prozess", value: "legacy" },
        { text: "V2 mit Kontrolle", value: "v2_assisted" },
        { text: "V2 automatisch (erst nach TAC-Testfreigabe)", value: "v2_auto", disabled: true },
      ],
      overrideTargets: [
        { text: "Plan / Laufzeit", value: "planTerm" },
        { text: "Zusatzpaket / Laufzeit", value: "addonTerm" },
      ],
      goalOptions: [
        { label: "Kraft", value: "strength", icon: "mdi-arm-flex" },
        { label: "Schnell trainieren", value: "fast", icon: "mdi-lightning-bolt" },
        { label: "Wellness", value: "wellness", icon: "mdi-spa-outline" },
        { label: "Kurse", value: "courses", icon: "mdi-account-group-outline" },
        { label: "Fortschritt messen", value: "progress", icon: "mdi-chart-line" },
        { label: "Coaching", value: "coaching", icon: "mdi-account-heart-outline" },
      ],
      creatorStepLabels: ["Start", "Bedarf", "Abo", "Laufzeit", "Extras", "Details", "Abschluss"],
      creatorStepIcons: [
        "mdi-account-outline",
        "mdi-tune-variant",
        "mdi-card-account-details-star-outline",
        "mdi-calendar-range",
        "mdi-puzzle-plus-outline",
        "mdi-calendar-check-outline",
        "mdi-file-sign",
      ],
      contractStatusOptions: [
        { text: "Temporär", value: "temporary" },
        { text: "Erstellt", value: "created" },
        { text: "Akzeptiert", value: "accepted" },
        { text: "Abgelehnt", value: "rejected" },
        { text: "TAC ausstehend", value: "tac_pending" },
        { text: "TAC synchronisiert", value: "tac_synced" },
        { text: "Gelöscht", value: "deleted" },
      ],
      contractStatusViews: [
        { value: "created", label: "Offen", icon: "mdi-inbox-outline", color: "warning" },
        { value: "temporary", label: "Temporär", icon: "mdi-file-clock-outline", color: "amber" },
        { value: "rejected", label: "Abgelehnt", icon: "mdi-file-remove-outline", color: "error" },
        { value: "accepted", label: "Akzeptiert", icon: "mdi-check-decagram-outline", color: "success" },
        { value: "tac_pending", label: "TAC ausstehend", icon: "mdi-cloud-upload-outline", color: "info" },
        { value: "tac_synced", label: "TAC synchronisiert", icon: "mdi-cloud-check-outline", color: "primary" },
      ],
      contractCommentReasons: [
        "Firmenbestätigung fehlt",
        "Unterschrift gesetzlicher Vertreter fehlt",
        "Geburtsdatum falsch",
        "Falsche Laufzeit",
        "Aktivierungs-Tarif falsch gewählt",
        "Betreibung in der Vergangenheit",
        "Unterschriften unklar",
        "Weitere",
      ],
      contractSortOptions: [
        { text: "Neueste zuerst", value: "createdAt:desc" },
        { text: "Älteste zuerst", value: "createdAt:asc" },
        { text: "Frühester Vertragsbeginn", value: "startsOn:asc" },
        { text: "Spätester Vertragsbeginn", value: "startsOn:desc" },
      ],
      contractHeaders: [
        { text: "Nummer", value: "number" },
        { text: "Kunde", value: "customer" },
        { text: "Standort", value: "studio" },
        { text: "Typ", value: "contractType" },
        { text: "Beginn", value: "startsOn" },
        { text: "Total", value: "total" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      tacHeaders: [
        { text: "Lokales Angebot", value: "local" },
        { text: "TAC Kategorie", value: "tacMemberCategoryId", sortable: false },
        { text: "TAC Aktion", value: "tacPromotionId", sortable: false },
        { text: "Status", value: "status" },
      ],
      mappingStatusOptions: [
        { text: "Aktiv", value: "active" },
        { text: "Prüfen", value: "review" },
        { text: "Ungültig", value: "invalid" },
      ],
    }
  },
  computed: {
    isPublic() {
      return Boolean(this.$route.meta && this.$route.meta.contractV2Public)
    },
    catalogEditable() {
      return this.catalog && this.catalog.status === "draft"
    },
    importReviewIssues() {
      const issues =
        this.catalog && this.catalog.importReport && Array.isArray(this.catalog.importReport.issues) ? this.catalog.importReport.issues : []
      return issues.filter((issue) => issue.level !== "error")
    },
    catalogOptions() {
      return this.catalogs.map((catalog) => ({ value: catalog._id, label: `V${catalog.version} - ${catalog.name} (${catalog.status})` }))
    },
    allCatalogOptions() {
      return this.catalogOptions
    },
    creatorCatalogOptions() {
      return this.catalogs
        .filter((catalog) => catalog.status !== "archived")
        .sort(
          (left, right) => Number(right.status === "published") - Number(left.status === "published") || Number(right.version) - Number(left.version),
        )
        .map((catalog) => ({
          value: catalog._id,
          label: `V${catalog.version} - ${catalog.name} (${catalog.status === "published" ? "publiziert" : "Vorschau"})`,
        }))
    },
    hasPublishedCreatorCatalog() {
      return this.catalogs.some((catalog) => catalog.status === "published")
    },
    publishedCatalogOptions() {
      return this.rolloutCatalogs.map((catalog) => ({ value: catalog._id, label: `V${catalog.version} - ${catalog.name}` }))
    },
    studioOptions() {
      return this.rolloutStudios.map((studio) => ({ value: studio._id, label: studio.name }))
    },
    contractTypeOptions() {
      return this.catalog ? this.catalog.contractTypes.map((contractType) => ({ value: contractType.id, label: contractType.title })) : []
    },
    planOptions() {
      return this.catalog ? this.catalog.plans.map((plan) => ({ value: plan.id, label: plan.title })) : []
    },
    addonOptions() {
      return this.catalog ? this.catalog.addons.map((addon) => ({ value: addon.id, label: addon.title })) : []
    },
    variantOptions() {
      if (!this.catalog) return []
      return this.catalog.plans.reduce(
        (options, plan) => options.concat(plan.variants.map((variant) => ({ value: variant.id, label: `${plan.title} / ${variant.title}` }))),
        [],
      )
    },
    categoryOptions() {
      return this.catalog ? this.catalog.categories.map((category) => ({ value: category.id, label: category.title })) : []
    },
    paymentMethodOptions() {
      return this.catalog ? this.catalog.paymentMethods.map((method) => ({ value: method.id, label: method.title })) : []
    },
    filteredCatalogCompanies() {
      return this.catalog ? this.catalog.companies.filter((company) => this.catalogMatches(company)) : []
    },
    filteredCatalogCategories() {
      return this.catalog ? this.catalog.categories.filter((category) => this.catalogMatches(category)) : []
    },
    selectedCatalogCompany() {
      if (!this.filteredCatalogCompanies.length) return null
      return this.filteredCatalogCompanies.find((company) => company.id === this.selectedCatalogCompanyId) || this.filteredCatalogCompanies[0]
    },
    selectedCatalogCategory() {
      if (!this.filteredCatalogCategories.length) return null
      return this.filteredCatalogCategories.find((category) => category.id === this.selectedCatalogCategoryId) || this.filteredCatalogCategories[0]
    },
    creatorStudioName() {
      if (this.creatorData && this.creatorData.studio && this.creatorData.studio.name) return this.creatorData.studio.name
      const studio = this.studioOptions.find((option) => String(option.value) === String(this.creatorStudioId || ""))
      return studio ? studio.label : "Standort"
    },
    creatorCatalogVersionLabel() {
      if (this.creatorData && this.creatorData.catalog && this.creatorData.catalog.version) return `Katalog V${this.creatorData.catalog.version}`
      const catalog = this.catalogOptions.find((option) => String(option.value) === String(this.creatorCatalogId || ""))
      return catalog ? catalog.label : "Katalogversion"
    },
    creatorAgeLocked() {
      if (this.isPublic) return this.publicBirthdayVerified
      return Boolean(this.creatorUser && this.userAge(this.creatorUser.geburtstag) !== null)
    },
    termOptions() {
      if (!this.catalog) return []
      const terms = new Map()
      this.catalog.plans.forEach((plan) =>
        plan.variants.forEach((variant) => variant.terms.forEach((term) => terms.set(term.id, { label: term.label, unit: term.unit || "fixed" }))),
      )
      return [...terms.entries()].map(([value, term]) => ({ value, label: term.label, unit: term.unit }))
    },
    overrideVariantOptions() {
      if (!this.catalog || !this.newOverride.planId) return []
      const plan = this.catalog.plans.find((entry) => entry.id === this.newOverride.planId)
      return plan ? plan.variants.map((variant) => ({ value: variant.id, label: variant.title })) : []
    },
    overrideTermOptions() {
      if (this.newOverride.target === "planTerm" && this.newOverride.variantId && this.catalog) {
        for (const plan of this.catalog.plans) {
          const variant = plan.variants.find((entry) => entry.id === this.newOverride.variantId)
          if (variant) return variant.terms.map((term) => ({ value: term.id, label: term.label }))
        }
      }
      return this.termOptions
    },
    selectedPlan() {
      return this.creatorData ? this.creatorData.plans.find((plan) => plan.id === this.creatorPlanId) || null : null
    },
    selectedContractType() {
      return this.creatorData && Array.isArray(this.creatorData.contractTypes)
        ? this.creatorData.contractTypes.find((contractType) => contractType.id === this.creatorContractTypeId) || null
        : null
    },
    creatorCategories() {
      if (!this.creatorData || !this.creatorContractTypeId) return []
      return (this.creatorData.categories || []).filter((category) => this.supportsCreatorContractType(category))
    },
    creatorPaymentMethods() {
      if (!this.creatorData || !this.creatorContractTypeId) return []
      return (this.creatorData.paymentMethods || []).filter((method) => this.supportsCreatorContractType(method))
    },
    creatorPhysiotherapists() {
      return this.creatorData && Array.isArray(this.creatorData.physiotherapists) ? this.creatorData.physiotherapists : []
    },
    selectedCategory() {
      return this.creatorData && this.creatorCategories
        ? this.creatorCategories.find((category) => category.id === this.creatorCategoryId) || null
        : null
    },
    creatorVariants() {
      return this.selectedPlan ? this.selectedPlan.variants : []
    },
    selectedVariant() {
      return this.creatorVariants.find((variant) => variant.id === this.creatorVariantId) || null
    },
    creatorTerms() {
      return this.selectedVariant ? this.selectedVariant.terms : []
    },
    selectedTerm() {
      return this.creatorTerms.find((term) => term.id === this.creatorTermId) || null
    },
    selectedPaymentMethod() {
      return this.creatorPaymentMethods.find((method) => method.id === this.creatorPaymentMethodId) || null
    },
    selectedLegalProfile() {
      if (!this.selectedContractType) return null
      const profiles = (this.selectedContractType.legalProfiles || []).filter((profile) => profile.active !== false)
      const studioId = String(this.creatorData?.studio?._id || this.creatorStudioId || "")
      const matches = profiles
        .filter((profile) => {
          const studioIds = (profile.studioIds || []).map(String)
          const planIds = (profile.planIds || []).map(String)
          return (!studioIds.length || studioIds.includes(studioId)) && (!planIds.length || planIds.includes(String(this.creatorPlanId || "")))
        })
        .sort((left, right) => {
          const score = (profile) => {
            const planIds = profile.planIds || []
            const studioIds = profile.studioIds || []
            return (
              (planIds.length ? 100 - Math.min(planIds.length, 99) : 0) +
              (studioIds.length ? 50 - Math.min(studioIds.length, 49) : 0) +
              Number(Boolean(profile.isDefault))
            )
          }
          return score(right) - score(left)
        })
      return matches[0] || profiles.find((profile) => profile.isDefault) || profiles[0] || null
    },
    legalContent() {
      const profile = this.selectedLegalProfile
      if (!profile) return null
      const company =
        (this.creatorData?.companies || []).find((entry) => entry.id === this.creatorCompanyId) || this.creatorData?.companies?.[0] || null
      const replace = (value) =>
        String(value || "")
          .replace(/\{\{\s*company\.name\s*\}\}/gi, company?.name || "der Vertragsfirma")
          .replace(/\{\{\s*studio\.name\s*\}\}/gi, this.creatorData?.studio?.name || "dem Standort")
      return {
        title: profile.title,
        preamble: replace(profile.preamble),
        conditions: (profile.conditions || []).map((condition) => ({ ...condition, text: replace(condition.text) })),
        confirmation: replace(profile.confirmation),
      }
    },
    eligiblePlans() {
      if (!this.creatorData || !this.creatorContractTypeId) return []
      return this.creatorData.plans.filter((plan) => this.supportsCreatorContractType(plan) && this.ageEligible(plan.eligibility))
    },
    availableAddons() {
      if (!this.creatorData || !this.creatorVariantId || !this.creatorTermId) return []
      return this.creatorData.addons.filter(
        (addon) => this.supportsCreatorContractType(addon) && this.ageEligible(addon.eligibility) && this.addonOffer(addon),
      )
    },
    selectedAddonArray() {
      return Object.keys(this.creatorAddonQuantities)
        .filter((addonId) => Number(this.creatorAddonQuantities[addonId]) > 0)
        .map((addonId) => ({ addonId, quantity: Number(this.creatorAddonQuantities[addonId]) }))
    },
    creatorProgressPercent() {
      return Math.round(((this.creatorStep - 1) / Math.max(1, this.creatorStepLabels.length - 1)) * 100)
    },
    creatorStepIssues() {
      const issues = []
      if (this.creatorStep === 1) {
        if (!this.isPublic && !this.creatorUser?._id) issues.push("Wähle zuerst den Kunden für diesen Vertrag.")
        if (this.creatorAge === null || this.creatorAge === "" || Number(this.creatorAge) < 0 || Number(this.creatorAge) > 110) {
          issues.push("Hinterlege ein gültiges Alter zwischen 0 und 110 Jahren.")
        }
      }
      if (this.creatorStep === 2) {
        if (!this.creatorContractTypeId) issues.push("Wähle einen Vertragstyp.")
        if (this.creatorCategories.length && !this.creatorCategoryId) issues.push("Wähle die passende Mitgliedschaftskategorie.")
        if (this.selectedCategory?.config?.notes && !String(this.creatorCategoryNotes || "").trim()) {
          issues.push("Ergänze die erforderliche Zusatzangabe zur Kategorie.")
        }
      }
      if (this.creatorStep === 3 && (!this.creatorPlanId || !this.eligiblePlans.some((plan) => plan.id === this.creatorPlanId))) {
        issues.push("Wähle das gewünschte Abo.")
      }
      if (this.creatorStep === 4) {
        if (!this.creatorVariantId) issues.push("Wähle eine Zahlungsvariante.")
        if (!this.creatorTermId) issues.push("Wähle eine Laufzeit.")
      }
      if (this.creatorStep === 6) return this.creatorTemporaryIssues.filter((issue) => !issue.includes("Preis konnte"))
      return issues
    },
    creatorTemporaryIssues() {
      const issues = []
      const companies = this.creatorData && Array.isArray(this.creatorData.companies) ? this.creatorData.companies : []
      if (!this.quote && !this.editingTemporaryContractId) issues.push("Der Preis konnte noch nicht berechnet werden.")
      if (this.creatorData?.catalog?.status !== "published" && !this.editingTemporaryContractId) issues.push("Wähle eine publizierte Katalogversion.")
      if (!this.isPublic && !this.creatorUser?._id) issues.push("Wähle den Kunden für den Vertrag.")
      if (!this.creatorStartDate) issues.push("Wähle den Vertragsbeginn.")
      if (!this.creatorUseStartDate) issues.push("Wähle den Nutzungsbeginn.")
      if (this.creatorStartDate && this.creatorUseStartDate && this.creatorUseStartDate < this.creatorStartDate) {
        issues.push("Der Nutzungsbeginn darf nicht vor dem Vertragsbeginn liegen.")
      }
      if (companies.length > 1 && !this.creatorCompanyId) issues.push("Wähle die Vertragsfirma.")
      if (this.creatorPaymentMethods.length && !this.creatorPaymentMethodId) issues.push("Wähle eine Zahlungsart.")
      if (this.selectedPaymentMethod?.selectDate && !this.creatorDebitDate) issues.push("Wähle das Belastungsdatum.")
      if (!this.selectedLegalProfile) issues.push("Für diese Auswahl fehlen freigegebene Vertragsbedingungen.")
      if (this.selectedContractType?.requiresPhysiotherapist && !this.creatorPhysiotherapistId) {
        issues.push("Wähle die betreuende Physiotherapie.")
      }
      return issues
    },
    creatorCompletionIssues() {
      const issues = [...this.creatorTemporaryIssues]
      if (this.isPublic && !this.publicAuthenticated) issues.push("Logge dich ein oder erstelle deinen Roody-Account.")
      if (this.isPublic && !this.publicBirthdayVerified) issues.push("Im Roody-Account fehlt ein gültiges Geburtsdatum.")
      if (!this.creatorTermsAccepted) issues.push("Akzeptiere die Vertragsbedingungen.")
      if (
        typeof this.creatorSignature !== "string" ||
        this.creatorSignature.length < 100 ||
        !/^data:image\/(?:png|jpe?g|webp);base64,/i.test(this.creatorSignature)
      ) {
        issues.push("Ergänze die Unterschrift.")
      }
      if (this.isMinor) {
        if (!this.creatorLegalRepresentative.firstName || !this.creatorLegalRepresentative.lastName || !this.creatorLegalRepresentative.email) {
          issues.push("Vervollständige die Angaben des gesetzlichen Vertreters.")
        }
        if (
          typeof this.creatorLegalSignature !== "string" ||
          this.creatorLegalSignature.length < 100 ||
          !/^data:image\/(?:png|jpe?g|webp);base64,/i.test(this.creatorLegalSignature)
        ) {
          issues.push("Ergänze die Unterschrift des gesetzlichen Vertreters.")
        }
      }
      return [...new Set(issues)]
    },
    creatorNextHint() {
      if (this.creatorStepIssues.length) return this.creatorStepIssues[0]
      return [
        "Danach finden wir die passende Mitgliedschaft.",
        "Als Nächstes wählst du dein konkretes Abo.",
        "Danach bestimmst du Laufzeit und Zahlungsvariante.",
        "Als Nächstes kannst du dein Abo ergänzen.",
        "Danach fehlen nur noch Start- und Zahlungsdetails.",
        "Alles bereit für Bedingungen und Unterschrift.",
        this.creatorCompletionIssues.length ? this.creatorCompletionIssues[0] : "Alles vollständig – der Vertrag kann jetzt abgeschlossen werden.",
      ][this.creatorStep - 1]
    },
    canContinueCreator() {
      return this.creatorStep < this.creatorStepLabels.length && this.creatorStepIssues.length === 0
    },
    canSubmitContract() {
      return !this.quoting && this.creatorCompletionIssues.length === 0
    },
    canSaveTemporaryContract() {
      return !this.isPublic && !this.quoting && this.creatorTemporaryIssues.length === 0
    },
    activeContractLockedByOther() {
      if (!this.activeContract || !this.activeContract.editingBy) return false
      if (!this.activeContract.editingAt || Date.now() - new Date(this.activeContract.editingAt).getTime() >= 30 * 60 * 1000) return false
      const editingId = this.activeContract.editingBy._id || this.activeContract.editingBy
      return !this.currentUserId || String(editingId) !== String(this.currentUserId)
    },
    activeContractEditingByCurrent() {
      if (!this.activeContract || !this.activeContract.editingBy || !this.currentUserId) return false
      if (!this.activeContract.editingAt || Date.now() - new Date(this.activeContract.editingAt).getTime() >= 30 * 60 * 1000) return false
      const editingId = this.activeContract.editingBy._id || this.activeContract.editingBy
      return String(editingId) === String(this.currentUserId)
    },
    contractSortValue: {
      get() {
        return `${this.contractSort}:${this.contractDirection}`
      },
      set(value) {
        const [sort, direction] = String(value || "createdAt:desc").split(":")
        this.contractSort = sort
        this.contractDirection = direction
      },
    },
    activeContractPaymentMethod() {
      const methods = this.activeContract?.managementOptions?.paymentMethods || []
      return methods.find((method) => method.id === this.contractEdit.paymentMethodId) || null
    },
    isMinor() {
      return this.creatorAge !== null && this.creatorAge !== "" && Number(this.creatorAge) < 18
    },
    filteredTacRows() {
      const search = String(this.tacSearch || "").toLowerCase()
      if (!search) return this.tacMappingRows
      return this.tacMappingRows.filter((row) => `${row.title} ${row.subtitle} ${row.localId}`.toLowerCase().includes(search))
    },
  },
  watch: {
    catalogSection() {
      this.catalogSearch = ""
    },
    masterDataView() {
      this.catalogSearch = ""
    },
    adminTab(value) {
      if (value === 3 && this.contracts.length === 0) this.loadContracts()
    },
  },
  async created() {
    this.currentUserId = this.storedUserId()
    if (this.isPublic) await this.loadPublicCreator()
    else await this.loadAdminData()
  },
  beforeDestroy() {
    clearTimeout(this.quoteTimer)
    if (this.editingTemporaryContractId) api.updateContractV2EditLock(this.editingTemporaryContractId, false).catch(() => {})
    else if (this.activeContractEditingByCurrent && this.activeContract) api.updateContractV2EditLock(this.activeContract._id, false).catch(() => {})
  },
  methods: {
    storedUserId() {
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}")
        const user = stored.user || stored
        return user._id || user.id || null
      } catch (error) {
        return null
      }
    },
    emptyOverride() {
      return { target: "planTerm", studioId: null, planId: null, variantId: null, addonId: null, termId: null, price: null, signUpFee: null }
    },
    defaultContractType(id, title, kind = "fixed", requiresPhysiotherapist = false) {
      return {
        id,
        title,
        description: "",
        kind,
        channels: ["internal", "public"],
        requiresPhysiotherapist,
        legalProfiles: [
          {
            id: `${id}-terms-default`,
            title: `${title} Bedingungen`,
            studioIds: [],
            planIds: [],
            preamble: "",
            conditions: [],
            confirmation: "",
            source: "manual",
            isDefault: true,
            active: true,
          },
        ],
        active: true,
      }
    },
    addContractType() {
      const id = this.uniqueId(
        "contract-type",
        this.catalog.contractTypes.map((contractType) => contractType.id),
      )
      this.catalog.contractTypes.push(this.defaultContractType(id, "Neuer Vertragstyp"))
    },
    removeContractType(typeIndex) {
      const contractType = this.catalog.contractTypes[typeIndex]
      const collections = [this.catalog.plans, this.catalog.addons, this.catalog.paymentMethods, this.catalog.categories, this.catalog.campaigns]
      const usageCount = collections.reduce(
        (count, items) =>
          count + items.filter((item) => Array.isArray(item.contractTypeIds) && item.contractTypeIds.includes(contractType.id)).length,
        0,
      )
      if (usageCount > 0) {
        this.error = `Der Vertragstyp "${contractType.title}" ist noch ${usageCount}-mal zugeordnet. Entferne zuerst diese Zuordnungen.`
        return
      }
      this.catalog.contractTypes.splice(typeIndex, 1)
    },
    addLegalProfile(contractType) {
      const id = this.uniqueId(
        `${contractType.id}-terms`,
        contractType.legalProfiles.map((profile) => profile.id),
      )
      contractType.legalProfiles.push({
        id,
        title: "Neue Vertragsbedingungen",
        studioIds: [],
        planIds: [],
        preamble: "",
        conditions: [],
        confirmation: "",
        source: "manual",
        isDefault: contractType.legalProfiles.length === 0,
        active: true,
      })
    },
    addLegalCondition(profile) {
      profile.conditions.push({
        id: this.uniqueId(
          "condition",
          profile.conditions.map((condition) => condition.id),
        ),
        text: "",
        highlight: false,
      })
    },
    setDefaultLegalProfile(contractType, profileId) {
      contractType.legalProfiles.forEach((profile) => this.$set(profile, "isDefault", profile.id === profileId))
    },
    removeLegalProfile(contractType, profileIndex) {
      const removed = contractType.legalProfiles.splice(profileIndex, 1)[0]
      if (removed && removed.isDefault && contractType.legalProfiles.length)
        this.setDefaultLegalProfile(contractType, contractType.legalProfiles[0].id)
    },
    catalogMatches(item) {
      const search = String(this.catalogSearch || "")
        .trim()
        .toLowerCase()
      if (!search) return true
      const config = item && item.config ? item.config : {}
      const mainCompany = config.mainCompany || {}
      const studios = (item && Array.isArray(item.studioIds) ? item.studioIds : [])
        .map((studioId) => this.studioOptions.find((option) => String(option.value) === String(studioId)))
        .filter(Boolean)
        .map((option) => option.label)
      return [
        item?.title,
        item?.name,
        item?.description,
        item?.id,
        config.description,
        config.street,
        config.city,
        mainCompany.name,
        mainCompany.street,
        mainCompany.city,
        ...studios,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search)
    },
    studioScopeLabel(studioIds) {
      const ids = Array.isArray(studioIds) ? studioIds : []
      if (ids.length === 0) return "Alle Standorte"
      const labels = ids
        .map((studioId) => this.studioOptions.find((option) => String(option.value) === String(studioId)))
        .filter(Boolean)
        .map((option) => option.label)
      if (labels.length === 1) return labels[0]
      if (labels.length === 2) return labels.join(", ")
      return labels.length ? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}` : `${ids.length} Standorte`
    },
    channelSummary(channels) {
      const values = Array.isArray(channels) ? channels : []
      if (values.includes("internal") && values.includes("public")) return "Intern und online"
      if (values.includes("public")) return "Nur online"
      if (values.includes("internal")) return "Nur intern"
      return "Kein Kanal"
    },
    companyAddress(company) {
      const config = company && company.config ? company.config : {}
      return [config.street, config.city].filter(Boolean).join(", ") || "Adresse noch nicht vollständig"
    },
    companySubtitle(company) {
      const config = company && company.config ? company.config : {}
      if (config.useMainCompany && config.mainCompany && config.mainCompany.city) return `Stammcenter ${config.mainCompany.city}`
      return config.city || "Adresse noch offen"
    },
    removeCatalogCompany(companyId) {
      const index = this.catalog.companies.findIndex((company) => company.id === companyId)
      if (index < 0) return
      this.catalog.companies.splice(index, 1)
      this.selectedCatalogCompanyId = this.catalog.companies[index]?.id || this.catalog.companies[index - 1]?.id || null
    },
    removeCatalogCategory(categoryId) {
      const index = this.catalog.categories.findIndex((category) => category.id === categoryId)
      if (index < 0) return
      this.catalog.categories.splice(index, 1)
      this.selectedCatalogCategoryId = this.catalog.categories[index]?.id || this.catalog.categories[index - 1]?.id || null
    },
    catalogSectionCount(section) {
      if (!this.catalog) return 0
      return (
        {
          types: this.catalog.contractTypes.length,
          plans: this.catalog.plans.length,
          addons: this.catalog.addons.length,
          payments: this.catalog.paymentMethods.length,
          masterdata: this.catalog.categories.length + this.catalog.companies.length,
          overrides: this.catalog.overrides.length,
          campaigns: this.catalog.campaigns.length,
        }[section] || 0
      )
    },
    contractTypeNames(ids) {
      const selected = new Set((ids || []).map(String))
      return (
        this.contractTypeOptions
          .filter((option) => selected.has(String(option.value)))
          .map((option) => option.label)
          .join(", ") || "Nicht zugeordnet"
      )
    },
    contractTypeKindLabel(kind) {
      const option = this.contractTypeKinds.find((entry) => entry.value === kind)
      return option ? option.text : "Vertragstyp"
    },
    physiotherapistLabel(physiotherapist) {
      const name = [physiotherapist.firstName, physiotherapist.lastName].filter(Boolean).join(" ")
      return name ? `${name} - ${physiotherapist.company}` : physiotherapist.company
    },
    supportsCreatorContractType(item) {
      const ids = Array.isArray(item?.contractTypeIds) ? item.contractTypeIds.map(String) : []
      return ids.length === 0 || ids.includes(String(this.creatorContractTypeId || ""))
    },
    async loadAdminData() {
      this.loading = true
      this.error = ""
      try {
        const [catalogs, rollout] = await Promise.all([api.fetchContractV2Catalogs(), api.fetchContractV2Rollouts()])
        this.catalogs = catalogs
        this.rolloutStudios = rollout.studios
        this.rolloutCatalogs = rollout.catalogs
        if (catalogs.length) {
          this.selectedCatalogId = catalogs[0]._id
          const publishedCatalog = catalogs.find((catalog) => catalog.status === "published")
          this.creatorCatalogId = publishedCatalog ? publishedCatalog._id : null
          await this.loadCatalog()
        }
        if (this.rolloutStudios.length) this.creatorStudioId = this.rolloutStudios[0]._id
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
    },
    async loadPublicCreator() {
      this.loading = true
      this.error = ""
      try {
        const studioId = this.$route.params.studioId
        this.creatorStudioId = studioId
        this.creatorData = await api.fetchContractV2PublicCatalog(studioId, "public")
        this.creatorContractTypeId = this.creatorData.contractTypes.length === 1 ? this.creatorData.contractTypes[0].id : null
        this.creatorPaymentMethodId = this.creatorPaymentMethods.length === 1 ? this.creatorPaymentMethods[0].id : null
        this.creatorCompanyId = this.creatorData.companies.length === 1 ? this.creatorData.companies[0].id : null
        if (this.publicAuthenticated) {
          try {
            const response = await api.getUser()
            const user = response.user || response
            const age = this.userAge(user && user.geburtstag)
            this.publicBirthdayVerified = age !== null
            if (age !== null) this.creatorAge = age
          } catch (authError) {
            localStorage.removeItem("token")
            this.publicAuthenticated = false
            this.publicBirthdayVerified = false
          }
        }
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
    },
    async importLegacy(force) {
      this.importing = true
      this.error = ""
      try {
        const response = await api.importContractV2Legacy(force)
        const imported = response.catalog
        await this.reloadCatalogLists()
        this.selectedCatalogId = imported._id
        await this.loadCatalog()
        this.notify(
          response.reused ? "Der identische Import war bereits vorhanden." : "Legacy-Einstellungen wurden als kompakter V2-Entwurf importiert.",
        )
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.importing = false
      }
    },
    openResetImportDialog() {
      this.resetImportError = ""
      this.resetImportDialog = true
    },
    async resetLegacyImport() {
      if (!this.catalog || this.catalog.source !== "legacy-import" || this.catalog.status !== "draft") return
      const catalogId = this.catalog._id
      this.resettingImport = true
      this.resetImportError = ""
      this.error = ""
      try {
        await api.resetContractV2LegacyImport(catalogId)
        this.resetImportDialog = false
        this.catalog = null
        this.validationIssues = []
        await this.reloadCatalogLists()
        this.selectedCatalogId = this.catalogs.length ? this.catalogs[0]._id : null
        if (this.creatorCatalogId === catalogId) this.creatorCatalogId = this.selectedCatalogId
        if (this.selectedCatalogId) await this.loadCatalog()
        this.notify("Legacy-Import zurückgesetzt. Du kannst die Einstellungen jetzt komplett neu importieren.")
      } catch (error) {
        this.resetImportError = this.errorText(error)
      } finally {
        this.resettingImport = false
      }
    },
    async reloadCatalogLists() {
      const [catalogs, rollout] = await Promise.all([api.fetchContractV2Catalogs(), api.fetchContractV2Rollouts()])
      this.catalogs = catalogs
      this.rolloutStudios = rollout.studios
      this.rolloutCatalogs = rollout.catalogs
    },
    async loadCatalog() {
      if (!this.selectedCatalogId) {
        this.catalog = null
        return
      }
      this.loading = true
      try {
        const catalog = await api.fetchContractV2Catalog(this.selectedCatalogId)
        this.validationIssues = catalog.validationIssues || []
        delete catalog.validationIssues
        this.ensureCatalogShape(catalog)
        this.catalog = catalog
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
    },
    ensureCatalogShape(catalog) {
      ;["contractTypes", "plans", "addons", "paymentMethods", "categories", "companies", "campaigns", "overrides"].forEach((key) => {
        if (!Array.isArray(catalog[key])) this.$set(catalog, key, [])
      })
      if (catalog.contractTypes.length === 0) {
        this.$set(catalog, "contractTypes", [
          this.defaultContractType("normal", "Normales Abo", "fixed", false),
          this.defaultContractType("flexi", "Flexi-Abo", "rolling", false),
          this.defaultContractType("mtt", "MTT", "medical", true),
        ])
      }
      const fallbackTypeIds = catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id)
      const termUnitsById = new Map()
      catalog.contractTypes.forEach((contractType) => {
        if (!Array.isArray(contractType.channels)) this.$set(contractType, "channels", ["internal", "public"])
        if (!Array.isArray(contractType.legalProfiles)) this.$set(contractType, "legalProfiles", [])
        contractType.legalProfiles.forEach((profile) => {
          ;["studioIds", "planIds", "conditions"].forEach((key) => {
            if (!Array.isArray(profile[key])) this.$set(profile, key, [])
          })
        })
      })
      catalog.plans.forEach((plan) => {
        if (!plan.eligibility) this.$set(plan, "eligibility", { minAge: null, maxAge: null, customerTypes: [] })
        if (!Array.isArray(plan.variants)) this.$set(plan, "variants", [])
        ;["contractTypeIds", "studioIds", "includedAddonIds", "tags"].forEach((key) => {
          if (!Array.isArray(plan[key])) this.$set(plan, key, [])
        })
        if (plan.contractTypeIds.length === 0) this.$set(plan, "contractTypeIds", [...fallbackTypeIds])
        plan.variants.forEach((variant) => {
          if (!Array.isArray(variant.studioIds)) this.$set(variant, "studioIds", [])
          if (!Array.isArray(variant.terms)) this.$set(variant, "terms", [])
          variant.terms.forEach((term) => termUnitsById.set(term.id, term.unit || "fixed"))
        })
      })
      catalog.campaigns.forEach((campaign) => {
        if (!campaign.conditions) this.$set(campaign, "conditions", {})
        if (!campaign.effects) this.$set(campaign, "effects", {})
        ;["studioIds", "channels", "contractTypeIds"].forEach((key) => {
          if (!Array.isArray(campaign[key])) this.$set(campaign, key, [])
        })
        if (campaign.contractTypeIds.length === 0) this.$set(campaign, "contractTypeIds", [...fallbackTypeIds])
        ;["planIds", "variantIds", "termIds", "categoryIds", "paymentMethodIds"].forEach((key) => {
          if (!Array.isArray(campaign.conditions[key])) this.$set(campaign.conditions, key, [])
        })
        const conditionDefaults = { minAddonTotal: null, minAddonCount: null, minAge: null, maxAge: null, isRenewal: null }
        Object.keys(conditionDefaults).forEach((key) => {
          if (campaign.conditions[key] === undefined) this.$set(campaign.conditions, key, conditionDefaults[key])
        })
        const effectDefaults = {
          waiveSignUpFee: false,
          planDiscountAmount: null,
          planDiscountPercent: null,
          addonDiscountAmount: null,
          addonDiscountPercent: null,
          giftCheapestAddon: false,
          bonusMonths: null,
        }
        Object.keys(effectDefaults).forEach((key) => {
          if (campaign.effects[key] === undefined) this.$set(campaign.effects, key, effectDefaults[key])
        })
        if (campaign.startsAt) campaign.startsAt = this.dateInput(campaign.startsAt)
        if (campaign.endsAt) campaign.endsAt = this.dateInput(campaign.endsAt)
      })
      catalog.addons.forEach((addon) => {
        if (!addon.eligibility) this.$set(addon, "eligibility", { minAge: null, maxAge: null, customerTypes: [] })
        ;["contractTypeIds", "studioIds", "availability", "prices", "incompatibleAddonIds", "includedInPlanIds", "tags"].forEach((key) => {
          if (!Array.isArray(addon[key])) this.$set(addon, key, [])
        })
        if (addon.contractTypeIds.length === 0) this.$set(addon, "contractTypeIds", [...fallbackTypeIds])
        addon.prices.forEach((price) => {
          const unit = termUnitsById.get(price.termId) || price.unit || "fixed"
          if (price.unit !== unit) this.$set(price, "unit", unit)
          if (price.maxPrice === undefined) this.$set(price, "maxPrice", null)
          if (unit !== "month" && price.maxPrice !== null) this.$set(price, "maxPrice", null)
        })
      })
      catalog.paymentMethods.forEach((method) => {
        ;["contractTypeIds", "studioIds", "channels"].forEach((key) => {
          if (!Array.isArray(method[key])) this.$set(method, key, [])
        })
        if (method.contractTypeIds.length === 0) this.$set(method, "contractTypeIds", [...fallbackTypeIds])
        if (method.channels.length === 0) this.$set(method, "channels", ["internal", "public"])
      })
      catalog.categories.forEach((category) => {
        if (!Array.isArray(category.contractTypeIds)) this.$set(category, "contractTypeIds", [...fallbackTypeIds])
        if (category.contractTypeIds.length === 0) this.$set(category, "contractTypeIds", [...fallbackTypeIds])
        if (!Array.isArray(category.studioIds)) this.$set(category, "studioIds", [])
        if (!Array.isArray(category.channels)) this.$set(category, "channels", ["internal", "public"])
        if (!category.config) this.$set(category, "config", {})
        const defaults = {
          description: "",
          additionalInfo: null,
          notes: false,
          notesSelection: false,
          notesSelectionOptions: [],
          notesDescription: "",
          noSignUpFee: false,
        }
        Object.keys(defaults).forEach((key) => {
          if (category.config[key] === undefined) this.$set(category.config, key, defaults[key])
        })
      })
      catalog.companies.forEach((company) => {
        if (!Array.isArray(company.studioIds)) this.$set(company, "studioIds", [])
        if (!company.config) this.$set(company, "config", {})
        const defaults = { street: "", city: "", phone: "", useMainCompany: false }
        Object.keys(defaults).forEach((key) => {
          if (company.config[key] === undefined) this.$set(company.config, key, defaults[key])
        })
        if (!company.config.mainCompany || typeof company.config.mainCompany !== "object") {
          this.$set(company.config, "mainCompany", { name: "", street: "", city: "", phone: "" })
        } else {
          ;["name", "street", "city", "phone"].forEach((key) => {
            if (company.config.mainCompany[key] === undefined) this.$set(company.config.mainCompany, key, "")
          })
        }
      })
      if (!catalog.companies.some((company) => company.id === this.selectedCatalogCompanyId)) {
        this.selectedCatalogCompanyId = catalog.companies[0]?.id || null
      }
      if (!catalog.categories.some((category) => category.id === this.selectedCatalogCategoryId)) {
        this.selectedCatalogCategoryId = catalog.categories[0]?.id || null
      }
    },
    async saveCatalog() {
      this.saving = true
      this.error = ""
      try {
        const saved = await api.saveContractV2Catalog(this.catalog._id, this.catalog)
        this.ensureCatalogShape(saved)
        this.catalog = saved
        this.validationIssues = []
        await this.reloadCatalogLists()
        this.notify("Katalog gespeichert.")
      } catch (error) {
        this.error = this.errorText(error)
        this.validationIssues = error.response && error.response.data && Array.isArray(error.response.data.details) ? error.response.data.details : []
      } finally {
        this.saving = false
      }
    },
    async cloneCatalog() {
      try {
        const cloned = await api.cloneContractV2Catalog(this.catalog._id)
        await this.reloadCatalogLists()
        this.selectedCatalogId = cloned._id
        await this.loadCatalog()
        this.notify("Neue bearbeitbare Katalogversion erstellt.")
      } catch (error) {
        this.error = this.errorText(error)
      }
    },
    async publishCatalog() {
      this.publishing = true
      try {
        await api.publishContractV2Catalog(this.catalog._id)
        await this.reloadCatalogLists()
        await this.loadCatalog()
        this.notify("Katalog publiziert. Standorte können ihn jetzt gezielt aktivieren.")
      } catch (error) {
        this.error = this.errorText(error)
        this.validationIssues = error.response && error.response.data && Array.isArray(error.response.data.details) ? error.response.data.details : []
      } finally {
        this.publishing = false
      }
    },
    uniqueId(label, existing) {
      const base =
        String(label || "item")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "item"
      let id = base
      let index = 2
      while (existing.includes(id)) id = `${base}-${index++}`
      return id
    },
    addPlan() {
      const id = this.uniqueId(
        "neuer-plan",
        this.catalog.plans.map((plan) => plan.id),
      )
      this.catalog.plans.push({
        id,
        title: "Neuer Plan",
        description: "",
        legacyTitles: [],
        contractTypeIds: this.catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id),
        studioIds: [],
        variants: [],
        includedAddonIds: [],
        tags: [],
        eligibility: { minAge: null, maxAge: null, customerTypes: [] },
        active: true,
      })
      this.addVariant(this.catalog.plans.length - 1)
    },
    removePlan(index) {
      this.catalog.plans.splice(index, 1)
    },
    addVariant(planIndex) {
      const plan = this.catalog.plans[planIndex]
      const variantIds = this.catalog.plans.reduce((ids, entry) => ids.concat(entry.variants.map((variant) => variant.id)), [])
      const id = this.uniqueId(`${plan.id}-upfront`, variantIds)
      plan.variants.push({ id, title: plan.title, paymentMode: "upfront", installments: 1, studioIds: [], terms: [], active: true })
      this.addTerm(planIndex, plan.variants.length - 1)
    },
    removeVariant(planIndex, variantIndex) {
      this.catalog.plans[planIndex].variants.splice(variantIndex, 1)
    },
    addTerm(planIndex, variantIndex) {
      const variant = this.catalog.plans[planIndex].variants[variantIndex]
      const id = this.uniqueId(
        "12-months",
        variant.terms.map((term) => term.id),
      )
      variant.terms.push({
        id,
        label: "12 Monate",
        days: 0,
        months: 12,
        years: 0,
        unit: "fixed",
        minUnits: 1,
        maxUnits: 1,
        price: 0,
        signUpFee: 0,
        active: true,
        sale: null,
      })
    },
    removeTerm(planIndex, variantIndex, termIndex) {
      this.catalog.plans[planIndex].variants[variantIndex].terms.splice(termIndex, 1)
    },
    addAddon() {
      const id = this.uniqueId(
        "neues-zusatzpaket",
        this.catalog.addons.map((addon) => addon.id),
      )
      this.catalog.addons.push({
        id,
        title: "Neues Zusatzpaket",
        description: "",
        contractTypeIds: this.catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id),
        prices: [],
        availability: [],
        studioIds: [],
        amountSelectable: false,
        minQuantity: 1,
        maxQuantity: 1,
        includedInPlanIds: [],
        incompatibleAddonIds: [],
        tags: [],
        eligibility: { minAge: null, maxAge: null, customerTypes: [] },
        active: true,
      })
    },
    addAddonPrice(addon) {
      const used = addon.prices.map((price) => price.termId)
      const option = this.termOptions.find((term) => !used.includes(term.value)) || this.termOptions[0]
      if (option) addon.prices.push({ termId: option.value, price: 0, maxPrice: null, unit: option.unit || "fixed" })
    },
    addonPriceUnit(price) {
      const option = this.termOptions.find((term) => term.value === price.termId)
      return option ? option.unit : price.unit || "fixed"
    },
    addonPriceTermChanged(price) {
      const unit = this.addonPriceUnit(price)
      this.$set(price, "unit", unit)
      if (unit !== "month") this.$set(price, "maxPrice", null)
      else if (price.maxPrice === undefined) this.$set(price, "maxPrice", null)
    },
    syncAddonPriceUnits(termId, unit) {
      this.catalog.addons.forEach((addon) =>
        addon.prices.forEach((price) => {
          if (price.termId !== termId) return
          this.$set(price, "unit", unit)
          if (unit !== "month") this.$set(price, "maxPrice", null)
        }),
      )
    },
    addPaymentMethod() {
      const id = this.uniqueId(
        "neue-zahlungsart",
        this.catalog.paymentMethods.map((method) => method.id),
      )
      this.catalog.paymentMethods.push({
        id,
        title: "Neue Zahlungsart",
        description: "",
        contractTypeIds: this.catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id),
        channels: ["internal", "public"],
        studioIds: [],
        selectDate: false,
        active: true,
      })
    },
    addCategory() {
      const id = this.uniqueId(
        "neue-kategorie",
        this.catalog.categories.map((category) => category.id),
      )
      this.catalog.categories.push({
        id,
        title: "Neue Kategorie",
        contractTypeIds: this.catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id),
        studioIds: [],
        channels: ["internal", "public"],
        active: true,
        config: {
          description: "",
          additionalInfo: null,
          notes: false,
          notesSelection: false,
          notesSelectionOptions: [],
          notesDescription: "",
          noSignUpFee: false,
        },
      })
      this.masterDataView = "categories"
      this.catalogSearch = ""
      this.selectedCatalogCategoryId = id
    },
    addCompany() {
      const id = this.uniqueId(
        "neue-firma",
        this.catalog.companies.map((company) => company.id),
      )
      this.catalog.companies.push({
        id,
        name: "Neue Vertragsfirma",
        studioIds: [],
        config: { street: "", city: "", phone: "", useMainCompany: false, mainCompany: { name: "", street: "", city: "", phone: "" } },
      })
      this.masterDataView = "companies"
      this.catalogSearch = ""
      this.selectedCatalogCompanyId = id
    },
    addCampaign() {
      const id = this.uniqueId(
        "neue-aktion",
        this.catalog.campaigns.map((campaign) => campaign.id),
      )
      const campaign = {
        id,
        title: "Neue Aktion",
        description: "",
        code: null,
        active: true,
        autoApply: true,
        stackable: true,
        priority: 0,
        startsAt: null,
        endsAt: null,
        studioIds: [],
        contractTypeIds: this.catalog.contractTypes.filter((contractType) => contractType.active !== false).map((contractType) => contractType.id),
        channels: ["internal", "public"],
        conditions: {
          planIds: [],
          variantIds: [],
          termIds: [],
          categoryIds: [],
          paymentMethodIds: [],
          minAddonTotal: null,
          minAddonCount: null,
          minAge: null,
          maxAge: null,
          isRenewal: null,
        },
        effects: {
          waiveSignUpFee: false,
          planDiscountAmount: null,
          planDiscountPercent: null,
          addonDiscountAmount: null,
          addonDiscountPercent: null,
          giftCheapestAddon: false,
          bonusMonths: null,
        },
        source: "manual",
      }
      this.catalog.campaigns.push(campaign)
      return campaign
    },
    addActivationWaiverCampaign() {
      const campaign = this.addCampaign()
      campaign.title = "Aktivierungs-Tarif ab CHF 100 geschenkt"
      campaign.description = "Sobald Zusatzpakete im Wert von mindestens CHF 100 gewählt werden, entfällt der Aktivierungs-Tarif."
      campaign.conditions.minAddonTotal = 100
      campaign.effects.waiveSignUpFee = true
      campaign.priority = 100
    },
    addOverride() {
      const value = this.newOverride
      if (
        !value.studioId ||
        !value.termId ||
        value.price === null ||
        (value.target === "planTerm" && (!value.planId || !value.variantId)) ||
        (value.target === "addonTerm" && !value.addonId)
      ) {
        this.error = "Bitte fülle Standort, Auswahl, Laufzeit und Preis für die Ausnahme aus."
        return
      }
      const values = { price: Number(value.price) }
      if (value.target === "planTerm" && value.signUpFee !== null && value.signUpFee !== "") values.signUpFee = Number(value.signUpFee)
      this.catalog.overrides.push({
        id: this.uniqueId(
          "manual-override",
          this.catalog.overrides.map((override) => override.id),
        ),
        target: value.target,
        studioId: value.studioId,
        planId: value.target === "planTerm" ? value.planId : null,
        variantId: value.target === "planTerm" ? value.variantId : null,
        addonId: value.target === "addonTerm" ? value.addonId : null,
        termId: value.termId,
        values,
        source: "manual",
      })
      this.newOverride = this.emptyOverride()
    },
    async saveRollout(studio) {
      this.savingRolloutId = studio._id
      try {
        const saved = await api.saveContractV2Rollout(studio._id, studio.rollout)
        studio.rollout = saved
        this.notify(`${studio.name} gespeichert.`)
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.savingRolloutId = null
      }
    },
    rolloutContractTypes(studio) {
      const catalog = this.rolloutCatalogs.find((entry) => entry._id === studio.rollout.catalog)
      if (!catalog) return []
      return (catalog.contractTypes || []).filter((contractType) => contractType.active !== false)
    },
    rolloutActive(studio) {
      const rollout = studio.rollout
      return rollout.internalFlow === "v2" || rollout.publicFlow === "v2" || rollout.websiteCatalog === "v2" || rollout.tacFlow !== "legacy"
    },
    async loadCreatorPreview() {
      if (!this.creatorStudioId || !this.creatorCatalogId) return
      this.loading = true
      try {
        this.creatorData = await api.previewContractV2Catalog(this.creatorCatalogId, this.creatorStudioId, "internal")
        this.resetCreatorSelections()
        this.creatorContractTypeId = this.creatorData.contractTypes.length === 1 ? this.creatorData.contractTypes[0].id : null
        this.creatorPaymentMethodId = this.creatorPaymentMethods.length === 1 ? this.creatorPaymentMethods[0].id : null
        this.creatorCompanyId = this.creatorData.companies.length === 1 ? this.creatorData.companies[0].id : null
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
    },
    resetCreatorData() {
      const temporaryId = this.editingTemporaryContractId
      if (temporaryId) api.updateContractV2EditLock(temporaryId, false).catch(() => {})
      this.editingTemporaryContractId = null
      this.creatorData = null
      this.resetCreatorSelections()
    },
    resetCreatorSelections() {
      this.creatorStep = 1
      this.creatorAge = null
      this.creatorGoals = []
      this.creatorContractTypeId = null
      this.creatorPlanId = null
      this.creatorCategoryId = null
      this.creatorCategoryNotes = ""
      this.creatorCompanyId = null
      this.creatorIsRenewal = false
      this.creatorCampaignCode = ""
      this.creatorStartDate = this.dateInput(new Date())
      this.creatorUseStartDate = this.creatorStartDate
      this.creatorVariantId = null
      this.creatorTermId = null
      this.creatorUnits = 1
      this.creatorAddonQuantities = {}
      this.creatorPaymentMethodId = null
      this.creatorDebitDate = null
      this.creatorPhysiotherapistId = null
      this.creatorUser = null
      this.creatorTermsAccepted = false
      this.creatorMarketingAccepted = false
      this.creatorSignature = null
      this.creatorLegalSignature = null
      this.creatorLegalRepresentative = { firstName: "", lastName: "", email: "", phone: "", relationship: "" }
      this.quote = null
      this.createdContract = null
      this.submitError = ""
      this.creatorValidationAttempted = false
    },
    creatorUserChanged(user) {
      this.creatorUser = user || null
      const age = this.userAge(user?.geburtstag)
      this.creatorAge = age
      this.creatorAgeChanged()
      this.submitError = ""
    },
    clearCreatorUser() {
      this.creatorUser = null
      this.creatorAge = null
      this.resetQuote()
    },
    creatorAgeChanged() {
      if (this.selectedPlan && !this.ageEligible(this.selectedPlan.eligibility)) {
        this.creatorPlanId = null
        this.creatorVariantId = null
        this.creatorTermId = null
        this.creatorAddonQuantities = {}
      }
      this.resetQuote()
    },
    ageEligible(eligibility) {
      if (this.creatorAge === null || this.creatorAge === "") return true
      const age = Number(this.creatorAge)
      if (eligibility && eligibility.minAge !== null && eligibility.minAge !== undefined && age < eligibility.minAge) return false
      if (eligibility && eligibility.maxAge !== null && eligibility.maxAge !== undefined && age > eligibility.maxAge) return false
      return true
    },
    toggleGoal(goal) {
      const index = this.creatorGoals.indexOf(goal)
      if (index >= 0) this.creatorGoals.splice(index, 1)
      else this.creatorGoals.push(goal)
      this.resetQuote()
    },
    selectCreatorContractType(contractType) {
      this.creatorContractTypeId = contractType.id
      this.creatorPlanId = null
      this.creatorCategoryId = null
      this.creatorCategoryNotes = ""
      this.creatorVariantId = null
      this.creatorTermId = null
      this.creatorAddonQuantities = {}
      this.creatorPaymentMethodId = null
      this.creatorDebitDate = null
      this.creatorPhysiotherapistId = null
      this.resetQuote()
      this.$nextTick(() => {
        if (this.creatorCategories.length === 1) this.creatorCategoryId = this.creatorCategories[0].id
        if (this.creatorPaymentMethods.length === 1) this.creatorPaymentMethodId = this.creatorPaymentMethods[0].id
      })
    },
    selectCategory(category) {
      this.creatorCategoryId = category.id
      this.creatorCategoryNotes = ""
      this.creatorIsRenewal = /erneuer|wieder|ehemalig/i.test(String(category.title || ""))
      this.resetQuote()
    },
    selectPlan(plan) {
      this.creatorPlanId = plan.id
      this.creatorVariantId = null
      this.creatorTermId = null
      this.creatorAddonQuantities = {}
      this.resetQuote()
    },
    selectVariant(variant) {
      this.creatorVariantId = variant.id
      this.creatorTermId = null
      this.creatorAddonQuantities = {}
      this.resetQuote()
    },
    selectTerm(term) {
      this.creatorTermId = term.id
      this.creatorUnits = term.minUnits || 1
      this.creatorAddonQuantities = {}
      this.scheduleQuote()
    },
    planStartingPrice(plan) {
      const prices = (plan?.variants || []).reduce(
        (values, variant) => values.concat((variant.terms || []).map((term) => Number(term.price)).filter(Number.isFinite)),
        [],
      )
      return prices.length ? Math.min(...prices) : null
    },
    addonOffer(addon) {
      return (
        addon.offers.find(
          (offer) => offer.planId === this.creatorPlanId && offer.variantId === this.creatorVariantId && offer.termId === this.creatorTermId,
        ) || null
      )
    },
    addonPrice(addon) {
      const offer = this.addonOffer(addon)
      if (!offer) return 0
      const price = offer.price * (offer.unit === "fixed" ? 1 : this.creatorUnits)
      return offer.unit === "month" && offer.maxPrice !== null && offer.maxPrice !== undefined ? Math.min(price, offer.maxPrice) : price
    },
    addonSelected(addonId) {
      return Number(this.creatorAddonQuantities[addonId] || 0) > 0
    },
    toggleAddon(addon) {
      if (this.addonSelected(addon.id)) this.$delete(this.creatorAddonQuantities, addon.id)
      else this.$set(this.creatorAddonQuantities, addon.id, addon.minQuantity || 1)
      this.scheduleQuote()
    },
    setAddonQuantity(addon, value) {
      const quantity = Math.min(addon.maxQuantity || 1, Math.max(addon.minQuantity || 1, Number(value) || 1))
      this.$set(this.creatorAddonQuantities, addon.id, quantity)
      this.scheduleQuote()
    },
    resetQuote() {
      this.quote = null
      clearTimeout(this.quoteTimer)
    },
    scheduleQuote() {
      this.resetQuote()
      this.quoteTimer = setTimeout(() => this.refreshQuote(), 250)
    },
    quotePayload() {
      return {
        studioId: this.creatorStudioId,
        catalogId: this.isPublic ? undefined : this.creatorCatalogId,
        channel: this.isPublic ? "public" : "internal",
        contractTypeId: this.creatorContractTypeId,
        planId: this.creatorPlanId,
        variantId: this.creatorVariantId,
        termId: this.creatorTermId,
        units: this.creatorUnits,
        addons: this.selectedAddonArray,
        paymentMethodId: this.creatorPaymentMethodId,
        age: this.creatorAge,
        preferences: this.creatorGoals,
        categoryId: this.creatorCategoryId,
        companyId: this.creatorCompanyId,
        isRenewal: this.creatorIsRenewal,
        campaignCodes: this.creatorCampaignCode ? [this.creatorCampaignCode] : [],
      }
    },
    async refreshQuote() {
      if (!this.creatorContractTypeId || !this.creatorPlanId || !this.creatorVariantId || !this.creatorTermId) return
      this.quoting = true
      this.quote = null
      this.submitError = ""
      try {
        this.quote = this.isPublic ? await api.createContractV2PublicQuote(this.quotePayload()) : await api.createContractV2Quote(this.quotePayload())
      } catch (error) {
        this.quote = null
        this.submitError = this.errorText(error)
      } finally {
        this.quoting = false
      }
    },
    async paymentMethodChanged() {
      if (!this.selectedPaymentMethod || !this.selectedPaymentMethod.selectDate) this.creatorDebitDate = null
      await this.refreshQuote()
    },
    creatorStartDateChanged() {
      if (!this.creatorUseStartDate || this.creatorUseStartDate < this.creatorStartDate) this.creatorUseStartDate = this.creatorStartDate
    },
    scrollCreatorTop() {
      this.$nextTick(() => {
        const container = this.$refs.creatorScroll
        if (container && typeof container.scrollTo === "function") container.scrollTo({ top: 0, behavior: "smooth" })
      })
    },
    showCreatorIssue(message) {
      this.creatorValidationAttempted = true
      this.submitError = message || "Bitte vervollständige die markierten Angaben."
      this.scrollCreatorTop()
    },
    async nextCreatorStep() {
      if (this.creatorStepIssues.length) {
        this.showCreatorIssue(this.creatorStepIssues[0])
        return
      }
      if ([4, 5, 6].includes(this.creatorStep) && !this.editingTemporaryContractId) {
        await this.refreshQuote()
        if (!this.quote) {
          this.showCreatorIssue(this.submitError || "Der Preis konnte nicht berechnet werden. Prüfe deine Auswahl.")
          return
        }
      }
      this.submitError = ""
      this.creatorValidationAttempted = false
      this.creatorStep += 1
      if (this.creatorStep === 6 && !this.creatorPaymentMethodId && this.creatorPaymentMethods.length === 1) {
        this.creatorPaymentMethodId = this.creatorPaymentMethods[0].id
        await this.refreshQuote()
      }
      this.scrollCreatorTop()
    },
    goToCreatorStep(step) {
      if (this.editingTemporaryContractId) return
      if (step < this.creatorStep) {
        this.creatorStep = step
        this.submitError = ""
        this.creatorValidationAttempted = false
        this.scrollCreatorTop()
      }
    },
    async applyRecommendation(recommendation) {
      const selections =
        recommendation.addons ||
        (recommendation.addonIds || (recommendation.addonId ? [recommendation.addonId] : [])).map((addonId) => ({ addonId, quantity: 1 }))
      selections.forEach((selection) => {
        const addon = this.availableAddons.find((entry) => entry.id === selection.addonId)
        if (addon) this.$set(this.creatorAddonQuantities, addon.id, Math.max(addon.minQuantity || 1, selection.quantity || 1))
      })
      await this.refreshQuote()
    },
    contractPayload() {
      return {
        quoteKey: this.quote.key,
        userId: this.creatorUser ? this.creatorUser._id : null,
        contract: { startsOn: this.creatorStartDate, useStartsOn: this.creatorUseStartDate || this.creatorStartDate },
        physiotherapistId: this.creatorPhysiotherapistId,
        payment: { debitDate: this.creatorDebitDate },
        responses: { categoryNotes: this.creatorCategoryNotes },
        legalRepresentative: this.isMinor ? this.creatorLegalRepresentative : null,
        signatures: { customer: this.creatorSignature, legalRepresentative: this.isMinor ? this.creatorLegalSignature : null },
        consents: { termsAccepted: this.creatorTermsAccepted, marketingAccepted: this.creatorMarketingAccepted },
      }
    },
    async submitContract() {
      if (!this.canSubmitContract) {
        this.showCreatorIssue(this.creatorCompletionIssues[0])
        return
      }
      this.submitting = true
      this.submitError = ""
      try {
        const data = this.contractPayload()
        if (this.editingTemporaryContractId) {
          this.createdContract = await api.finalizeContractV2Temporary(this.editingTemporaryContractId, data)
          this.editingTemporaryContractId = null
        } else {
          this.createdContract = this.isPublic ? await api.createContractV2Self(data) : await api.createContractV2(data)
        }
      } catch (error) {
        this.submitError = this.errorText(error)
      } finally {
        this.submitting = false
      }
    },
    async saveTemporaryContract() {
      if (!this.canSaveTemporaryContract) {
        this.showCreatorIssue(this.creatorTemporaryIssues[0])
        return
      }
      this.savingTemporary = true
      this.submitError = ""
      try {
        const contract = await api.createContractV2Temporary(this.contractPayload())
        this.notify(`Vertrag ${contract.number} wurde temporär gespeichert.`)
        this.resetCreatorSelections()
        this.creatorData = null
        this.contractFilters.status = "temporary"
        this.contractPage = 1
        await this.loadContracts()
        this.adminTab = 3
      } catch (error) {
        this.submitError = this.errorText(error)
      } finally {
        this.savingTemporary = false
      }
    },
    async resumeTemporaryContract(item) {
      this.contractActionLoading = `${item._id}:resume`
      this.error = ""
      let lockedContractId = null
      try {
        const detail = item.quote && item.managementOptions ? item : await api.fetchContractV2Contract(item._id)
        const alreadyLockedByCurrent = this.activeContract && this.activeContract._id === detail._id && this.activeContractEditingByCurrent
        if (!alreadyLockedByCurrent) await api.updateContractV2EditLock(detail._id, true)
        lockedContractId = detail._id
        this.creatorStudioId = detail.studio?._id || detail.studio
        this.creatorCatalogId = detail.catalog?._id || detail.catalog
        await this.loadCreatorPreview()
        if (!this.creatorData) throw new Error("Der Katalog für diesen temporären Vertrag konnte nicht geladen werden.")
        const request = detail.quote?.request || {}
        this.creatorContractTypeId = request.contractTypeId || detail.contractTypeId
        this.creatorAge = request.age ?? this.userAge(detail.user?.geburtstag)
        this.creatorGoals = Array.isArray(request.preferences) ? [...request.preferences] : []
        this.creatorPlanId = request.planId || null
        this.creatorCategoryId = request.categoryId || null
        this.creatorCategoryNotes = detail.responses?.category?.notes || ""
        this.creatorCompanyId = request.companyId || detail.responses?.company?.id || null
        this.creatorIsRenewal = Boolean(request.isRenewal)
        this.creatorCampaignCode = Array.isArray(request.campaignCodes) ? request.campaignCodes[0] || "" : ""
        this.creatorVariantId = request.variantId || null
        this.creatorTermId = request.termId || null
        this.creatorUnits = Number(request.units) || 1
        this.creatorAddonQuantities = (request.addons || []).reduce((result, addon) => {
          result[addon.addonId] = Number(addon.quantity) || 1
          return result
        }, {})
        this.creatorPaymentMethodId = request.paymentMethodId || detail.payment?.methodId || null
        this.creatorDebitDate = detail.payment?.debitDate || null
        this.creatorPhysiotherapistId = detail.physiotherapist?._id || detail.physiotherapist || null
        this.creatorUser = detail.user || null
        this.creatorStartDate = detail.startsOn
        this.creatorUseStartDate = detail.useStartsOn || detail.startsOn
        this.creatorLegalRepresentative = {
          firstName: detail.customerSnapshot?.legalRepresentative?.firstName || "",
          lastName: detail.customerSnapshot?.legalRepresentative?.lastName || "",
          email: detail.customerSnapshot?.legalRepresentative?.email || "",
          phone: detail.customerSnapshot?.legalRepresentative?.phone || "",
          relationship: detail.customerSnapshot?.legalRepresentative?.relationship || "",
        }
        this.creatorTermsAccepted = false
        this.creatorMarketingAccepted = Boolean(detail.consents?.marketingAccepted)
        this.creatorSignature = null
        this.creatorLegalSignature = null
        this.quote = { key: detail.quote?.key || "", result: detail.quote?.result || detail.offerSnapshot }
        this.editingTemporaryContractId = detail._id
        this.creatorStep = this.creatorStepLabels.length - 1
        this.activeContract = null
        this.contractDialog = false
        this.adminTab = 2
      } catch (error) {
        if (lockedContractId) api.updateContractV2EditLock(lockedContractId, false).catch(() => {})
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async accountLogin(data) {
      await this.completePublicAuthentication(data)
      this.showLoginDialog = false
    },
    async newAccountRegistered(data) {
      await this.completePublicAuthentication(data)
      this.showRegisterDialog = false
    },
    async completePublicAuthentication(data) {
      if (!data || !data.token) return
      localStorage.setItem("token", data.token)
      if (data.user) localStorage.setItem("user", JSON.stringify(data.user))
      this.publicAuthenticated = true
      try {
        const response = await api.getUser()
        const user = response.user || response
        const age = this.userAge(user && user.geburtstag)
        this.publicBirthdayVerified = age !== null
        if (age === null) {
          this.submitError = "Im Roody-Account fehlt ein gültiges Geburtsdatum."
          return
        }
        if (age !== null && age !== Number(this.creatorAge)) {
          this.creatorAge = age
          await this.refreshQuote()
          if (!this.quote) {
            this.creatorStep = 1
            this.submitError = "Bitte wähle das Angebot nochmals passend zum hinterlegten Geburtsdatum."
          }
        }
      } catch (error) {
        this.submitError = this.errorText(error)
      }
    },
    userAge(value) {
      if (!value) return null
      const text = String(value)
      let match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
      let year
      let month
      let day
      if (match) {
        year = Number(match[1])
        month = Number(match[2])
        day = Number(match[3])
      } else {
        match = text.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})$/)
        if (!match) return null
        day = Number(match[1])
        month = Number(match[2])
        year = Number(match[3])
      }
      if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return null
      const birthday = new Date(Date.UTC(year, month - 1, day))
      if (birthday.getUTCFullYear() !== year || birthday.getUTCMonth() + 1 !== month || birthday.getUTCDate() !== day) return null
      const current = Object.fromEntries(
        new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" })
          .formatToParts(new Date())
          .filter((part) => part.type !== "literal")
          .map((part) => [part.type, Number(part.value)]),
      )
      let age = current.year - year
      if (current.month < month || (current.month === month && current.day < day)) age -= 1
      return age >= 0 && age <= 120 ? age : null
    },
    startAnotherContract() {
      this.createdContract = null
      this.editingTemporaryContractId = null
      this.resetCreatorSelections()
      if (this.isPublic) this.loadPublicCreator()
      else this.loadCreatorPreview()
    },
    async loadContracts() {
      this.loadingContracts = true
      try {
        const params = Object.keys(this.contractFilters).reduce(
          (result, key) => {
            if (this.contractFilters[key]) result[key] = this.contractFilters[key]
            return result
          },
          { page: this.contractPage, limit: 25, sort: this.contractSort, direction: this.contractDirection },
        )
        const [response, stats] = await Promise.all([api.fetchContractV2Contracts(params), api.fetchContractV2Stats()])
        this.contracts = response.items || []
        this.contractTotal = response.total || 0
        this.contractPage = response.page || 1
        this.contractPages = response.pages || 1
        this.contractStats = stats || { counts: {}, online: 0, active: 0 }
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loadingContracts = false
      }
    },
    applyContractFilters() {
      this.contractPage = 1
      this.loadContracts()
    },
    selectContractStatus(status) {
      this.contractFilters.status = status
      this.contractPage = 1
      this.loadContracts()
    },
    changeContractPage(offset) {
      const page = Math.min(this.contractPages, Math.max(1, this.contractPage + offset))
      if (page === this.contractPage) return
      this.contractPage = page
      this.loadContracts()
    },
    populateContractEdit(contract) {
      this.contractEdit = {
        startsOn: contract.startsOn || "",
        useStartsOn: contract.useStartsOn || contract.startsOn || "",
        paymentMethodId: contract.payment?.methodId || null,
        debitDate: contract.payment?.debitDate || null,
        reference: contract.payment?.reference || "",
        physiotherapistId: contract.physiotherapist?._id || contract.physiotherapist || null,
      }
    },
    managementPaymentMethodChanged() {
      if (!this.activeContractPaymentMethod || !this.activeContractPaymentMethod.selectDate) this.contractEdit.debitDate = null
    },
    async openContract(item) {
      this.contractActionLoading = `${item._id}:open`
      try {
        this.activeContract = await api.fetchContractV2Contract(item._id)
        this.populateContractEdit(this.activeContract)
        this.contractComment = ""
        this.contractCommentReason = null
        this.contractDialogSection = "overview"
        this.contractDialog = true
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async closeContractDialog() {
      const contract = this.activeContract
      this.contractDialog = false
      if (contract && this.activeContractEditingByCurrent) {
        try {
          await api.updateContractV2EditLock(contract._id, false)
        } catch (error) {
          this.error = this.errorText(error)
        }
      }
      this.activeContract = null
    },
    async toggleContractEditLock() {
      if (!this.activeContract || this.activeContractLockedByOther) return
      const active = !this.activeContractEditingByCurrent
      this.contractActionLoading = `${this.activeContract._id}:lock`
      try {
        await api.updateContractV2EditLock(this.activeContract._id, active)
        this.activeContract = await api.fetchContractV2Contract(this.activeContract._id)
        this.populateContractEdit(this.activeContract)
        this.contractDialogSection = active ? "edit" : "overview"
        this.notify(active ? "Bearbeitung übernommen." : "Bearbeitung beendet.")
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async saveContractDetails() {
      if (!this.activeContract || this.activeContractLockedByOther) return
      this.contractDetailsSaving = true
      let acquiredLock = false
      try {
        if (!this.activeContractEditingByCurrent) {
          await api.updateContractV2EditLock(this.activeContract._id, true)
          acquiredLock = true
        }
        await api.updateContractV2Details(this.activeContract._id, {
          startsOn: this.contractEdit.startsOn,
          useStartsOn: this.contractEdit.useStartsOn,
          payment: {
            methodId: this.contractEdit.paymentMethodId,
            debitDate: this.contractEdit.debitDate,
            reference: this.contractEdit.reference,
          },
          physiotherapistId: this.contractEdit.physiotherapistId,
        })
        this.activeContract = await api.fetchContractV2Contract(this.activeContract._id)
        this.populateContractEdit(this.activeContract)
        await this.loadContracts()
        this.notify("Vertragsdaten gespeichert.")
      } catch (error) {
        if (acquiredLock && this.activeContract) api.updateContractV2EditLock(this.activeContract._id, false).catch(() => {})
        this.error = this.errorText(error)
      } finally {
        this.contractDetailsSaving = false
      }
    },
    async changeContractStatus(item, status) {
      this.contractActionLoading = `${item._id}:${status}`
      try {
        const updated = await api.updateContractV2Status(item._id, status)
        Object.assign(item, updated)
        if (this.activeContract && this.activeContract._id === item._id) Object.assign(this.activeContract, updated)
        await this.loadContracts()
        if (this.contractDialog && status !== "deleted") {
          this.activeContract = await api.fetchContractV2Contract(item._id)
          this.populateContractEdit(this.activeContract)
        }
        this.notify(status === "accepted" ? "Vertrag akzeptiert." : status === "created" ? "Vertrag erneut eingereicht." : "Status aktualisiert.")
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async addContractComment(showNotification = true) {
      const text = String(this.contractComment || "").trim()
      const reason = String(this.contractCommentReason || "").trim()
      if (!this.activeContract || (!text && !reason)) return false
      try {
        await api.addContractV2Comment(this.activeContract._id, text || reason, this.activeContract.status, reason || null)
        this.activeContract = await api.fetchContractV2Contract(this.activeContract._id)
        this.contractComment = ""
        this.contractCommentReason = null
        if (showNotification) this.notify("Kommentar gespeichert.")
        return true
      } catch (error) {
        this.error = this.errorText(error)
        return false
      }
    },
    async rejectActiveContract() {
      if (!this.activeContract || !this.contractCommentReason) return
      const saved = await this.addContractComment(false)
      if (saved) await this.changeContractStatus(this.activeContract, "rejected")
    },
    requestDeleteContract() {
      if (!this.activeContract) return
      this.contractDeleteDialog = true
    },
    async deleteActiveContract() {
      if (!this.activeContract) return
      const contract = this.activeContract
      this.contractActionLoading = `${contract._id}:deleted`
      try {
        await api.updateContractV2Status(contract._id, "deleted")
        this.contractDeleteDialog = false
        this.contractDialog = false
        this.activeContract = null
        await this.loadContracts()
        this.notify(`Vertrag ${contract.number} wurde gelöscht.`)
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async queueTac(item) {
      this.contractActionLoading = `${item._id}:tac`
      try {
        const response = await api.queueContractV2Tac(item._id)
        if (response.missingMappings && response.missingMappings.length) {
          const labels = [...new Set(response.missingMappings.map((entry) => entry.type))].join(", ")
          this.error = `${response.missingMappings.length} TAC-Voraussetzungen fehlen (${labels}). Bitte Profil, Standort und Mappings prüfen.`
        } else {
          this.notify("TAC-Auftrag wurde idempotent in die Queue gelegt. Es wurde kein Live-Aufruf ausgeführt.")
        }
        await this.loadContracts()
        if (this.activeContract && this.activeContract._id === item._id) {
          this.activeContract = await api.fetchContractV2Contract(item._id)
          this.populateContractEdit(this.activeContract)
          if (!["temporary", "created", "accepted", "rejected"].includes(this.activeContract.status)) this.contractDialogSection = "overview"
        }
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.contractActionLoading = null
      }
    },
    async loadTacMapping() {
      this.tacMappingRows = []
      if (!this.tacStudioId) return
      this.loading = true
      try {
        const studio = this.rolloutStudios.find((entry) => entry._id === this.tacStudioId)
        const catalogId = studio && studio.rollout ? studio.rollout.catalog : null
        if (!catalogId) throw new Error("Bitte zuerst im Rollout einen publizierten Katalog für diesen Standort wählen.")
        const [catalog, mappings] = await Promise.all([api.fetchContractV2Catalog(catalogId), api.fetchContractV2TacMappings(this.tacStudioId)])
        this.tacCatalog = catalog
        this.tacMappingRows = this.buildMappingRows(catalog, mappings)
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.loading = false
      }
    },
    buildMappingRows(catalog, mappings) {
      const byKey = new Map(mappings.map((mapping) => [this.mappingKey(mapping), mapping]))
      const rows = []
      catalog.plans.forEach((plan) => {
        plan.variants.forEach((variant) => {
          variant.terms.forEach((term) => {
            const base = { type: "plan", localId: plan.id, variantId: variant.id, termId: term.id }
            rows.push({
              ...base,
              title: plan.title,
              subtitle: `${variant.title} / ${term.label}`,
              tacMemberCategoryId: "",
              tacPromotionId: "",
              status: "review",
              ...(byKey.get(this.mappingKey(base)) || {}),
            })
          })
        })
      })
      catalog.addons.forEach((addon) => {
        const base = { type: "addon", localId: addon.id, variantId: "", termId: "" }
        rows.push({
          ...base,
          title: addon.title,
          subtitle: "Generisches Zusatzpaket-Mapping",
          tacMemberCategoryId: "",
          tacPromotionId: "",
          status: "review",
          ...(byKey.get(this.mappingKey(base)) || {}),
        })
      })
      catalog.campaigns.forEach((campaign) => {
        const base = { type: "campaign", localId: campaign.id, variantId: "", termId: "" }
        rows.push({
          ...base,
          title: campaign.title,
          subtitle: "Aktion / TAC Promotion",
          tacMemberCategoryId: "",
          tacPromotionId: "",
          status: "review",
          ...(byKey.get(this.mappingKey(base)) || {}),
        })
      })
      return rows
    },
    mappingKey(mapping) {
      return [mapping.type, mapping.localId, mapping.variantId || "", mapping.termId || ""].join("|")
    },
    async saveTacMappings() {
      this.savingTac = true
      try {
        const mappings = this.tacMappingRows.map((row) => ({
          type: row.type,
          localId: row.localId,
          variantId: row.variantId || "",
          termId: row.termId || "",
          tacMemberCategoryId: row.tacMemberCategoryId || null,
          tacPromotionId: row.tacPromotionId || null,
          status: row.status,
        }))
        await api.saveContractV2TacMappings(this.tacStudioId, mappings)
        await this.loadTacMapping()
        this.notify("TAC-Mappings gespeichert.")
      } catch (error) {
        this.error = this.errorText(error)
      } finally {
        this.savingTac = false
      }
    },
    studioName(id) {
      const studio = this.rolloutStudios.find((entry) => entry._id === String(id))
      return studio ? studio.name : String(id || "Global")
    },
    planName(id) {
      const plan = this.catalog && this.catalog.plans.find((entry) => entry.id === id)
      return plan ? plan.title : id
    },
    addonName(id) {
      const addon = this.catalog && this.catalog.addons.find((entry) => entry.id === id)
      return addon ? addon.title : id
    },
    termName(id) {
      const term = this.termOptions.find((entry) => entry.value === id)
      return term ? term.label : id
    },
    personName(person, fallback = "System") {
      if (!person || typeof person !== "object") return fallback
      return [person.vorname || person.firstName, person.nachname || person.lastName].filter(Boolean).join(" ") || person.email || fallback
    },
    sourceLabel(source) {
      return source === "internal" ? "Intern" : source === "website" ? "Webseite" : "Online"
    },
    contractViewLabel(status) {
      const view = this.contractStatusViews.find((entry) => entry.value === status)
      return view ? view.label : this.statusLabel(status)
    },
    contractPaymentLabel(contract) {
      const methods = contract?.managementOptions?.paymentMethods || []
      const method = methods.find((entry) => entry.id === contract?.payment?.methodId)
      return method?.title || contract?.payment?.methodId || "Nicht hinterlegt"
    },
    contractHasActiveLock(contract) {
      return Boolean(contract?.editingBy && contract?.editingAt && Date.now() - new Date(contract.editingAt).getTime() < 30 * 60 * 1000)
    },
    auditActionLabel(action) {
      const labels = {
        "temporary:created": "Temporär gespeichert",
        "temporary:finalized": "Temporären Vertrag abgeschlossen",
        created: "Vertrag erstellt",
        "details:updated": "Vertragsdaten angepasst",
        "editing:started": "Bearbeitung übernommen",
        "editing:stopped": "Bearbeitung beendet",
        commented: "Kommentar hinzugefügt",
        "status:created": "Erneut eingereicht",
        "status:accepted": "Vertrag akzeptiert",
        "status:rejected": "Vertrag abgelehnt",
        "status:deleted": "Vertrag gelöscht",
        "status:tac_pending": "Für TAC vorbereitet",
        "status:tac_synced": "Mit TAC synchronisiert",
        "tac:queued": "TAC-Auftrag eingereiht",
        "tac:review": "TAC-Prüfung erforderlich",
      }
      return labels[action] || String(action || "Aktualisiert").replace(/:/g, " · ")
    },
    formatDateOnly(value) {
      if (!value) return "-"
      const date = new Date(`${value}T12:00:00`)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("de-CH")
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
    },
    printV2Contract(contract = this.activeContract) {
      if (!contract) return
      const printWindow = window.open("", "_blank")
      if (!printWindow) {
        this.error = "Das Druckfenster wurde blockiert. Bitte Pop-ups für diese Seite erlauben."
        return
      }
      printWindow.opener = null
      const customerName = `${contract.customerSnapshot?.firstName || ""} ${contract.customerSnapshot?.lastName || ""}`.trim()
      const studioName = contract.studio?.name || "-"
      const lines = (contract.offerSnapshot?.lines || [])
        .map(
          (line) =>
            `<tr><td>${this.escapeHtml(line.title)}</td><td>${this.escapeHtml(line.quantity || 1)}</td><td>CHF ${this.money(line.total)}</td></tr>`,
        )
        .join("")
      const conditions = (contract.legalSnapshot?.conditions || []).map((condition) => `<li>${this.escapeHtml(condition.text)}</li>`).join("")
      const customerSignature = /^data:image\//.test(contract.signatures?.customer || "")
        ? `<div class="signature"><span>Unterschrift</span><img src="${this.escapeHtml(contract.signatures.customer)}" alt="Unterschrift" /></div>`
        : ""
      const html = `<!doctype html><html lang="de"><head><meta charset="utf-8"><title>${this.escapeHtml(contract.number)}</title><style>
        *{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#17221c;margin:0;padding:36px;font-size:14px;line-height:1.5}header{display:flex;justify-content:space-between;gap:24px;padding-bottom:24px;border-bottom:3px solid #00963f}h1{font-size:28px;margin:0}h2{font-size:18px;margin:28px 0 10px}.meta{color:#64736a}.grid{display:grid;grid-template-columns:repeat(2,1fr);gap:12px 24px;margin-top:24px}.grid div{padding:12px;background:#f4f8f5;border-radius:8px}.grid span,.signature span{display:block;color:#64736a;font-size:12px;text-transform:uppercase;letter-spacing:.06em}table{width:100%;border-collapse:collapse}td,th{padding:10px;border-bottom:1px solid #dfe7e2;text-align:left}td:last-child,th:last-child{text-align:right}.total{font-size:20px;font-weight:700;text-align:right;margin-top:14px}ol{padding-left:22px}.signature{margin-top:30px;padding-top:16px;border-top:1px solid #dfe7e2}.signature img{display:block;max-width:280px;max-height:110px;margin-top:8px}@media print{body{padding:0}}
      </style></head><body><header><div><div class="meta">Vertragsplattform V2</div><h1>Mitgliedschaftsvertrag</h1><div class="meta">${this.escapeHtml(
        contract.number,
      )}</div></div><div><strong>${this.escapeHtml(studioName)}</strong><div class="meta">Erstellt am ${this.escapeHtml(
        this.formatDate(contract.createdAt),
      )}</div></div></header>
      <section class="grid"><div><span>Kunde</span><strong>${this.escapeHtml(customerName)}</strong><br>${this.escapeHtml(
        contract.customerSnapshot?.email || "",
      )}</div><div><span>Vertrag</span><strong>${this.escapeHtml(
        contract.contractTypeSnapshot?.title || contract.contractTypeId,
      )}</strong><br>${this.escapeHtml(this.statusLabel(contract.status))}</div><div><span>Vertragsbeginn</span><strong>${this.escapeHtml(
        this.formatDateOnly(contract.startsOn),
      )}</strong></div><div><span>Nutzungsbeginn</span><strong>${this.escapeHtml(this.formatDateOnly(contract.useStartsOn))}</strong></div></section>
      <h2>Preisübersicht</h2><table><thead><tr><th>Position</th><th>Menge</th><th>Betrag</th></tr></thead><tbody>${lines}</tbody></table><div class="total">Total CHF ${this.money(
        contract.offerSnapshot?.total,
      )}</div>
      ${
        contract.legalSnapshot
          ? `<h2>${this.escapeHtml(contract.legalSnapshot.title || "Vertragsbedingungen")}</h2><p>${this.escapeHtml(
              contract.legalSnapshot.preamble || "",
            )}</p><ol>${conditions}</ol><p><strong>${this.escapeHtml(contract.legalSnapshot.confirmation || "")}</strong></p>`
          : ""
      }${customerSignature}
      <script>window.addEventListener('load',function(){window.print()})<\/script></body></html>`
      printWindow.document.open()
      printWindow.document.write(html)
      printWindow.document.close()
    },
    money(value) {
      return Number(value || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("de-CH") : "-"
    },
    dateInput(value) {
      if (!value) return null
      return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Zurich", year: "numeric", month: "2-digit", day: "2-digit" }).format(
        new Date(value),
      )
    },
    statusLabel(status) {
      const option = this.contractStatusOptions.find((entry) => entry.value === status)
      return option ? option.text : status
    },
    statusColor(status) {
      return (
        {
          temporary: "amber darken-2",
          created: "warning",
          accepted: "success",
          rejected: "error",
          tac_pending: "info",
          tac_synced: "primary",
          deleted: "grey",
        }[status] || "grey"
      )
    },
    errorText(error) {
      return (
        (error && error.response && error.response.data && (error.response.data.message || error.response.data.status)) ||
        (error && error.message) ||
        "Unbekannter Fehler"
      )
    },
    notify(message) {
      if (this.$toast && this.$toast.success) this.$toast.success(message)
    },
  },
}
</script>

<style lang="scss" scoped>
.contract-v2 {
  --v2-primary: #00963f;
  --v2-ink: #17221c;
  --v2-muted: #64736a;
  --v2-border: #dfe7e2;
  --v2-soft: #f4f8f5;
  --v2-surface: #ffffff;
  --v2-surface-subtle: #fafcfb;
  --v2-primary-soft: #e9f7ef;
  --v2-selected-soft: #f4fbf7;
  --v2-info-soft: #f0f7ff;
  --v2-info-border: #cddff3;
  --v2-rollout-soft: #eff7ff;
  --v2-progress-surface: #f7faf8;
  --v2-step-border: #b9c7be;
  --v2-shadow: rgba(23, 34, 28, 0.05);
  min-height: 100%;
  color: var(--v2-ink);
  font-size: 1rem;
  line-height: 1.5;
}

.contract-v2--admin {
  --v2-ink: #f2f6f3;
  --v2-muted: #a7b4ac;
  --v2-border: rgba(255, 255, 255, 0.12);
  --v2-soft: #252b28;
  --v2-surface: #1d211f;
  --v2-surface-subtle: #212623;
  --v2-primary-soft: rgba(0, 150, 63, 0.16);
  --v2-selected-soft: rgba(0, 150, 63, 0.11);
  --v2-info-soft: rgba(33, 150, 243, 0.1);
  --v2-info-border: rgba(100, 181, 246, 0.3);
  --v2-rollout-soft: rgba(33, 150, 243, 0.09);
  --v2-progress-surface: #191d1b;
  --v2-step-border: #59655e;
  --v2-shadow: rgba(0, 0, 0, 0.28);
  background: radial-gradient(circle at 8% 0%, rgba(0, 150, 63, 0.08), transparent 30rem), #161917;
}

.contract-v2 ::v-deep .v-chip {
  font-size: 0.75rem;
}

.contract-v2__container {
  max-width: 1480px;
  padding: 1.5rem;
}

.contract-v2--public {
  min-height: 100vh;
  background: linear-gradient(180deg, #f5faf7 0%, #ffffff 42%);
}

.contract-v2--public .contract-v2__container {
  max-width: 1120px;
  padding-top: 2rem;
}

.public-header {
  min-height: 5rem;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #17221c;
  color: white;
}

.public-header__brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.public-header__brand img {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
}

.public-header__brand div {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.public-header__brand span {
  font-size: 1.25rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.public-header__brand small,
.table-subline,
.dialog-title small {
  display: block;
  font-size: 0.75rem;
}

.public-header__spacer {
  width: 3rem;
}

.page-heading,
.section-heading,
.card-title-row,
.nested-card__heading,
.button-row,
.rollout-header,
.creator-actions,
.dialog-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.page-heading {
  margin-bottom: 1.5rem;
  align-items: flex-start;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  margin-bottom: 0.35rem;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  line-height: 1.15;
  font-weight: 850;
}

h2 {
  margin-bottom: 0.3rem;
  font-size: 1.35rem;
  line-height: 1.25;
  font-weight: 800;
}

h3 {
  font-size: 1.05rem;
  line-height: 1.35;
}

p,
.checkbox-label {
  margin-bottom: 0;
  font-size: 1rem;
}

.page-heading p,
.section-heading p,
.card-title-row p,
.secondary-text,
.conversation-title p,
.choice-card p,
.addon-card p,
.recommendation-box p,
.rollout-note {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.payment-method-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(17rem, 1fr));
  gap: 1rem;
}

.account-gate {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.eyebrow,
.field-label {
  color: var(--v2-primary);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.section-stack {
  display: grid;
  gap: 1.25rem;
}

.surface-card,
.creator-card,
.success-card {
  background-color: var(--v2-surface) !important;
  border-color: var(--v2-border) !important;
  border-radius: 1rem !important;
  box-shadow: 0 0.6rem 2rem var(--v2-shadow) !important;
}

.card-title-row {
  padding: 1.25rem !important;
  align-items: flex-start;
}

.v2-tabs {
  overflow: hidden;
  background-color: var(--v2-surface) !important;
  border: 1px solid var(--v2-border);
  border-radius: 0.9rem;
}

.contract-v2 ::v-deep .v2-tabs > .v-tabs-bar {
  background-color: transparent !important;
}

.button-row {
  flex-wrap: wrap;
}

.button-row--end {
  justify-content: flex-end;
}

.catalog-meta,
.chip-wrap,
.goal-grid {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
}

.catalog-meta span {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.import-scope {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.import-scope > div {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.8rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.75rem;
  background: var(--v2-soft);
}

.import-scope span,
.import-scope strong {
  display: block;
  font-size: 0.875rem;
}

.import-scope span {
  color: var(--v2-muted);
}

.import-scope strong {
  margin-bottom: 0.1rem;
  color: var(--v2-ink);
}

.catalog-command-card {
  overflow: hidden;
}

.catalog-command-card__top,
.catalog-command-bar,
.catalog-import-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
}

.catalog-command-card__top {
  padding: 1.5rem;
  background: radial-gradient(circle at 0 0, rgba(0, 150, 63, 0.18), transparent 24rem), var(--v2-surface);
}

.catalog-command-card__identity,
.catalog-command-card__identity > span,
.catalog-command-card__state,
.catalog-import-bar > div,
.catalog-import-bar > div > span {
  display: flex;
  align-items: center;
}

.catalog-command-card__identity {
  min-width: 0;
  gap: 1rem;
}

.catalog-command-card__identity > span,
.catalog-import-bar > div > span {
  width: 3.25rem;
  height: 3.25rem;
  flex: 0 0 auto;
  justify-content: center;
  border: 1px solid rgba(0, 150, 63, 0.34);
  border-radius: 1rem;
  background: var(--v2-primary-soft);
}

.catalog-command-card__identity h2 {
  max-width: 48rem;
  margin: 0.2rem 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.catalog-command-card__identity p,
.catalog-import-bar small {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.catalog-command-card__state {
  flex: 0 0 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
}

.catalog-command-card__state small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.catalog-status {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid var(--v2-border);
  border-radius: 999px;
  color: var(--v2-muted);
  background: var(--v2-soft);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.catalog-status i {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #ffb23e;
  box-shadow: 0 0 0 0.25rem rgba(255, 178, 62, 0.12);
}

.catalog-status--published i {
  background: #3dcc76;
  box-shadow: 0 0 0 0.25rem rgba(61, 204, 118, 0.12);
}

.catalog-status--archived i {
  background: #88948d;
  box-shadow: none;
}

.catalog-command-bar {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--v2-border);
  border-bottom: 1px solid var(--v2-border);
  background: var(--v2-surface-subtle);
}

.catalog-version-select {
  max-width: 42rem;
}

.catalog-command-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.65rem;
}

.catalog-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(8rem, 1fr)) auto;
  align-items: stretch;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
}

.catalog-kpis > div {
  min-width: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  justify-content: start;
  column-gap: 0.65rem;
  padding: 0.75rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.85rem;
  background: var(--v2-soft);
}

.catalog-kpis > div > span {
  width: 2rem;
  height: 2rem;
  grid-row: 1 / span 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.6rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.catalog-kpis strong {
  font-size: 1.1rem;
  line-height: 1.1;
}

.catalog-kpis small {
  overflow: hidden;
  color: var(--v2-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.catalog-details-toggle {
  min-height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.85rem;
  color: var(--v2-ink);
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 750;
}

.catalog-details-panel {
  display: grid;
  gap: 1rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--v2-border);
}

.catalog-import-bar {
  padding: 0.85rem 1.5rem;
  border-top: 1px solid var(--v2-border);
  background: rgba(0, 150, 63, 0.045);
}

.catalog-import-bar > div {
  gap: 0.75rem;
}

.catalog-import-bar > div > span {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 0.75rem;
}

.catalog-import-bar strong,
.catalog-import-bar small {
  display: block;
}

.catalog-health {
  display: grid;
  gap: 0.75rem;
}

.catalog-health-item {
  overflow: hidden;
  border: 1px solid var(--v2-border);
  border-radius: 0.9rem;
  background: var(--v2-surface);
}

.catalog-health-item > button {
  width: 100%;
  min-height: 4.4rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 1rem;
  color: var(--v2-ink);
  text-align: left;
  border: 0;
  background: transparent;
  font: inherit;
}

.catalog-health-item > button > span {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: var(--v2-soft);
}

.catalog-health-item strong,
.catalog-health-item small {
  display: block;
}

.catalog-health-item strong {
  font-size: 0.875rem;
}

.catalog-health-item small {
  margin-top: 0.1rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.catalog-health-item ul {
  display: grid;
  gap: 0.45rem;
  margin: 0;
  padding: 0 1.25rem 1rem 4.4rem;
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.masterdata-card {
  overflow: hidden;
}

.masterdata-heading {
  align-items: center;
  background: radial-gradient(circle at 100% 0, rgba(0, 150, 63, 0.12), transparent 24rem);
}

.masterdata-switch {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 0.35rem;
  padding: 0.3rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.85rem;
  background: var(--v2-progress-surface);
}

.masterdata-switch button {
  min-height: 2.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.75rem;
  color: var(--v2-muted);
  border: 0;
  border-radius: 0.65rem;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 750;
}

.masterdata-switch button.active {
  color: var(--v2-ink);
  background: var(--v2-surface);
  box-shadow: 0 0.35rem 1rem var(--v2-shadow);
}

.masterdata-switch b {
  min-width: 1.4rem;
  height: 1.4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--v2-primary-soft);
  color: var(--v2-primary);
  font-size: 0.75rem;
}

.masterdata-body {
  padding: 0 !important;
}

.entity-workspace {
  min-height: 36rem;
  display: grid;
  grid-template-columns: minmax(18rem, 22rem) minmax(0, 1fr);
  border-top: 1px solid var(--v2-border);
}

.entity-list {
  min-width: 0;
  border-right: 1px solid var(--v2-border);
  background: var(--v2-progress-surface);
}

.entity-list__header {
  min-height: 4.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--v2-border);
}

.entity-list__header strong,
.entity-list__header span {
  display: block;
}

.entity-list__header strong {
  font-size: 0.875rem;
}

.entity-list__header span {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.entity-list__items {
  max-height: 46rem;
  overflow-y: auto;
  padding: 0.65rem;
}

.entity-list-item {
  width: 100%;
  min-height: 4.5rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.35rem;
  padding: 0.65rem;
  color: var(--v2-ink);
  text-align: left;
  border: 1px solid transparent;
  border-radius: 0.8rem;
  background: transparent;
  font: inherit;
  transition: border-color 0.16s ease, background-color 0.16s ease, transform 0.16s ease;
}

.entity-list-item:hover {
  transform: translateX(0.12rem);
  background: var(--v2-soft);
}

.entity-list-item.active {
  border-color: rgba(0, 150, 63, 0.42);
  background: var(--v2-primary-soft);
}

.entity-list-item__icon {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: var(--v2-primary);
  background: var(--v2-surface);
}

.entity-list-item__copy {
  min-width: 0;
}

.entity-list-item__copy strong,
.entity-list-item__copy small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-list-item__copy strong {
  font-size: 0.875rem;
}

.entity-list-item__copy small,
.entity-list-item__meta {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.entity-list-item__meta {
  max-width: 7rem;
  text-align: right;
}

.entity-state {
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  color: #64d98e;
  background: rgba(61, 204, 118, 0.11);
  font-size: 0.75rem;
  font-weight: 750;
}

.entity-state.inactive {
  color: var(--v2-muted);
  background: var(--v2-soft);
}

.entity-empty {
  min-height: 15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1.5rem;
  color: var(--v2-muted);
  text-align: center;
}

.entity-empty strong {
  color: var(--v2-ink);
  font-size: 0.875rem;
}

.entity-empty span {
  max-width: 15rem;
  font-size: 0.75rem;
}

.entity-editor {
  min-width: 0;
  padding: 1.5rem;
}

.entity-editor--empty {
  min-height: 28rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  color: var(--v2-muted);
  text-align: center;
}

.entity-editor--empty > span {
  width: 4rem;
  height: 4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 1.15rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.entity-editor--empty h3 {
  margin: 0.35rem 0 0;
  color: var(--v2-ink);
}

.entity-editor--empty p {
  max-width: 28rem;
  font-size: 0.875rem;
}

.entity-editor__hero {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--v2-border);
}

.entity-editor__hero-icon {
  width: 3.5rem;
  height: 3.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.entity-editor__hero h3 {
  margin: 0.1rem 0;
  font-size: 1.25rem;
}

.entity-editor__hero p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.entity-editor__hero > .v-chip {
  max-width: 100%;
  height: auto;
  min-height: 2rem;
}

.entity-editor__hero ::v-deep .v-chip__content {
  white-space: normal;
}

.editor-section {
  margin-top: 1.25rem;
  padding: 1.25rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.editor-section--accent {
  border-color: rgba(0, 150, 63, 0.34);
  background: var(--v2-primary-soft);
}

.editor-section__heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.editor-section__heading > span {
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.65rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.editor-section__heading strong,
.editor-section__heading small {
  display: block;
}

.editor-section__heading strong {
  font-size: 0.875rem;
}

.editor-section__heading small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.editor-field-grid,
.editor-toggle-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 1rem;
}

.editor-field--wide {
  grid-column: 1 / -1;
}

.editor-toggle-row,
.editor-toggle-grid label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  background: var(--v2-surface);
}

.editor-toggle-grid label.disabled {
  opacity: 0.55;
}

.editor-toggle-row strong,
.editor-toggle-row span,
.editor-toggle-grid strong,
.editor-toggle-grid small {
  display: block;
}

.editor-toggle-row strong,
.editor-toggle-grid strong {
  font-size: 0.875rem;
}

.editor-toggle-row span,
.editor-toggle-grid small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.entity-editor__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.25rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.catalog-workspace-nav {
  position: sticky;
  top: 0.75rem;
  z-index: 5;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(15rem, 21rem);
  gap: 1rem;
  padding: 0.75rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: rgba(29, 33, 31, 0.96);
  box-shadow: 0 0.75rem 2rem rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(1rem);
}

.catalog-section-tabs {
  min-width: 0;
  display: flex;
  gap: 0.4rem;
  overflow-x: auto;
  scrollbar-width: thin;
}

.catalog-section-tabs button {
  min-height: 3rem;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.7rem;
  color: var(--v2-muted);
  border: 1px solid transparent;
  border-radius: 0.7rem;
  background: transparent;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  transition: color 0.18s ease, background-color 0.18s ease, border-color 0.18s ease;
}

.catalog-section-tabs button:hover,
.catalog-section-tabs button.active {
  color: var(--v2-ink);
  border-color: var(--v2-border);
  background: var(--v2-primary-soft);
}

.catalog-section-tabs button:focus-visible,
.catalog-details-toggle:focus-visible,
.catalog-health-item > button:focus-visible,
.masterdata-switch button:focus-visible,
.entity-list-item:focus-visible,
.type-choice-card:focus-visible,
.choice-card:focus-visible,
.onboarding-goal-grid button:focus-visible,
.onboarding-plan-card:focus-visible,
.onboarding-selected-user button:focus-visible,
.legal-disclosure summary:focus-visible,
.addon-card__main:focus-visible,
.recommendation-list button:focus-visible,
.creator-progress__step:focus-visible,
.creator-context-change:focus-visible {
  outline: 0.2rem solid rgba(0, 150, 63, 0.45);
  outline-offset: 0.15rem;
}

.catalog-details-toggle,
.catalog-health-item > button,
.masterdata-switch button,
.entity-list-item,
.catalog-section-tabs button,
.type-choice-card,
.choice-card,
.onboarding-goal-grid button,
.onboarding-plan-card,
.onboarding-selected-user button,
.addon-card__main,
.recommendation-list button,
.creator-progress__step,
.creator-context-change {
  cursor: pointer;
}

.catalog-section-tabs button.active {
  color: var(--v2-primary);
}

.catalog-section-tabs button b {
  min-width: 1.45rem;
  height: 1.45rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--v2-soft);
  font-size: 0.75rem;
}

.type-panel-header {
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(12rem, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding-right: 1rem;
}

.type-panel-header > div:nth-child(2) {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.type-panel-header strong {
  font-size: 1rem;
}

.type-panel-header small,
.rollout-types small,
.legal-copy__heading small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.type-icon {
  width: 2.6rem;
  height: 2.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: var(--v2-primary-soft);
}

.legal-heading,
.legal-profile-card__header,
.physio-selection__intro,
.legal-copy__heading {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.legal-heading {
  justify-content: space-between;
  margin: 0.5rem 0 1rem;
}

.legal-heading p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.legal-profile-list,
.condition-list {
  display: grid;
  gap: 0.85rem;
}

.legal-profile-card {
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.9rem;
  background: var(--v2-soft);
}

.legal-profile-card__header {
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) auto auto auto;
}

.condition-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.75rem;
  background: var(--v2-surface);
}

.rollout-types {
  min-height: 3.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.75rem;
  background: var(--v2-soft);
}

.rollout-types > span {
  display: block;
  margin-bottom: 0.5rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.panel-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding-right: 1rem;
}

.panel-header strong,
.rollout-header strong {
  font-size: 1rem;
}

.panel-header span,
.rollout-header span {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.nested-card {
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  background: var(--v2-soft);
}

.nested-card__heading {
  margin-bottom: 1rem;
}

.term-grid,
.addon-price-grid {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.term-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
  gap: 0.75rem;
  align-items: center;
}

.addon-price-row {
  display: grid;
  grid-template-columns: minmax(12rem, 1.4fr) minmax(9rem, 1fr) auto;
  gap: 0.75rem;
  align-items: center;
}

.addon-price-row--monthly {
  grid-template-columns: minmax(12rem, 1.4fr) minmax(9rem, 1fr) minmax(9rem, 1fr) auto;
}

.toggle-col {
  min-height: 4.5rem;
  display: flex;
  align-items: center;
}

.override-builder {
  display: grid;
  grid-template-columns: repeat(4, minmax(10rem, 1fr));
  gap: 0.75rem;
  align-items: center;
}

.override-table td,
.override-table th {
  font-size: 0.875rem !important;
}

.rollout-header {
  width: 100%;
  padding-right: 1rem;
}

.rollout-header div {
  display: flex;
  flex-direction: column;
}

.rollout-note {
  display: flex;
  align-items: center;
  padding: 0.85rem;
  background: var(--v2-rollout-soft);
  border-radius: 0.7rem;
}

.creator-shell {
  width: 100%;
}

.creator-launcher {
  display: grid;
  grid-template-columns: minmax(17rem, 0.8fr) minmax(30rem, 1.6fr);
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem;
  overflow: hidden;
  background: radial-gradient(circle at 0 0, rgba(0, 150, 63, 0.18), transparent 25rem), var(--v2-surface) !important;
}

.creator-launcher__intro,
.creator-launcher__intro > span {
  display: flex;
  align-items: center;
}

.creator-launcher__intro {
  gap: 1rem;
}

.creator-launcher__intro > span {
  width: 3.5rem;
  height: 3.5rem;
  flex: 0 0 auto;
  justify-content: center;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 1rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.creator-launcher__intro p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.creator-launcher__form {
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) minmax(16rem, 1.35fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: rgba(12, 15, 13, 0.28);
}

.creator-launcher__notice {
  grid-column: 1 / -1;
  margin: 0 !important;
}

.creator-card {
  height: min(56rem, calc(100vh - 9rem));
  height: clamp(40rem, calc(100dvh - 9rem), 56rem);
  min-height: 40rem;
  overflow: hidden;
}

.creator-app-layout {
  height: 100%;
  display: grid;
  grid-template-columns: 14rem minmax(28rem, 1fr) 17rem;
  align-items: stretch;
}

.creator-progress {
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 0.9rem;
  background: var(--v2-progress-surface);
  border-right: 1px solid var(--v2-border);
  overscroll-behavior: contain;
}

.creator-progress__brand,
.creator-live-summary__heading {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.creator-progress__brand {
  margin-bottom: 0.8rem;
  padding: 0.35rem;
}

.creator-progress__brand > span,
.creator-live-summary__heading > span {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.creator-progress__brand small,
.creator-progress__brand strong,
.creator-live-summary__heading small,
.creator-live-summary__heading strong {
  display: block;
}

.creator-progress__brand small,
.creator-live-summary__heading small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.creator-progress__brand strong,
.creator-live-summary__heading strong {
  font-size: 0.875rem;
}

.creator-progress__context {
  display: grid;
  gap: 0.2rem;
  margin: 0 0 0.7rem;
  padding: 0.7rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  background: var(--v2-surface-subtle);
}

.creator-progress__context > small,
.creator-progress__context > span {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.creator-progress__context > strong {
  overflow: hidden;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.creator-context-change {
  min-height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.5rem;
  padding: 0.45rem 0.65rem;
  color: var(--v2-primary);
  border: 1px solid rgba(0, 150, 63, 0.34);
  border-radius: 0.65rem;
  background: var(--v2-primary-soft);
  font: inherit;
  font-size: 0.75rem;
  font-weight: 750;
}

.creator-progress__meter {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0.35rem 0.55rem;
}

.creator-progress__meter > span {
  height: 0.35rem;
  overflow: hidden;
  border-radius: 999px;
  background: var(--v2-border);
}

.creator-progress__meter i {
  height: 100%;
  display: block;
  border-radius: inherit;
  background: linear-gradient(90deg, #00a947, #47cf7e);
  transition: width 0.3s ease;
}

.creator-progress__meter small {
  color: var(--v2-muted);
  font-size: 0.75rem;
  font-weight: 800;
}

.creator-progress__step {
  width: 100%;
  min-height: 3.35rem;
  padding: 0.45rem 0.55rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  color: var(--v2-muted);
  text-align: left;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.8rem;
  font: inherit;
  transition: color 0.16s ease, border-color 0.16s ease, background-color 0.16s ease;
}

.creator-progress__step > span {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--v2-step-border);
  border-radius: 0.7rem;
  font-size: 0.75rem;
  font-weight: 800;
}

.creator-progress__step small,
.creator-progress__step strong {
  display: block;
}

.creator-progress__step small {
  font-size: 0.75rem;
}

.creator-progress__step strong {
  color: inherit;
  font-size: 0.875rem;
}

.creator-progress__step.active,
.creator-progress__step.done {
  color: var(--v2-primary);
}

.creator-progress__step.active {
  border-color: rgba(0, 150, 63, 0.35);
  background: var(--v2-primary-soft);
}

.creator-progress__step.active > span,
.creator-progress__step.done > span {
  color: white;
  border-color: var(--v2-primary);
  background: var(--v2-primary);
}

.creator-progress__trust {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: auto;
  padding: 0.7rem;
  color: var(--v2-muted);
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  font-size: 0.75rem;
}

.creator-content {
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid var(--v2-border);
}

.creator-content__scroll {
  min-height: 0;
  flex: 1 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
  padding: clamp(1.15rem, 2.4vw, 2rem);
  scroll-behavior: smooth;
  overscroll-behavior: contain;
  scrollbar-color: rgba(0, 150, 63, 0.55) transparent;
  scrollbar-width: thin;
}

.creator-content__scroll::-webkit-scrollbar,
.creator-progress::-webkit-scrollbar,
.creator-live-summary::-webkit-scrollbar {
  width: 0.45rem;
}

.creator-content__scroll::-webkit-scrollbar-thumb,
.creator-progress::-webkit-scrollbar-thumb,
.creator-live-summary::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(0, 150, 63, 0.42);
}

.creator-stage-kicker {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--v2-border);
}

.creator-stage-kicker span,
.creator-stage-kicker b {
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.creator-stage-kicker span {
  color: var(--v2-muted);
}

.creator-stage-kicker b {
  color: var(--v2-primary);
}

.creator-live-summary {
  min-width: 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  padding: 1.1rem;
  background: var(--v2-progress-surface);
  overscroll-behavior: contain;
}

.creator-summary-location {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 1rem 0;
  padding: 0.65rem 0;
  color: var(--v2-muted);
  border-top: 1px solid var(--v2-border);
  border-bottom: 1px solid var(--v2-border);
  font-size: 0.75rem;
}

.creator-summary-list {
  display: grid;
  gap: 0.85rem;
}

.creator-summary-list > div {
  display: grid;
  gap: 0.15rem;
}

.creator-summary-list span,
.creator-summary-total span,
.creator-summary-total small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.creator-summary-list strong {
  overflow: hidden;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.creator-summary-total {
  display: grid;
  gap: 0.2rem;
  margin-top: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 0.85rem;
  background: var(--v2-primary-soft);
}

.creator-summary-total strong {
  color: var(--v2-primary);
  font-size: 1.35rem;
}

.creator-summary-placeholder {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 1.25rem;
  padding: 0.85rem;
  color: var(--v2-muted);
  border: 1px dashed var(--v2-border);
  border-radius: 0.8rem;
  font-size: 0.75rem;
}

.conversation-title {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.conversation-title .v-avatar {
  flex: 0 0 auto;
  box-shadow: 0 0.55rem 1.4rem rgba(0, 150, 63, 0.24);
}

.conversation-title h2 {
  font-size: clamp(1.25rem, 2.2vw, 1.65rem);
}

.onboarding-stage {
  width: 100%;
  max-width: 58rem;
  margin: 0 auto;
  animation: creator-stage-enter 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes creator-stage-enter {
  from {
    opacity: 0;
    transform: translateY(0.55rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.onboarding-stage__hero {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 1rem;
  padding: clamp(1rem, 2vw, 1.4rem);
  border: 1px solid rgba(0, 150, 63, 0.34);
  border-radius: 1.2rem;
  background: radial-gradient(circle at 0 0, rgba(71, 207, 126, 0.2), transparent 20rem), var(--v2-surface-subtle);
  box-shadow: 0 0.8rem 2.4rem var(--v2-shadow);
}

.onboarding-stage__hero > span {
  width: 3.6rem;
  height: 3.6rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
  color: white;
  background: linear-gradient(145deg, #00a947, #007b35);
  box-shadow: 0 0.65rem 1.5rem rgba(0, 150, 63, 0.28);
}

.onboarding-stage__hero small,
.onboarding-block-heading small {
  display: block;
  color: var(--v2-primary);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.onboarding-stage__hero h2 {
  margin: 0.15rem 0 0.25rem;
  font-size: clamp(1.35rem, 2.5vw, 1.9rem);
}

.onboarding-stage__hero p,
.onboarding-profile-card > p,
.onboarding-account-choice p,
.onboarding-empty p,
.creator-readiness p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.onboarding-promise-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65rem;
  margin: 0.8rem 0;
}

.onboarding-promise-row > span {
  min-height: 2.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.7rem;
  color: var(--v2-muted);
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  background: var(--v2-surface-subtle);
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
}

.onboarding-promise-row .v-icon {
  color: var(--v2-primary);
}

.onboarding-profile-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(15rem, 0.65fr);
  gap: 0.8rem;
}

.onboarding-profile-card,
.onboarding-goals {
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.onboarding-block-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 0.85rem;
}

.onboarding-block-heading > span {
  width: 2.65rem;
  height: 2.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.onboarding-block-heading strong {
  display: block;
  font-size: 1rem;
}

.onboarding-selected-user,
.onboarding-account-ready {
  min-height: 4.1rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem;
  border: 1px solid rgba(0, 150, 63, 0.38);
  border-radius: 0.85rem;
  background: var(--v2-primary-soft);
}

.onboarding-selected-user > span {
  width: 2.7rem;
  height: 2.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  color: white;
  background: linear-gradient(145deg, #00a947, #007b35);
  font-size: 0.875rem;
  font-weight: 850;
}

.onboarding-selected-user strong,
.onboarding-selected-user small,
.onboarding-account-ready strong,
.onboarding-account-ready small {
  display: block;
}

.onboarding-selected-user small,
.onboarding-account-ready small,
.onboarding-account-choice > small {
  overflow: hidden;
  color: var(--v2-muted);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.onboarding-selected-user button {
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 0.75rem;
  background: var(--v2-surface);
}

.onboarding-account-ready {
  grid-template-columns: auto minmax(0, 1fr);
}

.onboarding-account-choice {
  display: grid;
  gap: 0.65rem;
}

.onboarding-account-choice > div {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.onboarding-goals {
  margin-top: 0.8rem;
}

.onboarding-goal-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.55rem;
}

.onboarding-goal-grid button {
  min-height: 3rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 0.7rem;
  color: var(--v2-ink);
  border: 1px solid var(--v2-border);
  border-radius: 0.75rem;
  background: var(--v2-surface);
  font: inherit;
  font-size: 0.875rem;
  text-align: left;
  transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}

.onboarding-goal-grid button.selected {
  color: var(--v2-primary);
  border-color: rgba(0, 150, 63, 0.6);
  background: var(--v2-primary-soft);
  box-shadow: 0 0.4rem 1.2rem rgba(0, 150, 63, 0.12);
}

.onboarding-plan-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.onboarding-plan-card {
  min-height: 10rem;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 0.65rem;
  padding: 1rem;
  color: var(--v2-ink);
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: linear-gradient(145deg, var(--v2-surface), var(--v2-surface-subtle));
  font: inherit;
  text-align: left;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.onboarding-plan-card:hover {
  transform: translateY(-0.15rem);
}

.onboarding-plan-card:hover,
.onboarding-plan-card.selected {
  border-color: rgba(0, 150, 63, 0.7);
  box-shadow: 0 0.75rem 2rem rgba(0, 150, 63, 0.14);
}

.onboarding-plan-card.selected {
  background: radial-gradient(circle at 100% 0, rgba(71, 207, 126, 0.2), transparent 14rem), var(--v2-selected-soft);
}

.onboarding-plan-card__top,
.onboarding-plan-card__price {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.onboarding-plan-card__top > span {
  width: 2.7rem;
  height: 2.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.onboarding-plan-card strong {
  font-size: 1.05rem;
}

.onboarding-plan-card p {
  margin-top: 0.2rem;
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.onboarding-plan-card__price {
  align-items: baseline;
  justify-content: flex-end;
  padding-top: 0.6rem;
  border-top: 1px solid var(--v2-border);
}

.onboarding-plan-card__price small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.onboarding-plan-card__price strong {
  color: var(--v2-primary);
  font-size: 1.2rem;
}

.onboarding-empty {
  min-height: 13rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border: 1px dashed var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
  text-align: center;
}

.onboarding-empty > span {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.9rem;
  color: var(--v2-primary);
  background: var(--v2-primary-soft);
}

.onboarding-empty--compact {
  min-height: 0;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 1rem;
  padding: 1rem;
  text-align: left;
}

.choice-title {
  margin: 1.25rem 0 0.8rem;
}

.choice-title--first {
  margin-top: 0;
}

.type-choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
  gap: 0.8rem;
}

.type-choice-card {
  min-height: 8.5rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  color: var(--v2-ink);
  text-align: left;
  border: 2px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface);
  font: inherit;
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.type-choice-card:hover {
  transform: translateY(-0.15rem);
}

.type-choice-card:hover,
.type-choice-card.selected {
  border-color: var(--v2-primary);
  box-shadow: 0 0.6rem 1.7rem rgba(0, 150, 63, 0.13);
}

.type-choice-card > span {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.85rem;
  background: var(--v2-primary-soft);
}

.type-choice-card strong {
  display: block;
  margin-bottom: 0.2rem;
  font-size: 1rem;
}

.type-choice-card p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.type-choice-card__check {
  color: var(--v2-primary) !important;
}

.choice-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1rem;
}

.choice-grid--compact {
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
}

.choice-card {
  min-height: 12rem;
  padding: 1.15rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.55rem;
  text-align: left;
  border: 2px solid var(--v2-border);
  border-radius: 0.9rem;
  background: var(--v2-surface);
  color: var(--v2-ink);
  font: inherit;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.choice-card:hover,
.choice-card.selected {
  border-color: var(--v2-primary);
  box-shadow: 0 0.5rem 1.5rem rgba(0, 150, 63, 0.12);
}

.choice-card--compact {
  min-height: 8rem;
}

.choice-card__icon {
  width: 2.7rem;
  height: 2.7rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: var(--v2-primary-soft);
}

.choice-card strong,
.addon-card strong {
  font-size: 1rem;
}

.choice-card small,
.recommendation-list small,
.price-summary small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.old-price {
  color: var(--v2-muted);
  text-decoration: line-through;
}

.addon-grid {
  display: grid;
  gap: 0.75rem;
}

.addon-card {
  padding: 0.8rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
}

.addon-card.selected {
  border-color: var(--v2-primary);
  background: var(--v2-selected-soft);
}

.addon-card__main {
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 0.9rem;
  text-align: left;
  border: 0;
  background: transparent;
  color: var(--v2-ink);
  font: inherit;
}

.addon-card__check {
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--v2-primary);
  border: 1px solid var(--v2-primary);
  border-radius: 50%;
}

.recommendation-box {
  margin: 1.25rem 0;
  padding: 1rem;
  border-radius: 0.85rem;
  background: var(--v2-info-soft);
}

.recommendation-list {
  display: grid;
  gap: 0.55rem;
  margin-top: 0.8rem;
}

.recommendation-list button {
  padding: 0.75rem;
  display: grid;
  grid-template-columns: 1fr auto;
  text-align: left;
  border: 1px solid var(--v2-info-border);
  border-radius: 0.65rem;
  background: var(--v2-surface);
  color: var(--v2-ink);
  font: inherit;
}

.recommendation-list small {
  grid-column: 1;
}

.recommendation-list b {
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: center;
}

.price-summary {
  margin-top: 1.25rem;
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.8rem;
  background: var(--v2-surface);
}

.price-summary > div,
.final-total {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.45rem 0;
  font-size: 0.875rem;
}

.price-summary .discount {
  color: var(--v2-primary);
}

.price-summary__total,
.final-total {
  margin-top: 0.5rem;
  padding-top: 0.9rem !important;
  border-top: 1px solid var(--v2-border);
  font-size: 1.1rem !important;
}

.contract-copy {
  max-height: 16rem;
  overflow-y: auto;
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.75rem;
  background: var(--v2-surface-subtle);
  font-size: 0.875rem;
}

.physio-selection {
  padding: 1rem;
  border: 1px solid var(--v2-info-border);
  border-radius: 0.9rem;
  background: var(--v2-info-soft);
}

.physio-selection__intro {
  margin-bottom: 1rem;
}

.physio-selection__intro > span {
  width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: var(--v2-surface);
}

.physio-selection__intro p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.legal-copy {
  max-height: 24rem;
  padding: 1.15rem;
}

.legal-disclosure {
  overflow: hidden;
  margin-bottom: 0.85rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.legal-disclosure summary {
  min-height: 4.8rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.8rem;
  padding: 0.85rem 1rem;
  cursor: pointer;
  list-style: none;
}

.legal-disclosure summary::-webkit-details-marker {
  display: none;
}

.legal-disclosure summary > span {
  width: 2.8rem;
  height: 2.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  background: var(--v2-primary-soft);
}

.legal-disclosure summary small,
.legal-disclosure summary strong,
.legal-disclosure summary p {
  display: block;
}

.legal-disclosure summary small {
  color: var(--v2-primary);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.legal-disclosure summary strong {
  font-size: 1rem;
}

.legal-disclosure summary p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.legal-disclosure__chevron {
  transition: transform 0.2s ease;
}

.legal-disclosure[open] .legal-disclosure__chevron {
  transform: rotate(180deg);
}

.legal-disclosure .legal-copy {
  max-height: 16rem;
  margin: 0 0.85rem 0.85rem;
  border-radius: 0.75rem;
  background: var(--v2-surface);
}

.legal-copy__heading {
  padding-bottom: 0.85rem;
  border-bottom: 1px solid var(--v2-border);
}

.legal-copy__heading > div {
  display: flex;
  flex-direction: column;
}

.legal-copy__preamble,
.legal-copy__confirmation,
.legal-copy li {
  white-space: pre-line;
  font-size: 0.875rem;
}

.legal-copy__preamble {
  margin-top: 1rem;
}

.legal-copy ol {
  display: grid;
  gap: 0.65rem;
  margin: 1rem 0;
  padding-left: 1.4rem;
}

.legal-copy li.highlight {
  padding: 0.65rem;
  border-left: 0.2rem solid var(--v2-primary);
  border-radius: 0.35rem;
  background: var(--v2-primary-soft);
}

.legal-copy__confirmation {
  padding-top: 0.85rem;
  border-top: 1px solid var(--v2-border);
  font-weight: 700;
}

.signature-box {
  margin-top: 1rem;
  padding: 1rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.creator-readiness {
  min-height: 4.5rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(255, 183, 77, 0.36);
  border-radius: 0.9rem;
  background: rgba(255, 183, 77, 0.08);
}

.creator-readiness > span {
  width: 2.75rem;
  height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  color: #ffb74d;
  background: rgba(255, 183, 77, 0.14);
}

.creator-readiness strong,
.creator-readiness p {
  display: block;
}

.creator-readiness.ready {
  border-color: rgba(0, 150, 63, 0.42);
  background: var(--v2-primary-soft);
}

.creator-readiness.ready > span {
  color: var(--v2-primary);
  background: rgba(0, 150, 63, 0.16);
}

.creator-readiness--finish {
  margin-top: 1rem;
}

.creator-fixed-customer--compact {
  min-height: 0;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
}

.creator-fixed-customer--compact > div {
  min-width: 0;
  flex: 1;
}

.creator-actions {
  min-height: 5.25rem;
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: auto minmax(8rem, 1fr) auto;
  align-items: center;
  gap: 0.8rem;
  margin: 0;
  padding: 0.8rem 1.1rem;
  border-top: 1px solid var(--v2-border);
  background: var(--v2-surface);
  box-shadow: 0 -0.65rem 1.6rem rgba(0, 0, 0, 0.06);
  backdrop-filter: blur(0.8rem);
}

.creator-action-hint {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
  text-align: center;
}

.creator-action-hint span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.creator-action-hint .v-icon {
  color: var(--v2-primary);
}

.creator-actions__buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
}

.creator-actions .v-btn {
  min-height: 2.9rem;
  border-radius: 0.8rem;
  letter-spacing: 0.01em;
  text-transform: none;
}

.creator-actions__buttons .v-btn:last-child {
  box-shadow: 0 0.55rem 1.35rem rgba(0, 150, 63, 0.22);
}

.creator-action-label--short {
  display: none;
}

.success-card {
  max-width: 48rem;
  margin: 2rem auto;
}

.contracts-table,
.tac-table {
  font-size: 0.875rem;
}

.table-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

.dialog-title div {
  display: flex;
  flex-direction: column;
}

.dialog-total {
  font-size: 1.25rem;
  font-weight: 800;
}

.contract-dialog-card {
  --v2-primary: #00963f;
  --v2-ink: #f2f6f3;
  --v2-muted: #a7b4ac;
  --v2-border: rgba(255, 255, 255, 0.12);
  --v2-surface: #1d211f;
  color: var(--v2-ink) !important;
  background-color: var(--v2-surface) !important;
}

.creator-draft-alert ::v-deep .v-alert__content > div {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.creator-draft-alert strong,
.creator-fixed-customer strong {
  font-size: 1rem;
}

.creator-draft-alert span,
.creator-fixed-customer p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.creator-fixed-customer {
  min-height: 5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 0.9rem;
  background: var(--v2-primary-soft);
}

.creator-fixed-customer > span {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.85rem;
  background: rgba(0, 150, 63, 0.16);
}

.creator-fixed-customer small,
.creator-fixed-customer strong,
.creator-fixed-customer p {
  display: block;
}

.creator-fixed-customer small {
  color: var(--v2-primary);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.contract-management__command {
  overflow: hidden;
}

.contract-management__hero {
  min-height: 7.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 1.5rem;
  border-bottom: 1px solid var(--v2-border);
  background: linear-gradient(130deg, rgba(0, 150, 63, 0.12), transparent 58%);
}

.contract-management__identity,
.contract-management__actions,
.contract-list-card__meta,
.contract-status-cell,
.contract-studio-cell {
  display: flex;
  align-items: center;
}

.contract-management__identity {
  gap: 1rem;
}

.contract-management__identity > span {
  width: 3.75rem;
  height: 3.75rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 1.1rem;
  background: rgba(0, 150, 63, 0.14);
}

.contract-management__identity > span .v-icon {
  color: #35c56f;
  font-size: 1.75rem;
}

.contract-management__identity h2 {
  margin: 0.15rem 0;
  font-size: 1.65rem;
}

.contract-management__identity p,
.contract-comment-composer p,
.contract-detail-section__heading p,
.contract-edit-gate p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-management__actions {
  flex: 0 0 auto;
  gap: 0.75rem;
}

.contract-status-rail {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.65rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--v2-border);
}

.contract-status-rail button {
  min-width: 0;
  min-height: 4.75rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.9rem;
  color: var(--v2-ink);
  background: var(--v2-surface-subtle);
  text-align: left;
  transition: border-color 160ms ease, background 160ms ease, transform 160ms ease;
}

.contract-status-rail button:hover,
.contract-status-rail button.active {
  border-color: rgba(0, 150, 63, 0.6);
  background: var(--v2-primary-soft);
}

.contract-status-rail button:hover {
  transform: translateY(-0.1rem);
}

.contract-status-rail button small,
.contract-status-rail button strong {
  display: block;
}

.contract-status-rail button small {
  overflow: hidden;
  color: var(--v2-muted);
  font-size: 0.75rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contract-status-rail button strong {
  margin-top: 0.1rem;
  font-size: 1.25rem;
  line-height: 1;
}

.contract-status-rail__icon {
  width: 2.35rem;
  height: 2.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
}

.contract-status-rail__icon .v-icon {
  font-size: 1.25rem;
}

.contract-status-rail__icon--warning,
.contract-status-rail__icon--amber {
  background: rgba(255, 179, 0, 0.14);
}

.contract-status-rail__icon--warning .v-icon,
.contract-status-rail__icon--amber .v-icon {
  color: #ffca4b;
}

.contract-status-rail__icon--error {
  background: rgba(255, 82, 82, 0.13);
}

.contract-status-rail__icon--error .v-icon {
  color: #ff7777;
}

.contract-status-rail__icon--success,
.contract-status-rail__icon--primary {
  background: rgba(0, 150, 63, 0.16);
}

.contract-status-rail__icon--success .v-icon,
.contract-status-rail__icon--primary .v-icon {
  color: #35c56f;
}

.contract-status-rail__icon--info {
  background: rgba(33, 150, 243, 0.14);
}

.contract-status-rail__icon--info .v-icon {
  color: #64b5f6;
}

.contract-status-rail__arrow {
  color: var(--v2-muted) !important;
  font-size: 1rem !important;
}

.contract-filter-bar {
  display: grid;
  grid-template-columns: minmax(16rem, 2fr) minmax(12rem, 1fr) minmax(14rem, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
}

.contract-filter-bar__submit {
  min-height: 3.5rem;
}

.contract-list-card {
  overflow: hidden;
}

.contract-list-card__header {
  min-height: 5.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--v2-border);
}

.contract-list-card__header h3 {
  margin: 0.15rem 0 0;
  font-size: 1.2rem;
}

.contract-list-card__meta {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.6rem;
}

.contract-list-card__meta span,
.contract-list-card__meta button {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--v2-border);
  border-radius: 999px;
  color: var(--v2-muted);
  background: transparent;
  font-size: 0.75rem;
}

.contract-list-card__meta button:hover,
.contract-list-card__meta button.active {
  border-color: rgba(255, 82, 82, 0.35);
  color: #ff7777;
  background: rgba(255, 82, 82, 0.08);
}

.contract-table-shell ::v-deep .v-data-table-header th {
  height: 3.5rem;
  color: var(--v2-muted) !important;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.contract-table-shell ::v-deep tbody tr {
  cursor: pointer;
  transition: background 150ms ease;
}

.contract-table-shell ::v-deep tbody tr:hover {
  background: rgba(0, 150, 63, 0.06) !important;
}

.contract-table-shell ::v-deep tbody td {
  height: 5.25rem;
  border-color: var(--v2-border) !important;
  font-size: 0.875rem;
}

.contract-number-cell,
.contract-customer-cell > div,
.contract-mobile-card__customer {
  display: flex;
  flex-direction: column;
}

.contract-number-cell strong {
  color: #47cf7e;
  font-size: 0.875rem;
}

.contract-number-cell span,
.contract-customer-cell small,
.contract-mobile-card small,
.contract-fact-grid small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-customer-cell {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.contract-customer-cell > span {
  width: 2.25rem;
  height: 2.25rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.7rem;
  color: #47cf7e;
  background: rgba(0, 150, 63, 0.14);
  font-size: 0.75rem;
  font-weight: 850;
}

.contract-studio-cell,
.contract-status-cell {
  gap: 0.35rem;
}

.contract-studio-cell .v-icon {
  color: var(--v2-muted);
}

.contract-status-cell > span {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-table-total {
  white-space: nowrap;
}

.contract-empty-state {
  min-height: 15rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: var(--v2-muted);
  text-align: center;
}

.contract-empty-state > span {
  width: 4rem;
  height: 4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border-radius: 1.2rem;
  background: var(--v2-primary-soft);
}

.contract-empty-state > span .v-icon {
  color: #35c56f;
  font-size: 2rem;
}

.contract-empty-state h3 {
  margin-bottom: 0.25rem;
  color: var(--v2-ink);
  font-size: 1.1rem;
}

.contract-empty-state p {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-mobile-list {
  display: none;
}

.contract-pagination {
  min-height: 4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1.5rem;
  border-top: 1px solid var(--v2-border);
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-workspace {
  max-height: 92vh;
  overflow: hidden;
}

.contract-workspace__header {
  min-height: 5.5rem;
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--v2-border);
  background: #191d1b;
}

.contract-workspace__title,
.contract-workspace__header-meta,
.contract-workspace__title > div,
.contract-workspace__nav,
.contract-lock-banner,
.contract-overview__lead,
.contract-customer-hero,
.contract-detail-section__heading,
.contract-history-column__heading,
.contract-edit-gate,
.contract-edit-panel__actions,
.contract-workspace__footer,
.contract-workspace__footer > div,
.contract-comment-composer__actions {
  display: flex;
  align-items: center;
}

.contract-workspace__title {
  min-width: 0;
  gap: 0.85rem;
}

.contract-workspace__title > span {
  width: 3.25rem;
  height: 3.25rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 0.95rem;
  background: rgba(0, 150, 63, 0.14);
}

.contract-workspace__title > span .v-icon {
  color: #47cf7e;
}

.contract-workspace__title > div {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
}

.contract-workspace__title small,
.contract-workspace__title p {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-workspace__title strong {
  font-size: 1.15rem;
}

.contract-workspace__header-meta {
  flex: 0 0 auto;
  gap: 0.45rem;
}

.contract-lock-banner {
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  border-bottom: 1px solid var(--v2-border);
  font-size: 0.875rem;
}

.contract-lock-banner > div {
  min-width: 0;
  display: flex;
  flex: 1;
  flex-direction: column;
}

.contract-lock-banner span {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-lock-banner--warning {
  background: rgba(255, 179, 0, 0.08);
}

.contract-lock-banner--active {
  background: rgba(0, 150, 63, 0.08);
}

.contract-workspace__nav {
  min-height: 4rem;
  flex: 0 0 auto;
  gap: 0.4rem;
  padding: 0.6rem 1.25rem;
  border-bottom: 1px solid var(--v2-border);
  background: var(--v2-surface-subtle);
}

.contract-workspace__nav button {
  min-height: 2.7rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.85rem;
  border: 1px solid transparent;
  border-radius: 0.75rem;
  color: var(--v2-muted);
  font-size: 0.875rem;
  font-weight: 750;
}

.contract-workspace__nav button:hover,
.contract-workspace__nav button.active {
  color: #f2f6f3;
  border-color: var(--v2-border);
  background: rgba(255, 255, 255, 0.06);
}

.contract-workspace__nav button.active .v-icon {
  color: #47cf7e;
}

.contract-workspace__nav button > span {
  min-width: 1.35rem;
  height: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: #47cf7e;
  background: rgba(0, 150, 63, 0.15);
  font-size: 0.75rem;
}

.contract-workspace__body {
  padding: 1.5rem !important;
}

.contract-overview,
.contract-history,
.contract-edit-panel {
  display: grid;
  gap: 1.25rem;
}

.contract-overview__lead {
  align-items: stretch;
  gap: 1rem;
}

.contract-customer-hero,
.contract-total-hero {
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.contract-customer-hero {
  min-width: 0;
  flex: 1;
  gap: 1rem;
  padding: 1.15rem;
}

.contract-customer-hero > span {
  width: 3.5rem;
  height: 3.5rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 1rem;
  color: #47cf7e;
  background: rgba(0, 150, 63, 0.15);
  font-size: 1rem;
  font-weight: 850;
}

.contract-customer-hero small,
.contract-total-hero small {
  color: #47cf7e;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.contract-customer-hero h3 {
  margin: 0.15rem 0;
  font-size: 1.2rem;
}

.contract-customer-hero p {
  overflow-wrap: anywhere;
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-total-hero {
  min-width: 16rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 1.15rem;
}

.contract-total-hero strong {
  margin: 0.2rem 0;
  color: #47cf7e;
  font-size: 1.75rem;
  line-height: 1.2;
}

.contract-total-hero span {
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-fact-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.contract-fact-grid > div {
  min-height: 6rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.9rem;
  border: 1px solid var(--v2-border);
  border-radius: 0.85rem;
  background: var(--v2-surface-subtle);
}

.contract-fact-grid > div > span {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.3rem;
  color: var(--v2-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.contract-fact-grid strong {
  overflow-wrap: anywhere;
  font-size: 0.875rem;
}

.contract-detail-section,
.contract-comment-composer,
.contract-history-column,
.contract-edit-panel {
  padding: 1.15rem;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.contract-detail-section__heading,
.contract-history-column__heading {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.contract-detail-section__heading h3 {
  margin: 0.1rem 0;
  font-size: 1.15rem;
}

.contract-detail-section__heading > span,
.contract-history-column__heading > span {
  width: 2.75rem;
  height: 2.75rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.85rem;
  background: rgba(0, 150, 63, 0.13);
}

.contract-detail-section__heading > span .v-icon,
.contract-history-column__heading > span .v-icon {
  color: #47cf7e;
}

.contract-offer-list > div {
  min-height: 3.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid var(--v2-border);
}

.contract-offer-list > div > span {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.contract-offer-list strong,
.contract-offer-list b {
  font-size: 0.875rem;
}

.contract-offer-list small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-offer-list .discount b {
  color: #47cf7e;
}

.contract-offer-list__total {
  border-bottom: 0 !important;
}

.contract-offer-list__total span,
.contract-offer-list__total strong {
  color: #47cf7e;
  font-size: 1.15rem;
  font-weight: 850;
}

.contract-legal-details {
  overflow: hidden;
  border: 1px solid var(--v2-border);
  border-radius: 1rem;
  background: var(--v2-surface-subtle);
}

.contract-legal-details summary {
  min-height: 4.75rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 0.9rem 1.15rem;
  cursor: pointer;
  list-style: none;
}

.contract-legal-details summary::-webkit-details-marker {
  display: none;
}

.contract-legal-details summary > span {
  width: 2.75rem;
  height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  background: rgba(0, 150, 63, 0.13);
}

.contract-legal-details summary > span .v-icon {
  color: #47cf7e;
}

.contract-legal-details summary strong,
.contract-legal-details summary small {
  display: block;
}

.contract-legal-details summary small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-legal-details[open] summary > .v-icon {
  transform: rotate(180deg);
}

.contract-legal-details > div {
  padding: 0 1.25rem 1.25rem;
  color: #cbd5cf;
  font-size: 0.875rem;
}

.contract-legal-details li + li {
  margin-top: 0.55rem;
}

.contract-comment-composer__actions,
.contract-edit-panel__actions {
  justify-content: flex-end;
  gap: 0.75rem;
}

.contract-history-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.contract-history-column__heading {
  justify-content: flex-start;
}

.contract-history-column__heading > div {
  display: flex;
  flex-direction: column;
}

.contract-history-column__heading small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-timeline {
  display: grid;
  gap: 0;
}

.contract-timeline article {
  min-width: 0;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.8rem;
}

.contract-timeline article > span {
  position: relative;
  width: 0.75rem;
  min-height: 5rem;
}

.contract-timeline article > span::before {
  content: "";
  position: absolute;
  top: 0.35rem;
  left: 0.15rem;
  width: 0.45rem;
  height: 0.45rem;
  border: 0.12rem solid #47cf7e;
  border-radius: 999px;
  background: var(--v2-surface-subtle);
}

.contract-timeline article > span::after {
  content: "";
  position: absolute;
  top: 1.05rem;
  bottom: 0;
  left: 0.35rem;
  width: 1px;
  background: var(--v2-border);
}

.contract-timeline article:last-child > span::after {
  display: none;
}

.contract-timeline article > div {
  min-width: 0;
  padding-bottom: 1.15rem;
}

.contract-timeline article > div > div {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.6rem;
}

.contract-timeline time,
.contract-timeline article small {
  color: var(--v2-muted);
  font-size: 0.75rem;
}

.contract-timeline p {
  margin: 0.45rem 0 0.2rem;
  overflow-wrap: anywhere;
  color: #dfe7e2;
  font-size: 0.875rem;
}

.contract-timeline .v-chip {
  max-width: 100%;
  margin-top: 0.45rem;
}

.contract-history-empty {
  min-height: 10rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--v2-muted);
  text-align: center;
}

.contract-history-empty p {
  margin-top: 0.5rem;
  font-size: 0.875rem;
}

.contract-edit-gate {
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(0, 150, 63, 0.28);
  border-radius: 0.9rem;
  background: rgba(0, 150, 63, 0.07);
}

.contract-edit-gate > span {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: 0.85rem;
  background: rgba(0, 150, 63, 0.15);
}

.contract-edit-gate > span .v-icon {
  color: #47cf7e;
}

.contract-edit-gate > div {
  min-width: 0;
  flex: 1;
}

.contract-workspace__footer {
  min-height: 5rem;
  flex: 0 0 auto;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--v2-border);
  background: #191d1b;
}

.contract-workspace__footer > div {
  flex-wrap: wrap;
  gap: 0.5rem;
}

.contract-delete-card {
  padding: 1.5rem;
  border: 1px solid rgba(255, 82, 82, 0.25);
  border-radius: 1rem !important;
  background: #1d211f !important;
  text-align: center;
}

.contract-delete-card__icon {
  width: 4rem;
  height: 4rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border-radius: 1.2rem;
  background: rgba(255, 82, 82, 0.1);
}

.contract-delete-card__icon .v-icon {
  font-size: 2rem;
}

.contract-delete-card p {
  margin: 0.5rem 0 1.5rem;
  color: var(--v2-muted);
  font-size: 0.875rem;
}

.contract-delete-card > div:last-child {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.reset-import-card {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem !important;
  background: #1d211f !important;
}

.reset-import-card__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
}

.reset-import-card__header h2 {
  margin: 0.2rem 0 0;
  color: #f2f6f3;
  font-size: 1.25rem;
}

.reset-import-card__icon {
  width: 3rem;
  height: 3rem;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 82, 82, 0.35);
  border-radius: 0.9rem;
  background: rgba(255, 82, 82, 0.1);
}

.reset-import-card__content {
  padding: 0 1.5rem 1.25rem !important;
  color: #c7d1cb !important;
  font-size: 1rem;
  line-height: 1.6;
}

.reset-import-card__safe {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(0, 150, 63, 0.35);
  border-radius: 0.8rem;
  background: rgba(0, 150, 63, 0.1);
  color: #dce8e0;
  font-size: 0.875rem;
}

.reset-import-card__actions {
  gap: 0.75rem;
  justify-content: flex-end;
  padding: 1rem 1.5rem 1.5rem;
}

@media (max-width: 1279px) {
  .contract-status-rail {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .contract-fact-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .creator-launcher {
    grid-template-columns: 1fr;
  }

  .creator-app-layout {
    grid-template-columns: 12.5rem minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
  }

  .creator-progress {
    grid-row: 1 / span 2;
  }

  .creator-content {
    grid-column: 2;
    grid-row: 1;
  }

  .creator-live-summary {
    grid-column: 2;
    grid-row: 2;
    height: auto;
    display: grid;
    grid-template-columns: minmax(12rem, 1fr) minmax(0, 2fr) minmax(12rem, 1fr);
    align-items: center;
    gap: 1rem;
    overflow: hidden;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--v2-border);
  }

  .creator-summary-location {
    display: none;
  }

  .creator-summary-list {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .creator-summary-total,
  .creator-summary-placeholder {
    margin-top: 0;
  }
}

@media (max-width: 959px) {
  .creator-card {
    min-height: 36rem;
  }

  .contract-management__hero,
  .contract-list-card__header {
    align-items: stretch;
    flex-direction: column;
  }

  .contract-management__actions {
    align-self: flex-start;
  }

  .contract-filter-bar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .contract-filter-bar__submit {
    width: 100%;
  }

  .contract-list-card__meta {
    justify-content: flex-start;
  }

  .contract-table-shell {
    display: none;
  }

  .contract-mobile-list {
    display: grid;
    gap: 0.75rem;
    padding: 1rem;
  }

  .contract-mobile-card {
    width: 100%;
    display: grid;
    gap: 0.7rem;
    padding: 1rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.95rem;
    color: var(--v2-ink);
    background: var(--v2-surface-subtle);
    text-align: left;
  }

  .contract-mobile-card__top,
  .contract-mobile-card__facts,
  .contract-mobile-card__bottom {
    display: flex;
    align-items: center;
  }

  .contract-mobile-card__top,
  .contract-mobile-card__bottom {
    justify-content: space-between;
    gap: 0.75rem;
  }

  .contract-mobile-card__top > span {
    color: #47cf7e;
    font-size: 0.875rem;
    font-weight: 800;
  }

  .contract-mobile-card__customer strong {
    font-size: 1rem;
  }

  .contract-mobile-card__facts {
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }

  .contract-mobile-card__facts span {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: var(--v2-muted);
    font-size: 0.75rem;
  }

  .contract-mobile-card__bottom {
    padding-top: 0.7rem;
    border-top: 1px solid var(--v2-border);
    font-size: 0.875rem;
  }

  .contract-mobile-card__bottom > span {
    min-width: 0;
    flex: 1;
  }

  .contract-mobile-card__bottom strong {
    white-space: nowrap;
  }

  .contract-overview__lead {
    flex-direction: column;
  }

  .contract-total-hero {
    min-width: 0;
  }

  .contract-history-grid {
    grid-template-columns: 1fr;
  }

  .contract-workspace__footer {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .contract-workspace__footer > div:last-child {
    justify-content: flex-end;
  }

  .page-heading,
  .section-heading,
  .card-title-row {
    align-items: stretch;
    flex-direction: column;
  }

  .button-row--end {
    justify-content: flex-start;
  }

  .term-row,
  .addon-price-row,
  .override-builder {
    grid-template-columns: 1fr 1fr;
  }

  .catalog-workspace-nav {
    position: static;
    grid-template-columns: 1fr;
  }

  .catalog-command-card__top,
  .catalog-command-bar,
  .catalog-import-bar {
    align-items: stretch;
    flex-direction: column;
  }

  .catalog-command-card__state {
    align-items: flex-start;
  }

  .catalog-version-select {
    width: 100%;
    max-width: none;
  }

  .catalog-command-actions {
    flex-wrap: wrap;
  }

  .catalog-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .catalog-details-toggle {
    min-height: 3rem;
    grid-column: 1 / -1;
  }

  .masterdata-switch {
    align-self: flex-start;
  }

  .entity-workspace {
    grid-template-columns: 1fr;
  }

  .entity-list {
    border-right: 0;
    border-bottom: 1px solid var(--v2-border);
  }

  .entity-list__items {
    max-height: none;
    display: grid;
    grid-auto-columns: minmax(17rem, 44vw);
    grid-auto-flow: column;
    gap: 0.4rem;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .entity-list-item {
    margin-bottom: 0;
  }

  .creator-launcher__form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .creator-launcher__form > .v-btn {
    grid-column: 1 / -1;
  }

  .creator-app-layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto;
  }

  .creator-progress {
    grid-column: 1;
    grid-row: 1;
    min-height: auto;
    height: auto;
    display: flex;
    flex-direction: row;
    gap: 0.35rem;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.55rem;
    border-right: 0;
    border-bottom: 1px solid var(--v2-border);
    scroll-snap-type: inline proximity;
  }

  .creator-progress__brand,
  .creator-progress__context,
  .creator-progress__meter,
  .creator-progress__trust {
    display: none;
  }

  .creator-progress__step {
    min-width: 6.6rem;
    min-height: 3.8rem;
    flex: 0 0 6.6rem;
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.3rem;
    text-align: center;
    scroll-snap-align: start;
  }

  .creator-progress__step div small {
    display: none;
  }

  .creator-content {
    grid-column: 1;
    grid-row: 2;
    min-height: 0;
    height: auto;
    border-right: 0;
  }

  .creator-live-summary {
    grid-column: 1;
    grid-row: 3;
    grid-template-columns: minmax(0, 1fr) minmax(11rem, auto);
    align-items: center;
    gap: 0.75rem;
    padding: 0.65rem 0.9rem;
  }

  .creator-summary-list {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.55rem;
  }

  .creator-live-summary__heading {
    display: none;
  }

  .onboarding-goal-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .import-scope {
    grid-template-columns: 1fr;
  }

  .legal-profile-card__header {
    grid-template-columns: minmax(0, 1fr) auto;
  }
}

@media (max-width: 599px) {
  .contract-management__hero {
    padding: 1rem;
  }

  .contract-management__identity {
    align-items: flex-start;
  }

  .contract-management__identity > span {
    width: 3rem;
    height: 3rem;
  }

  .contract-management__identity h2 {
    font-size: 1.35rem;
  }

  .contract-management__actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .contract-management__actions .v-btn {
    flex: 1 1 auto;
  }

  .contract-status-rail {
    grid-auto-columns: minmax(11rem, 72vw);
    grid-auto-flow: column;
    grid-template-columns: none;
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    padding: 0.85rem 1rem;
    scroll-snap-type: inline proximity;
  }

  .contract-status-rail button {
    scroll-snap-align: start;
  }

  .contract-filter-bar {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .contract-list-card__header {
    padding: 1rem;
  }

  .contract-mobile-list {
    padding: 0.75rem;
  }

  .contract-mobile-card__bottom {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
  }

  .contract-pagination {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .contract-workspace {
    height: 100%;
    max-height: none;
    border-radius: 0 !important;
  }

  .contract-workspace__header {
    align-items: flex-start;
    padding: 0.85rem 1rem;
  }

  .contract-workspace__title > span {
    width: 2.75rem;
    height: 2.75rem;
  }

  .contract-workspace__header-meta {
    gap: 0.15rem;
  }

  .contract-workspace__header-meta > .v-chip:first-child,
  .contract-workspace__header-meta > .v-btn:nth-last-child(2) {
    display: none;
  }

  .contract-lock-banner {
    align-items: flex-start;
    flex-wrap: wrap;
    padding: 0.8rem 1rem;
  }

  .contract-lock-banner .v-btn {
    width: 100%;
  }

  .contract-workspace__nav {
    overflow-x: auto;
    padding: 0.5rem 0.75rem;
  }

  .contract-workspace__nav button {
    flex: 0 0 auto;
  }

  .contract-workspace__body {
    padding: 1rem !important;
  }

  .contract-customer-hero {
    align-items: flex-start;
  }

  .contract-fact-grid {
    grid-template-columns: 1fr;
  }

  .contract-detail-section,
  .contract-comment-composer,
  .contract-history-column,
  .contract-edit-panel {
    padding: 1rem;
  }

  .contract-comment-composer__actions,
  .contract-edit-panel__actions,
  .contract-edit-gate {
    align-items: stretch;
    flex-direction: column;
  }

  .contract-edit-gate > span {
    width: 2.75rem;
    height: 2.75rem;
  }

  .contract-comment-composer__actions .v-btn,
  .contract-edit-panel__actions .v-btn,
  .contract-edit-gate .v-btn {
    width: 100%;
  }

  .contract-workspace__footer {
    padding: 0.75rem;
  }

  .contract-workspace__footer > div,
  .contract-workspace__footer .v-btn {
    width: 100%;
  }

  .contract-workspace__footer .v-btn {
    margin: 0 !important;
  }

  .contract-delete-card > div:last-child {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .contract-delete-card > div:last-child .v-btn {
    width: 100%;
  }

  .contract-v2__container,
  .contract-v2--public .contract-v2__container {
    padding: 1rem 0.75rem;
  }

  .public-header__brand span {
    font-size: 1rem;
  }

  .catalog-command-card__top,
  .catalog-command-bar,
  .catalog-kpis,
  .catalog-details-panel,
  .catalog-import-bar {
    padding-right: 1rem;
    padding-left: 1rem;
  }

  .catalog-command-card__identity {
    align-items: flex-start;
  }

  .catalog-command-card__identity > span {
    width: 2.75rem;
    height: 2.75rem;
  }

  .catalog-command-card__identity h2 {
    white-space: normal;
  }

  .catalog-command-actions .v-btn,
  .catalog-import-bar .button-row .v-btn {
    flex: 1 1 auto;
  }

  .catalog-kpis > div {
    grid-template-columns: auto 1fr;
  }

  .catalog-health-item > button {
    grid-template-columns: auto minmax(0, 1fr) auto;
  }

  .catalog-health-item ul {
    padding-left: 1.5rem;
  }

  .masterdata-switch {
    width: 100%;
    overflow-x: auto;
  }

  .masterdata-switch button {
    flex: 1 0 auto;
    justify-content: center;
  }

  .entity-list__items {
    grid-auto-columns: minmax(16rem, 86vw);
  }

  .entity-editor {
    padding: 1rem;
  }

  .entity-editor__hero {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .entity-editor__hero > .v-chip,
  .entity-editor__hero > .v-input {
    grid-column: 1 / -1;
    justify-self: start;
  }

  .editor-field-grid,
  .editor-toggle-grid {
    grid-template-columns: 1fr;
  }

  .entity-editor__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .creator-launcher {
    padding: 1rem;
  }

  .creator-launcher__form {
    grid-template-columns: 1fr;
  }

  .creator-launcher__form > .v-btn {
    grid-column: 1;
  }

  .creator-card {
    height: calc(100vh - 7rem);
    height: calc(100dvh - 7rem);
    min-height: 0;
  }

  .creator-progress {
    padding: 0.45rem;
  }

  .creator-progress__step {
    min-width: 5.4rem;
    min-height: 3.5rem;
    flex-basis: 5.4rem;
    padding: 0.35rem;
  }

  .creator-progress__step > span {
    width: 1.8rem;
    height: 1.8rem;
  }

  .creator-progress__step strong {
    font-size: 0.75rem;
    line-height: 1.25;
    overflow-wrap: anywhere;
  }

  .creator-content {
    padding: 0;
  }

  .creator-content__scroll {
    padding: 0.85rem;
  }

  .creator-live-summary {
    min-height: 3.9rem;
    grid-template-columns: minmax(0, 1fr);
    padding: 0.55rem 0.75rem;
  }

  .creator-summary-list {
    display: none;
  }

  .creator-summary-total,
  .creator-summary-placeholder {
    width: 100%;
    min-height: 2.7rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    padding: 0.55rem 0.75rem;
  }

  .creator-summary-total small {
    display: none;
  }

  .creator-summary-total strong {
    font-size: 1.1rem;
  }

  .creator-summary-placeholder {
    margin: 0;
  }

  .creator-stage-kicker {
    margin-bottom: 0.9rem;
  }

  .onboarding-stage__hero {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: flex-start;
    padding: 0.9rem;
  }

  .onboarding-stage__hero > span {
    width: 2.9rem;
    height: 2.9rem;
  }

  .onboarding-stage__hero h2 {
    font-size: 1.3rem;
  }

  .onboarding-promise-row {
    grid-auto-columns: minmax(9.5rem, 68vw);
    grid-auto-flow: column;
    grid-template-columns: none;
    overflow-x: auto;
    padding-bottom: 0.2rem;
    scroll-snap-type: inline proximity;
  }

  .onboarding-promise-row > span {
    scroll-snap-align: start;
  }

  .onboarding-profile-grid,
  .onboarding-plan-grid {
    grid-template-columns: 1fr;
  }

  .onboarding-profile-card,
  .onboarding-goals {
    padding: 0.85rem;
  }

  .onboarding-goal-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .onboarding-goal-grid button {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .onboarding-goal-grid button .v-icon:last-child {
    display: none;
  }

  .onboarding-plan-card {
    min-height: 8.5rem;
  }

  .conversation-title {
    align-items: flex-start;
    margin-bottom: 1rem;
  }

  .conversation-title .v-avatar {
    width: 2.6rem !important;
    min-width: 2.6rem !important;
    height: 2.6rem !important;
  }

  .conversation-title h2 {
    font-size: 1.25rem;
  }

  .term-row,
  .addon-price-row,
  .override-builder {
    grid-template-columns: 1fr;
  }

  .choice-grid,
  .choice-grid--compact,
  .type-choice-grid {
    grid-template-columns: 1fr;
  }

  .type-choice-card {
    min-height: 6.5rem;
  }

  .type-panel-header {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .type-panel-header > .chip-wrap {
    grid-column: 1 / -1;
  }

  .legal-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .legal-profile-card__header,
  .condition-row {
    grid-template-columns: 1fr;
  }

  .catalog-section-tabs button {
    min-height: 2.75rem;
  }

  .choice-card {
    min-height: auto;
  }

  .addon-card__main {
    grid-template-columns: auto 1fr;
  }

  .addon-card__main b {
    grid-column: 2;
  }

  .creator-actions {
    min-height: 4.6rem;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 0.45rem;
    padding: 0.55rem 0.65rem;
  }

  .creator-action-hint {
    display: none;
  }

  .creator-actions__buttons {
    min-width: 0;
    gap: 0.4rem;
  }

  .creator-actions__buttons .v-btn {
    min-width: 0 !important;
    flex: 1 1 auto;
    padding-right: 0.55rem !important;
    padding-left: 0.55rem !important;
    font-size: 0.75rem;
  }

  .creator-action-label--full {
    display: none;
  }

  .creator-action-label--short {
    display: inline;
  }

  .creator-actions__back {
    min-width: 0 !important;
    padding-right: 0.4rem !important;
    padding-left: 0.4rem !important;
  }

  .legal-copy {
    max-height: 14rem;
    padding: 0.9rem;
  }

  .creator-readiness {
    align-items: flex-start;
    padding: 0.75rem;
  }

  .account-gate {
    align-items: stretch;
    flex-direction: column;
  }

  .reset-import-card__actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .reset-import-card__actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }

  .override-table,
  .contracts-table,
  .tac-table {
    overflow-x: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .choice-card,
  .type-choice-card,
  .onboarding-plan-card,
  .onboarding-stage,
  .catalog-section-tabs button {
    animation: none;
    transition: none;
  }

  .type-choice-card:hover,
  .onboarding-plan-card:hover {
    transform: none;
  }
}
</style>
